import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Chart from 'chart.js/auto';
import axios from 'axios';
import TransactionsPieChart from '../TransactionsPieChart';
import ExpensesTimeSeriesChart from '../ExpensesTimeSeriesChart';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { formatCurrency } from '../CoreFunctions';
import { buildCategoryTreeSorted, getCategoryDisplayLabel, AUTOMOTIVE_ALL_VALUES } from '../utils/expenseCategories';
import { exportToExcel } from '../utils/exportUtils';
import AssetExpenseLinkModal from '../components/AssetExpenseLinkModal.jsx';
import IncomeLinkModal from '../components/IncomeLinkModal.jsx';
import DebtDisbursementLinkModal from '../components/DebtDisbursementLinkModal.jsx';
import IncomeSplitModal from '../components/IncomeSplitModal';
import ReimbursementLinkModal from '../components/ReimbursementLinkModal';

import { useReceipts } from '../hooks/useReceipts';
import ReceiptModal from '../components/ReceiptModal';
import { buildAssetLabel, getDomainForCategory } from '../utils/assetLabelHelper';

const ExpensesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState([]);
  const [ccTxns, setCcTxns] = useState([]);
  const [cashTxns, setCashTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Pie vs Table view toggle
  const [chartView, setChartView] = useState('pie');
  // Budget vs Time Series vs Recurring view toggle
  const [pageView, setPageView] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') === 'recurring' ? 'recurring' : 'budget';
  }); // 'budget' | 'timeseries' | 'recurring' | 'search'
  // Recurring view: pre-selected series from URL params (read once, not stored in state)
  // series_id and series_type are available via new URLSearchParams(window.location.search) if needed
  const [categoryChoices, setCategoryChoices] = useState({ items: [], noneItem: { value: 'none', label: 'None' } });
    const [expandedParents, setExpandedParents] = useState({});
  const [openDropdownId, setOpenDropdownId] = useState(null);
  // Debt linking modal state
  const [showDebtModal, setShowDebtModal] = useState(false);
  const [linkingRow, setLinkingRow] = useState(null); // the table row being linked (cash only)
  const [debtList, setDebtList] = useState([]); // non-revolving debts
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [debtCandidates, setDebtCandidates] = useState([]); // candidate amortization rows
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalPhase, setModalPhase] = useState('chooseDebt'); // 'chooseDebt' | 'chooseCandidate'

  // Inline editing state for transaction table (must be declared before any early returns)
  const [editingCell, setEditingCell] = useState(null); // { sourceType, id, field }
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(new Set()); // track rows being deleted

  const [expandedTxns, setExpandedTxns] = useState(new Set());
  const [expandedSearchParents, setExpandedSearchParents] = useState(new Set());
  const [tsExpandedTxns, setTsExpandedTxns] = useState(new Set());

  // Precompute category label map for fast lookup
  const categoryLabelMap = useMemo(() => {
    const map = new Map();
    const items = Array.isArray(categoryChoices.items) ? categoryChoices.items : [];
    for (const p of items) {
      const parentLabel = p.label || String(p.value);
      if (p.value != null) map.set(String(p.value), parentLabel);
      if (Array.isArray(p.children)) {
        for (const ch of p.children) {
          const childLabel = ch.label || String(ch.value);
          if (ch.value != null) map.set(String(ch.value), `${parentLabel} - ${childLabel}`);
        }
      }
    }
    return map;
  }, [categoryChoices]);

  const getCategoryLabel = useCallback((v) => {
    return categoryLabelMap.get(v) || (categoryChoices.noneItem?.label || 'None');
  }, [categoryLabelMap, categoryChoices]);

  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [tsSortField, setTsSortField] = useState('date');
  const [tsSortDirection, setTsSortDirection] = useState('desc');

  // Month selection controls
  const [showMulti, setShowMulti] = useState(false);
  const [allTimeTransactions, setAllTimeTransactions] = useState([]);
  const [loadingAllTime, setLoadingAllTime] = useState(false);

  // Search view state
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchStartDate, setSearchStartDate] = useState('');
  const [searchEndDate, setSearchEndDate] = useState('');
  const [searchMinAmount, setSearchMinAmount] = useState('');
  const [searchMaxAmount, setSearchMaxAmount] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [searchAsset, setSearchAsset] = useState('');
  const [searchExcludeWithSeries, setSearchExcludeWithSeries] = useState(false);

  // Bulk series linking (search view)
  const [selectedSearchRows, setSelectedSearchRows] = useState(new Set());
  const [showBulkSeriesModal, setShowBulkSeriesModal] = useState(false);
  const [bulkSeriesMode, setBulkSeriesMode] = useState('existing');
  const [bulkSeriesOptions, setBulkSeriesOptions] = useState([]);
  const [bulkSeriesLoadingOptions, setBulkSeriesLoadingOptions] = useState(false);
  const [bulkSelectedSeriesId, setBulkSelectedSeriesId] = useState('');
  const [bulkNewName, setBulkNewName] = useState('');
  const [bulkNewType, setBulkNewType] = useState('fixed');
  const [bulkNewFrequency, setBulkNewFrequency] = useState('monthly');
  const [bulkNewVendor, setBulkNewVendor] = useState('');
  const [bulkNewUserDescription, setBulkNewUserDescription] = useState('');
  const [bulkNewIsAssetLinked, setBulkNewIsAssetLinked] = useState(false);
  const [bulkNewAssetId, setBulkNewAssetId] = useState('');
  const [bulkNewCategory, setBulkNewCategory] = useState('');
  const [bulkPhysicalAssets, setBulkPhysicalAssets] = useState([]);
  const [bulkSeriesError, setBulkSeriesError] = useState('');
  const [bulkSeriesSaving, setBulkSeriesSaving] = useState(false);
  // Category conflict resolution
  const [bulkConflictCategories, setBulkConflictCategories] = useState([]);
  const [bulkResolvedCategory, setBulkResolvedCategory] = useState('');
  const [bulkPendingSeriesId, setBulkPendingSeriesId] = useState(null);
  const [bulkSeriesSearch, setBulkSeriesSearch] = useState('');

  // single-month (YYYY-MM)
  const [month, setMonth] = useState(() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${d.getFullYear()}-${mm}`;
  });
  // multi-month range (YYYY-MM)
  const [startMonth, setStartMonth] = useState(() => {
    const d = new Date();
    const mm = '01';
    return `${d.getFullYear()}-${mm}`;
  });
  const [endMonth, setEndMonth] = useState(() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${d.getFullYear()}-${mm}`;
  });

  // Time Series date range (YYYY-MM-DD)
  const [tsSelectedCategory, setTsSelectedCategory] = useState(null);
  const [tsSelectedPeriods, setTsSelectedPeriods] = useState([]);
  const [tsStartDate, setTsStartDate] = useState(() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-01`;
  });
  const [tsEndDate, setTsEndDate] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });

  // Parse YYYY-MM-DD safely as a local date to avoid UTC timezone shifts
  const parseYMDToLocal = useCallback((s) => {
    if (!s) return null;
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [yy, mm, dd] = s.split('-').map(Number);
        return new Date(yy, mm - 1, dd);
      }
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    } catch (e) {
      return null;
    }
  }, []);

  const [tsExcludeDebtPayments, setTsExcludeDebtPayments] = useState(false);

  // Filter historical transactions for the details table based on chart selection
  const tsProcessedRows = useMemo(() => {
    if (pageView !== 'timeseries') return [];

    const parseLocalDate = (s) => {
      if (!s) return null;
      const [yy, mm, dd] = s.split('T')[0].split('-').map(Number);
      return new Date(yy, mm - 1, dd);
    };
    const startD = parseLocalDate(tsStartDate);
    const endD = parseLocalDate(tsEndDate);
    const utcStart = startD ? new Date(Date.UTC(startD.getFullYear(), startD.getMonth(), startD.getDate())) : null;
    const utcEnd = endD ? new Date(Date.UTC(endD.getFullYear(), endD.getMonth(), endD.getDate())) : null;

    const filtered = allTimeTransactions.filter(t => {
      // Avoid double-counting split transactions
      const hasChildren = !!(t.children_count || (t.has_children === true) || (t.children && t.children.length > 0));
      const rawCat = (t.expense_category || 'none').toLowerCase();
      const linkedType = t.revolver_type || t.linked_debt_info?.type || null;
      const isCCPayment = (rawCat === 'payments') && (linkedType === 'credit_card' || (!linkedType && (t.revolver_type || t.revolver_id)));

      // Exclude child transactions from the table — they appear under their parent when expanded
      if (t.parent != null) return false;

      if (hasChildren && isCCPayment) return false;

      // 1. Filter out non-expenses
      const rawCatForMatch = (t.expense_category || 'none'); // Case-sensitive for hierarchy matching
      const excluded = ["income_nonrecurring","income_recurring","income","debt_disbursement","rewards_cashback","tax_refund","transfer","debt_refinancing"];
      if (excluded.includes(rawCat)) return false;
      
      // Special logic for payments: only include if linked to specific debt types (matching Budget view)
      // For HELOC payments specifically, only include the cash account side (not the HELOC revolver transaction itself)
      if (rawCat === 'payments') {
        const linkedType = t.revolver_type || t.linked_debt_info?.type || null;
        // Exclude CC payments (linked or unlinked CC revolvers); include mortgage/auto/HELOC and unlinked payments
        if (linkedType === 'credit_card') return false;
        // Exclude HELOC transactions from the HELOC revolver itself — only the cash account debit counts
        if (linkedType === 'heloc' && (t.revolver_type === 'heloc' || t.revolver_id)) return false;
        // Exclude unlinked payments that come from a CC revolver account
        if (!linkedType && (t.revolver_type || t.revolver_id)) return false;
        // Exclude non-CC debt payments (mortgage, auto_loan, HELOC cash-side) when toggle is on
        if (tsExcludeDebtPayments) return false;
      }
      
      // 1b. Align with Income Statement: Filter by sign to exclude credits/deposits from expenses
      const amt = parseFloat(t.amount || 0);
      const isCash = !t.revolver_type && !t.revolver_id; // Simple check for cash source
      const isCC = !!(t.revolver_type || t.revolver_id);
      
      let isSpend = false;
      if (isCash && amt < 0) isSpend = true;
      if (isCC && amt > 0) isSpend = true;
      // HELOC payments on the HELOC revolver itself are NOT counted — only the cash account debit counts.

      // ALSO allow credits/refunds if they are in an expense category (they reduce overall spend)
      if (!isSpend) {
        if (isCash && amt > 0 && !excluded.includes(rawCat)) {
          isSpend = true;
        }
        if (isCC && amt < 0 && !excluded.includes(rawCat) && rawCat !== 'payments') {
          isSpend = true;
        }
      }

      if (!isSpend) return false;

      if (rawCat === 'transfer' || rawCat === 'rewards_cashback') return false;

      // 2. Filter by Category
      if (tsSelectedCategory && tsSelectedCategory !== 'all' && tsSelectedCategory !== 'none') {
        let matchesCategory = false;
        // Logic to match parent or sub-category
        if (tsSelectedCategory.includes('|')) {
          // It's a sub-category selection (Parent|Child)
          const [p, c] = tsSelectedCategory.split('|');
          if (rawCatForMatch === c) {
            // Check if it really belongs to that parent
            let isCorrectParent = false;
            categoryChoices.items.forEach(parent => {
              if (String(parent.value) === p && parent.children) {
                if (parent.children.some(child => String(child.value) === c)) {
                  isCorrectParent = true;
                }
              }
            });
            if (isCorrectParent) matchesCategory = true;
          }
        } else {
          // It's a parent category selection
          if (rawCatForMatch === tsSelectedCategory) {
            matchesCategory = true;
          } else {
            // Check if rawCatForMatch is a child of the selected parent
            categoryChoices.items.forEach(parent => {
              if (String(parent.value) === tsSelectedCategory && parent.children) {
                if (parent.children.some(child => String(child.value) === rawCatForMatch)) {
                  matchesCategory = true;
                }
              }
            });
          }
        }
        if (!matchesCategory) return false;
      } else if (tsSelectedCategory === 'none') {
        // Handle "Uncategorized" explicitly
        if (rawCatForMatch !== 'none') return false;
      }

      // 3. Filter by Period (if selected)
      const rawDateStr = t.date || t.transaction_date || t.post_date;
      const datePart = rawDateStr ? rawDateStr.split('T')[0] : null;
      if (!datePart) return false;
      
      const date = parseYMDToLocal(datePart);
      if (!date) return false;

      if (tsSelectedPeriods && tsSelectedPeriods.length > 0) {
        // Grouping detection based on period format (use first selected period to detect format)
        const refPeriod = tsSelectedPeriods[0];
        let period;
        if (/^\d{4}-\d{2}-\d{2}$/.test(refPeriod)) { // Daily
          period = datePart;
        } else if (refPeriod.startsWith('Week of')) { // Weekly
          const d = new Date(date);
          const day = d.getDay();
          const diff = d.getDate() - day + (day === 0 ? -6 : 1);
          const monday = new Date(d.setDate(diff));
          period = `Week of ${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
        } else if (/^\d{4}-\d{2}$/.test(refPeriod)) { // Monthly
          period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        } else if (refPeriod.includes(' Q')) { // Quarterly
          period = `${date.getFullYear()} Q${Math.floor(date.getMonth() / 3) + 1}`;
        } else if (/^\d{4}$/.test(refPeriod)) { // Annually
          period = `${date.getFullYear()}`;
        }

        if (!tsSelectedPeriods.includes(period)) return false;
      }

      // 4. Filter by global TS date range using UTC for consistency
      if (utcStart && utcEnd) {
        const tDate = new Date(datePart);
        const utcT = new Date(Date.UTC(tDate.getUTCFullYear(), tDate.getUTCMonth(), tDate.getUTCDate()));
        if (utcT < utcStart || utcT > utcEnd) return false;
      }

      return true;
    });

    // Deduplicate: Plaid sometimes returns two records for the same payment — one with the raw
    // ACH description and one with a cleaned merchant name. Keep the "richer" record:
    // prefer the one with children/splits, then the one with any linked field, then lowest id.
    const hasLinked = t => !!(
      t.linked_debt_id || t.linked_debt_txn_id ||
      t.linked_asset_id || t.linked_asset_expense_id ||
      t.linked_utility_bill_id || t.linked_recurring_series_id ||
      t.linked_income_payment_id || t.linked_income_stream_id ||
      t.linkedDebtId || t.linkedAssetId || t.linkedUtilityBillId || t.linkedRecurringSeriesId
    );
    const hasKidsCheck = t => !!(t.children_count || (t.has_children === true) || (t.children && t.children.length > 0));
    // Build a map: key -> best transaction to keep
    const bestByKey = new Map();
    for (const t of filtered) {
      // Dedup key includes transaction id so only the exact same transaction (same id, same source type)
      // appearing twice is collapsed — two different transactions with same date/amount are kept.
      const acctKey = t.revolver_id || t.source_account_id || t.sourceType || '';
      const key = `${acctKey}|${t.id}|${(t.date || t.transaction_date || '').split('T')[0]}|${parseFloat(t.amount || 0)}`;
      if (!bestByKey.has(key)) {
        bestByKey.set(key, t);
      } else {
        const existing = bestByKey.get(key);
        const tScore = (hasKidsCheck(t) ? 2 : 0) + (hasLinked(t) ? 1 : 0);
        const eScore = (hasKidsCheck(existing) ? 2 : 0) + (hasLinked(existing) ? 1 : 0);
        if (tScore > eScore || (tScore === eScore && (t.id || 0) < (existing.id || 0))) {
          bestByKey.set(key, t);
        }
      }
    }
    const deduped = filtered.filter(t => {
      const acctKey = t.revolver_id || t.source_account_id || t.sourceType || '';
      const key = `${acctKey}|${t.id}|${(t.date || t.transaction_date || '').split('T')[0]}|${parseFloat(t.amount || 0)}`;
      return bestByKey.get(key) === t;
    });

    console.log(`[Expenses TS] Filtered ${filtered.length} rows (${deduped.length} after dedup) from ${allTimeTransactions.length} total. Category: ${tsSelectedCategory}, Periods: ${tsSelectedPeriods.join(',')}`);

    return deduped.map(t => {
      const amt = parseFloat(t.amount || 0);
      const isCash = !t.revolver_type && !t.revolver_id;
      const isCC = !!(t.revolver_type || t.revolver_id);
      let isSpend = false;
      let isRefund = false;
      if (isCash && amt < 0) isSpend = true;
      if (isCC && amt > 0) isSpend = true;
      // HELOC payments on the HELOC revolver itself are NOT counted — only the cash account debit counts.
      if (!isSpend) {
        if (isCash && amt > 0) isRefund = true;
        if (isCC && amt < 0) isRefund = true;
        // HELOC revolver transactions are excluded — only cash account side is counted.
      }

      return {
        ...t,
        amount: isRefund ? -Math.abs(amt) : Math.abs(amt),
        sourceType: t.sourceType || (t.source_account_id ? 'cash' : 'revolver'),
        source: t.source_account_display || (t.lender_name ? (t.account_last4 ? `${t.lender_name} *${t.account_last4}` : t.lender_name) : '') || '',
        source_account_display: t.source_account_display || '',
        source_account_id: t.source_account_id,
        revolver_id: t.revolver_id,
        revolver_type: t.revolver_type,
        category: t.expense_category || 'none',
        // Normalize linked fields to camelCase aliases used by the full-featured table
        linkedDebt: t.linked_debt_info || t.linkedDebt || null,
        linkedDebtId: t.linked_debt_id || t.linkedDebtId || null,
        linkedAssetId: t.linked_asset_id || t.linkedAssetId || null,
        linkedAssetExpenseId: t.linked_asset_expense_id || t.linkedAssetExpenseId || null,
        linkedUtilityBillId: t.linked_utility_bill_id || t.linkedUtilityBillId || null,
        linkedRecurringSeriesId: t.linked_recurring_series_id || t.linkedRecurringSeriesId || null,
        linkedPropertyId: t.parent_property_id || t.linked_property_id || t.linkedPropertyId || null,
      };
    });
  }, [pageView, tsSelectedCategory, tsSelectedPeriods, allTimeTransactions, tsStartDate, tsEndDate, categoryChoices.items, parseYMDToLocal, tsExcludeDebtPayments]);

  // Sorted time series rows
  const tsSortedRows = useMemo(() => {
    if (!tsProcessedRows || tsProcessedRows.length === 0) return tsProcessedRows;
    const dir = tsSortDirection === 'asc' ? 1 : -1;
    return [...tsProcessedRows].sort((a, b) => {
      let av, bv;
      switch (tsSortField) {
        case 'date': av = a.date || a.transaction_date || ''; bv = b.date || b.transaction_date || ''; break;
        case 'description': av = String(a.description || '').toLowerCase(); bv = String(b.description || '').toLowerCase(); break;
        case 'user_description': av = String(a.user_description || '').toLowerCase(); bv = String(b.user_description || '').toLowerCase(); break;
        case 'category': av = String(a.expense_category || a.category || '').toLowerCase(); bv = String(b.expense_category || b.category || '').toLowerCase(); break;
        case 'amount': av = Number(a.amount) || 0; bv = Number(b.amount) || 0; break;
        case 'source': av = String(a.sourceType || '').toLowerCase(); bv = String(b.sourceType || '').toLowerCase(); break;
        default: av = a.date || ''; bv = b.date || ''; break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [tsProcessedRows, tsSortField, tsSortDirection]);

  // Expand sorted rows to include children under expanded parents
  const tsFinalRows = useMemo(() => {
    const out = [];
    for (const r of tsSortedRows) {
      const hasKids = Array.isArray(r.children) && r.children.length > 0;
      const rgi = r._reimbursementGroupInfo;
      const hasReimbKids = !!(rgi && rgi.reimbursements && rgi.reimbursements.length > 0);
      out.push({ ...r, _isChild: false, _hasKids: hasKids || hasReimbKids });
      const key = `${r.sourceType}-${r.id}`;
      if (!tsExpandedTxns.has(key)) continue;
      // Regular split children
      if (hasKids) {
        for (const sc of r.children) {
          let amt = 0;
          try { amt = typeof sc.amount === 'object' && sc.amount.amount != null ? Number(sc.amount.amount) : Number(sc.amount); } catch (e) { amt = 0; }
          out.push({
            ...sc,
            id: sc.id || `synth-${r.id}-${sc.id}`,
            sourceType: r.sourceType,
            date: sc.transaction_date || r.date,
            description: sc.description || r.description,
            user_description: sc.user_description || '',
            amount: Math.abs(amt),
            category: (sc.expense_category || 'none').toLowerCase(),
            expense_category: sc.expense_category || 'none',
            source: r.source,
            parent: r.id,
            parentId: r.id,
            hasChildren: false,
            _isChild: true,
            _hasKids: false,
          });
        }
      }
      // Reimbursement group children
      if (hasReimbKids) {
        // Original transaction (full amount)
        out.push({
          ...r,
          id: `reimb-orig-${r.id}`,
          amount: r._originalAmount !== undefined ? r._originalAmount : r.amount,
          _isChild: true,
          _isReimbursementOriginal: true,
          _hasKids: false,
          _reimbursementGroupInfo: null,
        });
        // Each reimbursement
        for (const reimb of rgi.reimbursements) {
          out.push({
            id: `reimb-child-${reimb.id}`,
            sourceType: r.sourceType,
            date: reimb.transaction_date || r.date,
            description: reimb.description,
            user_description: '',
            amount: Math.abs(Number(reimb.amount) || 0),
            category: 'reimbursement',
            expense_category: 'reimbursement',
            source: r.source,
            parent: r.id,
            parentId: r.id,
            hasChildren: false,
            _isChild: true,
            _isReimbursementChild: true,
            _hasKids: false,
          });
        }
      }
    }
    return out;
  }, [tsSortedRows, tsExpandedTxns]);

  // Reusable fetch for Time Series transactions — called on initial load and after modal saves
  const refreshTsData = useCallback(async () => {
    setLoadingAllTime(true);
    try {
      const [ccRes, cashRes] = await Promise.all([
        axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: tsStartDate, end_date: tsEndDate } }),
        axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: tsStartDate, end_date: tsEndDate } })
      ]);

      const combined = [];

      const processTxns = (txns, sourceType) => {
        txns.forEach(t => {
          // Skip split child transactions
          if (t.parent != null) return;
          // Skip reimbursement children (they appear under their primary's group)
          const rgi = t.reimbursement_group_info;
          if (rgi && t.expense_category === 'reimbursement') return;
          // For reimbursement group primaries, use net_amount
          let txnData = { ...t, sourceType, startDate: tsStartDate, endDate: tsEndDate };
          if (rgi && rgi.primary_id === t.id && rgi.reimbursements && rgi.reimbursements.length > 0) {
            txnData = { ...txnData, amount: rgi.net_amount, _reimbursementGroupInfo: rgi };
          }
          combined.push(txnData);
        });
      };

      if (ccRes.data) {
        const list = Array.isArray(ccRes.data) ? ccRes.data : (ccRes.data.transactions || []);
        processTxns(list, 'revolver');
      }
      if (cashRes.data) {
        const list = Array.isArray(cashRes.data) ? cashRes.data : (cashRes.data.transactions || []);
        processTxns(list, 'cash');
      }

      setAllTimeTransactions(combined);
    } catch (e) {
      console.error('Failed to fetch time series transactions', e);
    } finally {
      setLoadingAllTime(false);
    }
  }, [tsStartDate, tsEndDate]);

  // Fetch all transactions for Time Series view on mount / date range change
  useEffect(() => {
    if (pageView === 'timeseries') {
      refreshTsData();
    }
  }, [pageView, tsStartDate, tsEndDate, refreshTsData]);

  // React to URL param changes (e.g. navigating here from a standalone series link)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get('view');
    if (view === 'recurring') {
      setPageView('recurring');
    }
  }, [location.search]);

  // Compute the selected date range before any hooks that depend on it
  const dateRange = useMemo(() => {
    const buildRangeFromMonth = (ym) => {
      const [y, m] = ym.split('-').map(Number);
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0);
      start.setHours(0,0,0,0);
      end.setHours(23,59,59,999);
      return [start, end];
    };
    if (!showMulti) return buildRangeFromMonth(month);
    // Build from startMonth to endMonth inclusive
    const [ys, ms] = startMonth.split('-').map(Number);
    const [ye, me] = endMonth.split('-').map(Number);
    const start = new Date(ys, ms - 1, 1);
    const end = new Date(ye, me, 0);
    start.setHours(0,0,0,0);
    end.setHours(23,59,59,999);
    return [start, end];
  }, [showMulti, month, startMonth, endMonth]);

  const inRange = useCallback((dateStr) => {
    if (!dateStr) return false;
    const d = parseYMDToLocal(dateStr);
    if (!d) return false;
    return d >= dateRange[0] && d <= dateRange[1];
  }, [dateRange, parseYMDToLocal]);

  // Helper to decide if a transaction falls in the selected range using transaction_date
  const txnInSelectedRange = useCallback((t) => {
    const ds = t?.transaction_date || null;
    return inRange(ds);
  }, [inRange]);

  const tableRows = useMemo(() => {
    const rows = [];

    const pushRow = (t, sourceType, amtSignNormalized) => {
      // For Budget view, we want the parent transactions in tableRows.
      // We will handle grouping/expansion in processedRows.
      // So only skip if it's explicitly a child transaction from the API.
      if (t.parent != null) return;

      const hasChildren = !!(t.children_count || (t.has_children === true) || (t.children && t.children.length > 0));

      let amt = 0; try { amt = typeof t.amount === 'object' && t.amount.amount != null ? Number(t.amount.amount) : Number(t.amount); } catch(e) { amt = 0; }
      const normalized = amtSignNormalized ? -Math.abs(amt) : Math.abs(amt);
      rows.push({
        id: t.id,
        sourceType,
        date: t.transaction_date,
        description: t.description,
        user_description: t.user_description || '',
        amount: normalized,
        category: (t.expense_category || 'none').toLowerCase(),
        source: sourceType === 'cash'
          ? (t.source_account_display || `${t.lender_name || 'Cash'}${t.account_last4 ? ' *'+t.account_last4 : ''}`)
          : `${t.lender_name || 'Card'}${t.account_last4 ? ' *'+t.account_last4 : ''}`,
        source_account_id: t.source_account_id,
        revolver_id: t.revolver_id,
        revolver_type: t.revolver_type,
        asset_expense_splits: t.asset_expense_splits || [],
        linkedDebt: t.linked_debt_info || t.linkedDebtInfo || null,
        linkedDebtId: t.linked_debt_id || t.linkedDebtId || null,
        linkedAssetId: t.linked_asset_id || t.linkedAssetId || null,
        linkedAssetExpenseId: t.linked_asset_expense_id || t.linkedAssetExpenseId || null,
        linkedUtilityBillId: t.linked_utility_bill_id || t.linkedUtilityBillId || null,
        linkedRecurringSeriesId: t.linked_recurring_series_id || t.linkedRecurringSeriesId || null,
        linkedPropertyId: t.parent_property_id || t.linked_property_id || t.parentPropertyId || t.linkedPropertyId || null,
        parentId: t.parent || null,
        hasChildren: hasChildren,
        reimbursement_group_info: t.reimbursement_group_info || null,
        expense_category: (t.expense_category || 'none').toLowerCase(),
        standalone_recurring_series_id: t.standalone_recurring_series_id || null,
        standalone_recurring_series_name: t.standalone_recurring_series_name || null,
        linked_expense_info: t.linked_expense_info || null,
        linked_recurring_series_id: t.linked_recurring_series_id || null,
        linked_income_info: t.linked_income_info || null,
        linked_income_stream_id: t.linked_income_stream_id || null,
      });
    };

    // Cash: include negatives as expenses; include payments only if linked to mortgage/auto loans
    (cashTxns || []).forEach(t => {
      if (!txnInSelectedRange(t)) return;
      let amt = 0; try { amt = typeof t.amount === 'object' && t.amount.amount != null ? Number(t.amount.amount) : Number(t.amount); } catch(e) { amt = 0; }
      const cat = (t.expense_category || 'none').toLowerCase();
      const linkedType = t.linked_debt_info?.type || null;
      // Exclude CC-linked payments only; include mortgage/auto/HELOC and unlinked cash payments
      if (cat === 'payments' && linkedType === 'credit_card') return;

      const excluded = ["income_nonrecurring","income_recurring","income","debt_disbursement","rewards_cashback","tax_refund","transfer","debt_refinancing"];
      if (excluded.includes(cat)) return;

      let isSpend = false;
      let isRefund = false;
      if (amt < 0) isSpend = true;
      if (amt > 0) { isSpend = true; isRefund = true; }

      if (isSpend) pushRow(t, 'cash', isRefund);
    });

    // CC: include positive charges; skip payments and rewards
    (ccTxns || []).forEach(t => {
      if (!txnInSelectedRange(t)) return;
      const cat = (t.expense_category || 'none').toLowerCase();
      let amt = 0; try { amt = typeof t.amount === 'object' && t.amount.amount != null ? Number(t.amount.amount) : Number(t.amount); } catch(e) { amt = 0; }

      let isSpend = false;
      let isRefund = false;

      // HELOC payments on the HELOC revolver itself are NOT counted — only the cash account debit counts.
      if (['payments','rewards_cashback'].includes(cat)) {
          return;
      } else {
          if (amt > 0) isSpend = true;
          if (amt < 0) { isSpend = true; isRefund = true; }
      }

      if (isSpend) pushRow(t, 'revolver', isRefund);
    });

    return rows;
  }, [cashTxns, ccTxns, txnInSelectedRange]);

  // Build hierarchy for cash transactions (parents and real cash children)
  const hierarchy = useMemo(() => {
    const all = [
      ...((cashTxns || []).map(t => ({ ...t, _source: 'cash' }))),
      ...((ccTxns || []).map(t => ({ ...t, _source: 'revolver' }))),
    ];
    const withAmt = all.map(t => {
      let amt = 0;
      try { amt = typeof t.amount === 'object' && t.amount.amount != null ? Number(t.amount.amount) : Number(t.amount); } catch (e) { amt = 0; }
      return { ...t, _amt: amt };
    });
    const byId = new Map();
    const childrenByParent = new Map();
    const childIds = new Set();
    for (const t of withAmt) {
      byId.set(`${t._source}-${t.id}`, t);
    }
    for (const t of withAmt) {
      const p = t.parent;
      if (p) {
        const key = `${t._source}-${p}`;
        if (!childrenByParent.has(key)) childrenByParent.set(key, []);
        childrenByParent.get(key).push(t);
        childIds.add(`${t._source}-${t.id}`);
      }
    }
    for (const [, arr] of childrenByParent.entries()) {
      arr.sort((a, b) => {
        const ad = a.post_date || a.transaction_date; const bd = b.post_date || b.transaction_date;
        if (ad === bd) return a.id - b.id;
        return (ad < bd) ? -1 : 1;
      });
    }
    return { byId, childrenByParent, childIds };
  }, [cashTxns, ccTxns]);

  // Determine parents-only set and sort by selected sortField
  const parentsOnlySorted = useMemo(() => {
    const childrenSet = hierarchy.childIds;
    // For Budget view, we only want top-level parents (those NOT in childrenSet)
    // Also exclude reimbursement children (they appear under their primary's group)
    const parents = (tableRows || []).filter(r => {
        const key = `${r.sourceType === 'revolver' ? 'revolver' : 'cash'}-${r.id}`;
        if (childrenSet.has(key)) return false;
        const rgi = r.reimbursement_group_info;
        if (rgi && r.expense_category === 'reimbursement') return false;
        return true;
    });
    return [...parents].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'amount') {
        aVal = Number(aVal);
        bVal = Number(bVal);
      } else if (sortField === 'date') {
        aVal = aVal ? parseYMDToLocal(aVal) : null;
        bVal = bVal ? parseYMDToLocal(bVal) : null;
      } else if (sortField === 'category') {
        aVal = getCategoryLabel(aVal || 'none').toLowerCase();
        bVal = getCategoryLabel(bVal || 'none').toLowerCase();
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tableRows, hierarchy, sortField, sortDirection, getCategoryLabel, parseYMDToLocal]);

  // Parent keys helper and global toggle for transactions
  const parentRowKeysWithChildren = useMemo(() => {
    // Build from parentsOnlySorted and real hierarchy map to ensure reliability across sources
    const keys = [];
    for (const r of parentsOnlySorted) {
      const key = `${r.sourceType}-${r.id}`;
      const kids = hierarchy.childrenByParent.get(key) || [];
      if (kids.length > 0) keys.push(key);
    }
    return keys;
  }, [parentsOnlySorted, hierarchy]);

  const toggleAllTxnParents = () => {
    const allOpen = parentRowKeysWithChildren.length > 0 && parentRowKeysWithChildren.every(k => expandedTxns.has(k));
    if (allOpen) {
      setExpandedTxns(new Set());
    } else {
      setExpandedTxns(new Set(parentRowKeysWithChildren));
    }
  };

  // Compose final rows: parent then its children if expanded
  const processedRows = useMemo(() => {
    const out = [];
    for (const p of parentsOnlySorted) {
      const parentKey = `${p.sourceType}-${p.id}`;
      const rgi = p.reimbursement_group_info;
      const isPrimaryOfGroup = rgi && rgi.primary_id === p.id;
      const hasReimbKids = isPrimaryOfGroup && rgi.reimbursements && rgi.reimbursements.length > 0;
      // For reimbursement group primaries, show net amount
      const displayAmount = hasReimbKids ? rgi.net_amount : p.amount;
      out.push({
        ...p,
        _isChild: false,
        amount: displayAmount,
        _originalAmount: p.amount,
        _reimbursementGroupInfo: hasReimbKids ? rgi : null,
        hasChildren: !!(p.hasChildren || p.children_count > 0 || hasReimbKids),
      });
      const isOpen = expandedTxns.has(parentKey);
      if (!isOpen) continue;
      const lookupKey = `${p.sourceType}-${p.id}`;
      const kidsRaw = (p.children && p.children.length > 0) ? p.children : (hierarchy.childrenByParent.get(lookupKey) || []);
      
      // If we still have no children but hasChildren is true, check if we need to synthesize them from asset_expense_splits
      if (kidsRaw.length === 0 && p.hasChildren && p.asset_expense_splits && p.asset_expense_splits.length > 0) {
          // Determine sign to align with parent transaction sign (expenses in Budget view are typically absolute or signed)
          // Based on line 336, normalized = amtSignNormalized ? -Math.abs(amt) : amt
          const sign = p.amount < 0 ? -1.0 : 1.0;
          for (const s of p.asset_expense_splits) {
              kidsRaw.push({
                  ...s,
                  transaction_date: s.payment_date,
                  expense_category: s.expense_category,
                  description: s.expense_desc || s.description,
                  amount: sign * Math.abs(s.amount)
              });
          }
      }

      for (const kt of kidsRaw) {
        let amt = 0; try { amt = typeof kt.amount === 'object' && kt.amount.amount != null ? Number(kt.amount.amount) : Number(kt.amount); } catch (e) { amt = 0; }
        
        const isCash = p.sourceType === 'cash';
        const isCC = p.sourceType === 'revolver';

        let isSpend = false;
        let isRefund = false;
        if (isCash && amt < 0) isSpend = true;
        if (isCC && amt > 0) isSpend = true;

        if (!isSpend) {
            if (isCash && amt > 0) isRefund = true;
            if (isCC && amt < 0) isRefund = true;
        }
        
        const amount = isRefund ? -Math.abs(amt) : Math.abs(amt);
        const ktId = kt.id || `${p.id}-child-${kt.description}-${amt}`;
        out.push({
          id: ktId,
          realId: kt.id,
          sourceType: p.sourceType,
          date: kt.transaction_date || p.date,
          description: kt.description || p.description,
          amount,
          category: (kt.expense_category || 'none').toLowerCase(),
          source: p.source,
          user_description: kt.user_description || '',
          linkedDebt: kt.linked_debt_info || kt.linkedDebtInfo || null,
          linkedDebtId: kt.linked_debt_id || kt.linkedDebtId || null,
          linkedAssetId: kt.linked_asset_id || kt.asset_id || kt.asset || null,
          linkedAssetExpenseId: kt.linked_asset_expense_id || kt.id || null,
          linkedUtilityBillId: kt.linked_utility_bill_id || kt.utility_bill_id || kt.utility_bill || null,
          linkedRecurringSeriesId: kt.linked_recurring_series_id || kt.recurring_series_id || kt.recurring_series || null,
          parentId: p.id,
          hasChildren: false,
          _isChild: true,
        });
      }
      // Reimbursement group children
      if (hasReimbKids) {
        out.push({
          ...p,
          id: `reimb-orig-${p.id}`,
          amount: p._originalAmount !== undefined ? p._originalAmount : p.amount,
          _isChild: true,
          _isReimbursementOriginal: true,
          hasChildren: false,
          _reimbursementGroupInfo: null,
        });
        for (const reimb of rgi.reimbursements) {
          out.push({
            id: `reimb-child-${reimb.id}`,
            realId: reimb.id,
            sourceType: p.sourceType,
            date: reimb.transaction_date || p.date,
            description: reimb.description,
            amount: Math.abs(Number(reimb.amount) || 0),
            category: 'reimbursement',
            expense_category: 'reimbursement',
            source: p.source,
            user_description: '',
            parentId: p.id,
            hasChildren: false,
            _isChild: true,
            _isReimbursementChild: true,
          });
        }
      }
    }
    return out;
  }, [parentsOnlySorted, expandedTxns, hierarchy]);

  const [allAssets, setAllAssets] = useState([]);
  useEffect(() => {
    const loadAssets = async () => {
      try {
        const phys = await axios.get('/api/balance_sheet/assets/physical_assets/minimal/');
        setAllAssets(Array.isArray(phys.data) ? phys.data : []);
      } catch (e) {
        setAllAssets([]);
      }
    };
    loadAssets();
  }, []);

  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitRow, setSplitRow] = useState(null); // { id, sourceType, amount, description, date }
  const [splitItems, setSplitItems] = useState([]); // [{ key, amount, description }]
  const [splitError, setSplitError] = useState('');
  const [splitSubmitting, setSplitSubmitting] = useState(false);

  // Income link modal state
  const [showIncomeLinkModal, setShowIncomeLinkModal] = useState(false);
  const [showReimbursementModal, setShowReimbursementModal] = useState(false);
  const [reimbursementRow, setReimbursementRow] = useState(null);
  const [showDebtDisbursementModal, setShowDebtDisbursementModal] = useState(false);
  const [debtDisbursementRow, setDebtDisbursementRow] = useState(null);
  const [incomeLinkRow, setIncomeLinkRow] = useState(null);

  // Income split modal state
  const [showIncomeSplitModal, setShowIncomeSplitModal] = useState(false);
  const [incomeSplitRow, setIncomeSplitRow] = useState(null); // { id, amount, date, description, sourceType }
  const [incomeSplitDefaultKind] = useState('non_recurring');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Build YYYY-MM-DD strings from dateRange without UTC conversion to avoid off-by-one issues
      const startDate = dateRange[0];
      const endDate = dateRange[1];
      const start = `${startDate.getFullYear()}-${String(startDate.getMonth()+1).padStart(2,'0')}-01`;
      const end = `${endDate.getFullYear()}-${String(endDate.getMonth()+1).padStart(2,'0')}-${String(endDate.getDate()).padStart(2,'0')}`;
      const results = await Promise.allSettled([
        axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: start, end_date: end }  }),
        axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: start, end_date: end }  }),
        axios.get('/api/balance_sheet/financing/revolvers/expense-category-choices/'),
        axios.get('/api/income_statement/budgets/'),
      ]);

      const [rvRes, caRes, catRes, budgetsRes] = results;

      // Revolver transactions (credit cards only)
      if (rvRes.status === 'fulfilled') {
        const data = rvRes.value?.data;
        const list = Array.isArray(data) ? data : (data?.transactions || []);
        const rv = list.filter(t => {
          const type = (t.revolver_type || '').toLowerCase();
          return type === 'credit_card' || type === 'heloc';
        });
        setCcTxns(rv);
      } else {
        console.warn('Failed to load revolver transactions:', rvRes.reason);
        setCcTxns([]);
      }

      // Cash transactions
      if (caRes.status === 'fulfilled') {
        const data = caRes.value?.data;
        const list = Array.isArray(data) ? data : (data?.transactions || []);
        setCashTxns(list);
      } else {
        console.warn('Failed to load cash transactions:', caRes.reason);
        setCashTxns([]);
      }

      // Categories
      if (catRes.status === 'fulfilled') {
        const catRaw = Array.isArray(catRes.value?.data) ? catRes.value.data : [];
        const tree = buildCategoryTreeSorted(catRaw);
        setCategoryChoices(tree);
      } else {
        console.warn('Failed to load category choices:', catRes.reason);
        setCategoryChoices({ items: [], noneItem: { value: 'none', label: 'None' } });
      }

      // Budgets
      if (budgetsRes.status === 'fulfilled') {
        setBudgets(Array.isArray(budgetsRes.value?.data) ? budgetsRes.value.data : []);
      } else {
        console.warn('Failed to load budgets:', budgetsRes.reason);
        setBudgets([]);
      }

      // Only set a blocking error if both transaction sources failed
      if (rvRes.status === 'rejected' && caRes.status === 'rejected') {
        setError('Failed to load transactions.');
      }
    } catch (e) {
      console.error('Fetch expenses error (unexpected):', e);
      setError('Failed to load transactions.');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSearch = useCallback(async () => {
    setSearchLoading(true);
    try {
      const params = {
        q: searchQuery,
        start_date: searchStartDate,
        end_date: searchEndDate,
        min_amount: searchMinAmount,
        max_amount: searchMaxAmount,
        category: searchCategory,
        asset_id: searchAsset,
        exclude_with_series: searchExcludeWithSeries ? 'true' : undefined,
      };
      const res = await axios.get('/api/balance_sheet/transactions/search/', { params  });
      
      const processed = (res.data || []).map(t => {
        const amt = parseFloat(t.amount || 0);
        const isCash = t.transaction_type === 'cash';
        const isCC = t.transaction_type === 'revolver';

        let isSpend = false;
        let isRefund = false;
        if (isCash && amt < 0) isSpend = true;
        if (isCC && amt > 0) isSpend = true;
        // HELOC payments on the HELOC revolver itself are NOT counted — only the cash account debit counts.

        if (!isSpend) {
            if (isCash && amt > 0) isRefund = true;
            if (isCC && amt < 0) isRefund = true;
            // HELOC revolver transactions are excluded — only cash account side is counted.
        }

        return {
          ...t,
          sourceType: t.transaction_type, // 'revolver' or 'cash' from backend
          source: t.source_account_display || (t.lender_name ? `${t.lender_name}${t.account_last4 ? ' *'+t.account_last4 : ''}` : '') || '',
          category: t.expense_category || 'none',
          amount: isRefund ? -Math.abs(amt) : Math.abs(amt),
          linkedAssetId: t.linked_asset_id,
          linkedDebtId: t.linked_debt_id || (t.linked_debt_info ? t.linked_debt_info.id : null)
        };
      });
      
      setSearchResults(processed);
    } catch (e) {
      console.error('Search failed', e);
      alert('Search failed');
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery, searchStartDate, searchEndDate, searchMinAmount, searchMaxAmount, searchCategory, searchAsset, searchExcludeWithSeries]);

  // Build flat display list for search results: parents + expanded children
  const searchDisplayRows = useMemo(() => {
    const rows = [];
    for (const r of searchResults) {
      const hasKids = Array.isArray(r.children) && r.children.length > 0;
      rows.push({ ...r, _hasSearchChildren: hasKids });
      const key = String(r.sourceType) + "-" + String(r.id);
      if (hasKids && expandedSearchParents.has(key)) {
        for (const ch of r.children) {
          const chAmt = parseFloat(ch.amount || 0);
          rows.push({ ...ch, _isSearchChild: true, _searchParentId: r.id, sourceType: r.sourceType, transaction_type: r.transaction_type, source: r.source, amount: Math.abs(chAmt), category: ch.expense_category || "none", linkedAssetId: ch.linked_asset_id });
        }
      }
    }
    return rows;
  }, [searchResults, expandedSearchParents]);


  const {
    showReceiptModal: showCcReceiptModal,
    receiptPreviewUrl: ccReceiptPreviewUrl,
    receiptModalTitle: ccReceiptModalTitle,
    openReceipt: openCcReceipt,
    closeReceipt: closeCcReceipt,
    uploadReceipt: uploadCcReceipt,
    deleteReceipt: deleteCcReceipt,
  } = useReceipts('/api/balance_sheet/financing/revolvers/transactions', fetchAll);

  const {
    showReceiptModal: showCashReceiptModal,
    receiptPreviewUrl: cashReceiptPreviewUrl,
    receiptModalTitle: cashReceiptModalTitle,
    openReceipt: openCashReceipt,
    closeReceipt: closeCashReceipt,
    uploadReceipt: uploadCashReceipt,
    deleteReceipt: deleteCashReceipt,
  } = useReceipts('/api/balance_sheet/assets/cash-accounts/transactions', fetchAll);

  // Normalize for chart: cash expenses are negative -> make positive; 
  // credit card charges are positive; exclude payments & rewards for cc in the chart component itself.
  const chartTransactions = useMemo(() => {
    // Build sets of parent IDs that have children so we can exclude the parent from totals/graphs
    const cashParentIds = new Set();
    const ccParentIds = new Set();
    for (const t of (cashTxns || [])) {
      if (t && t.parent != null) cashParentIds.add(t.parent);
    }
    for (const t of (ccTxns || [])) {
      if (t && t.parent != null) ccParentIds.add(t.parent);
    }

    // Prepare cash: map negative expenses to positive amounts; positive refunds to negative amounts
    const cashItems = (cashTxns || [])
      .filter(t => txnInSelectedRange(t))
      .filter(t => !cashParentIds.has(t.id)) // exclude parents that have children; keep children and standalone rows
      .filter(t => {
        // Exclude reimbursement children — they appear under their primary's group
        const cat = (t.expense_category || 'none').toLowerCase();
        if (cat === 'reimbursement' && t.reimbursement_group_info) return false;
        return true;
      })
      .map(t => {
        let amt = 0;
        try { amt = typeof t.amount === 'object' && t.amount.amount != null ? Number(t.amount.amount) : Number(t.amount); } catch(e) { amt = 0; }
        const cat = (t.expense_category || 'none').toLowerCase();
        const linkedType = t.linked_debt_info?.type || null;
        // Exclude CC-linked payments only; include mortgage/auto/HELOC and unlinked cash payments
        if (cat === 'payments' && linkedType === 'credit_card') return null;
        
        const excluded = ["income_nonrecurring","income_recurring","income","debt_disbursement","rewards_cashback","tax_refund","transfer","debt_refinancing"];
        if (excluded.includes(cat)) return null;

        // Use net amount if this is the primary of a reimbursement group
        const rgi = t.reimbursement_group_info;
        const isPrimary = rgi && rgi.primary_id === t.id;
        if (isPrimary) amt = rgi.net_amount;

        // For expenses: amount is negative. Flip sign for charting so expenses contribute positive values
        // For refunds: amount is positive. Flip sign to negative so they reduce total.
        const normalizedAmount = Math.abs(amt) * (amt < 0 ? 1 : -1); 
        return {
          ...t,
          amount: normalizedAmount,
          _source: 'cash',
        };
      })
      .filter(t => t && t.amount !== 0);

    // Prepare credit cards: include charges (positive) and refunds (negative); 
    // payments and rewards are excluded except for HELOC payments.
    const ccItems = (ccTxns || [])
      .filter(t => txnInSelectedRange(t))
      .filter(t => !ccParentIds.has(t.id)) // exclude parents that have children
      .filter(t => {
        // Exclude reimbursement children — they appear under their primary's group
        const cat = (t.expense_category || 'none').toLowerCase();
        if (cat === 'reimbursement' && t.reimbursement_group_info) return false;
        return true;
      })
      .map(t => {
        let amt = 0;
        try { amt = typeof t.amount === 'object' && t.amount.amount != null ? Number(t.amount.amount) : Number(t.amount); } catch(e) { amt = 0; }
        const cat = (t.expense_category || 'none').toLowerCase();
        
        let normalizedAmount = 0;
        // Exclude payments and rewards for CC always; HELOC payments on the revolver are excluded (only cash account debit counts)
        if (['payments','rewards_cashback'].includes(cat)) {
            return null;
        } else {
            // Use net amount if this is the primary of a reimbursement group
            const rgi = t.reimbursement_group_info;
            const isPrimary = rgi && rgi.primary_id === t.id;
            if (isPrimary) amt = rgi.net_amount;
            // Charges are positive, refunds are negative
            normalizedAmount = Math.abs(amt) * (amt > 0 ? 1 : -1);
        }

        return {
          ...t,
          amount: normalizedAmount,
          _source: 'credit_card',
        };
      })
      .filter(t => t && t.amount !== 0);

    return [...cashItems, ...ccItems];
  }, [cashTxns, ccTxns, txnInSelectedRange]);

  const openDebtModalForRow = async (row) => {
    setModalError('');
    setModalLoading(true);
    setShowDebtModal(true);
    setLinkingRow(row);
    setSelectedDebt(null);
    setDebtCandidates([]);
    setModalPhase('chooseDebt');
    try {
      const [debtRes, revolverRes] = await Promise.allSettled([
        axios.get('/api/balance_sheet/financing/'),
        axios.get('/api/balance_sheet/financing/revolvers/'),
      ]);
      const allDebts = debtRes.status === 'fulfilled' && Array.isArray(debtRes.value.data) ? debtRes.value.data : [];
      const installment = allDebts.filter(d => d.debt_type === 'auto_loan' || d.debt_type === 'mortgage');
      let revolvers = [];
      if (revolverRes.status === 'fulfilled') {
        const rdata = revolverRes.value.data;
        const rlist = Array.isArray(rdata) ? rdata : (rdata?.results || []);
        revolvers = rlist
          .filter(r => r.revolver_type === 'credit_card' || r.revolver_type === 'heloc')
          .map(r => ({
            id: r.id,
            debt_type: r.revolver_type,
            lender_name: r.lender?.name || r.lender_name || 'Unknown',
            account_number: r.account_number || '',
            asset_info: { description: r.account_number ? `*${String(r.account_number).slice(-4)}` : '' },
          }));
      }
      setDebtList([...installment, ...revolvers]);
    } catch (e) {
      console.error('Failed to load debts', e);
      setModalError('Failed to load debts.');
    } finally {
      setModalLoading(false);
    }
  };

  const loadDebtCandidates = async (debt) => {
    if (!linkingRow || !debt) return;
    setModalError('');
    setModalLoading(true);
    try {
      const url = `/api/balance_sheet/assets/cash_accounts/transactions/${linkingRow.id}/candidate-debt-payments/`;
      const params = { debt_type: debt.debt_type, debt_id: debt.id };
      const res = await axios.get(url, { params  });
      const candidates = res.data && Array.isArray(res.data.candidates) ? res.data.candidates : [];
      setDebtCandidates(candidates);
      setModalPhase('chooseCandidate');
    } catch (e) {
      console.error('Failed to load candidate payments', e);
      setModalError('Failed to load candidate payments.');
    } finally {
      setModalLoading(false);
    }
  };

  const confirmLinkToCandidate = async (candidate) => {
    if (!linkingRow || !selectedDebt || !candidate) return;
    setModalError('');
    setModalLoading(true);
    try {
      const url = `/api/balance_sheet/assets/cash_accounts/transactions/${linkingRow.id}/link-debt-payment/`;
      await axios.post(url,  {
        debt_type: selectedDebt.debt_type,
        debt_id: selectedDebt.id,
        candidate_id: candidate.id,
      });
      // Refresh cash transactions for current period to pick up linked_debt_info
      const caRes = await axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: dateRange[0].toISOString().slice(0,10), end_date: dateRange[1].toISOString().slice(0,10) }  });
      const list = Array.isArray(caRes.data) ? caRes.data : (caRes.data.transactions || []);
      setCashTxns(list);
      if (pageView === 'timeseries') { refreshTsData(); }
      if (pageView === 'search') handleSearch();
      setShowDebtModal(false);
      setLinkingRow(null);
    } catch (e) {
      console.error('Failed to link payment', e);
      setModalError('Failed to link payment.');
    } finally {
      setModalLoading(false);
    }
  };

  // Memoized helper maps for fast asset lookups and labels
  const assetMaps = useMemo(() => {
    try {
      const list = Array.isArray(allAssets) ? allAssets : [];
      const byId = new Map();
      const resiAddressById = new Map(); // property id -> street address
      const structIdsByProperty = new Map(); // property id -> sorted structure ids
      for (const a of list) {
        const idStr = String(a.id);
        byId.set(idStr, a);
        const type2 = (a.asset_type_2 || a.type_2 || a.type2 || '').toLowerCase();
        if (type2 === 'resi_property') {
          const addr = a.address && a.address.street_address ? a.address.street_address : '';
          resiAddressById.set(idStr, addr);
        } else if (type2 === 'structure') {
          const rp = a.residential_property || a.residentialProperty || a.parent_property || a.parent || null;
          const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
          if (rpId != null) {
            const key = String(rpId);
            if (!structIdsByProperty.has(key)) structIdsByProperty.set(key, []);
            structIdsByProperty.get(key).push(a.id);
          }
        }
      }
      // Sort structure ids for ordinal positions
      const structOrdinalById = new Map();
      for (const ids of structIdsByProperty.values()) {
        ids.sort((a,b) => a - b);
        ids.forEach((sid, idx) => {
          structOrdinalById.set(String(sid), idx + 1);
        });
      }
      return { byId, resiAddressById, structOrdinalById };
    } catch (e) {
      return { byId: new Map(), resiAddressById: new Map(), structOrdinalById: new Map() };
    }
  }, [allAssets]);

  const buildAssetUrl = (asset) => {
    if (!asset) return null;
    const id = asset.id;
    const type2 = asset.asset_type_2 || asset.type_2 || asset.type2;
    const t = (type2 || '').toLowerCase();
    if (t === 'vehicle') return `/balance_sheet/assets/physical_assets/vehicles/${id}`;
    if (t === 'resi_property') return `/balance_sheet/assets/physical_assets/resi_properties/${id}`;
    if (t === 'structure' || t === 'land') {
      // Resolve parent property id from various serializer shapes quickly
      const rp = asset.residential_property || asset.residentialProperty || asset.parent_property || asset.parent;
      const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
      if (rpId) return `/balance_sheet/assets/physical_assets/resi_properties/${rpId}`;
    }
    return null;
  };

  // Auto-link modal state
  const [showAutoModal, setShowAutoModal] = useState(false);
  const [autoRow, setAutoRow] = useState(null); // { id, sourceType, description, amount, date }
  const [vehicles] = useState([]);
  const [assetExpenseChoices] = useState([]);
  const [autoForm, setAutoForm] = useState({
    vehicleId: '',
    assetExpenseCategory: 'maintenance',
    isRecurring: false,
    scheduleMode: 'existing', // 'existing' | 'new'
    scheduleId: '',
    newSchedule: { description: '', frequency: 'monthly', expected_date: '', expected_amount: '' },
    loading: false,
    error: ''
  });
  const [schedules, setSchedules] = useState([]);

  // Landscaping modal state
  const [showLandModal, setShowLandModal] = useState(false);
  const [showUtilityModal, setShowUtilityModal] = useState(false);
  const [utilityRow, setUtilityRow] = useState(null);
  const [landRow, setLandRow] = useState(null); // { id, sourceType }
  const resiProperties = useMemo(() => {
    return allAssets.filter(a => (a.asset_type_2 || a.type_2 || a.type2 || '').toLowerCase() === 'resi_property');
  }, [allAssets]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [landChild, setLandChild] = useState(null); // {id,label}
  const [landLoading, setLandLoading] = useState(false);
  const [landError, setLandError] = useState('');
  const [landIsRecurring, setLandIsRecurring] = useState(false);
  const [landFrequency, setLandFrequency] = useState('monthly');
  const [landSeriesOptions, setLandSeriesOptions] = useState([]);
  const [landRecurringName, setLandRecurringName] = useState('');
  const [landRecurringType] = useState('variable');
  const [landRecurringChoice, setLandRecurringChoice] = useState(''); // existing id or '__new__'
  const [landRecurringLoading, setLandRecurringLoading] = useState(false);
  // Add Land form state
  const [showAddLand, setShowAddLand] = useState(false);
  const [landFormLoading, setLandFormLoading] = useState(false);
  const [landFormError, setLandFormError] = useState('');
  const [landForm, setLandForm] = useState({
    legal_description: '',
    parcel_number: '',
    lot_number: '',
    plat_number: '',
    tax_district: '',
    area_in_acres: '',
    latitude: '',
    longitude: '',
    survey_description: '',
    financing_allocation: ''
  });

  const onChangePropertyForLand = async (pid) => {
    setSelectedPropertyId(pid);
    setLandChild(null);
    if (!pid) return;
    setLandLoading(true);
    setLandError('');
    try {
      const opts = await axios.get(`/api/balance_sheet/assets/physical_assets/${pid}/linkage-options/`);
      const root = opts?.data || {};
      const children = (root && Array.isArray(root.children)) ? root.children : [];
      const propertyLabel = root.label || (root.address ? root.address : `Property ${pid}`);
      
      let land = children.find(ch => (String(ch.type || '').toLowerCase()) === 'land');
      // Fallback: if not present in linkage-options, load property details to derive land
      if (!land) {
        try {
          const propRes = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${pid}/`);
          const pdata = propRes.data || {};
          const fallbackPropertyLabel = pdata.address?.street_address || pdata.label || `Property ${pid}`;
          if (pdata && pdata.land && pdata.land.id) {
            land = { 
              id: pdata.land.id, 
              label: `${fallbackPropertyLabel} - Land`
            };
          }
        } catch (e) {
          // ignore fallback error; we'll handle no-land case below
        }
      }
      if (land) {
        setLandChild({ 
          id: land.id, 
          label: land.label?.includes(' - ') ? land.label : `${propertyLabel} - ${land.label || 'Land'}` 
        });
        // Load recurring series for Land
        try {
          setLandRecurringLoading(true);
          const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${land.id}/maintenance-items/`);
          const items = Array.isArray(res.data) ? res.data.map(m => ({ id: m.id, name: m.description || `Series ${m.id}`, frequency: m.recurring_frequency })) : [];
          setLandSeriesOptions(items);
        } catch (e) { setLandSeriesOptions([]); }
        finally { setLandRecurringLoading(false); }
      } else {
        setLandChild(null);
        setLandSeriesOptions([]);
      }
    } catch (e) {
      setLandError('Failed to load property details.');
      setLandChild(null);
    } finally {
      setLandLoading(false);
    }
  };

  const submitAddLand = async () => {
    if (!selectedPropertyId) { setLandFormError('Please select a property first.'); return; }
    setLandFormError('');
    setLandFormLoading(true);
    try {
      const payload = {
        asset_type_2: 'land',
        residential_property: Number(selectedPropertyId),
        legal_description: landForm.legal_description,
        parcel_number: landForm.parcel_number,
        lot_number: landForm.lot_number ? Number(landForm.lot_number) : null,
        plat_number: landForm.plat_number ? Number(landForm.plat_number) : null,
        tax_district: landForm.tax_district,
        area_in_acres: landForm.area_in_acres ? Number(landForm.area_in_acres) : null,
        latitude: landForm.latitude || null,
        longitude: landForm.longitude || null,
        survey_description: landForm.survey_description || '',
        financing_allocation: landForm.financing_allocation ? Number(landForm.financing_allocation) : null
      };
      const res = await axios.post('/api/balance_sheet/assets/physical_assets/resi_properties/land/create-land-asset/', payload);
      const created = res.data;
      const label = created && created.area_in_acres ? `Land — ${created.area_in_acres} acres` : 'Land';
      setLandChild({ id: created.id, label });
      setShowAddLand(false);
    } catch (e) {
      setLandFormError('Failed to create Land. Please check inputs.');
    } finally {
      setLandFormLoading(false);
    }
  };

  const submitLandLink = async () => {
    if (!landRow) return;
    if (!selectedPropertyId) { setLandError('Please select a property.'); return; }
    if (!landChild || !landChild.id) { setLandError('Selected property has no Land configured.'); return; }
    setLandLoading(true);
    setLandError('');
    try {
      const url = landRow.sourceType === 'revolver'
        ? `/api/balance_sheet/financing/revolvers/transactions/${landRow.id}/link-asset-expense/`
        : `/api/balance_sheet/assets/cash_accounts/transactions/${landRow.id}/link-asset-expense/`;
      let scheduleId = '';
      if (landIsRecurring) {
        if (landRecurringChoice === '__new__') {
          const txAmt = (()=>{ try { return Math.abs(Number(landRow.amount)); } catch(e) { return 0; } })();
          const txDate = (landRow.transaction_date || landRow.post_date || new Date().toISOString().slice(0,10));
          const payload = {
            asset: Number(landChild.id),
            description: (landRecurringName && landRecurringName.trim().length ? landRecurringName : (landRow.description || 'Recurring landscaping expense')),
            recurring: true,
            recurring_frequency: landFrequency,
            expected_maintenance_date: txDate,
            expected_expense_amount: Number(txAmt) || 0
          };
          const cr = await axios.post('/api/balance_sheet/assets/physical_assets/add-maintenance-item/', payload);
          scheduleId = cr?.data?.id;
        } else if (landRecurringChoice) {
          scheduleId = landRecurringChoice;
        }
      }
      await axios.post(url,  {
        asset_id: Number(landChild.id),
        expense_category: 'landscaping',
        description: landRow.description,
        schedule_id: scheduleId || undefined,
        recurring: landIsRecurring,
        recurring_frequency: landIsRecurring ? landFrequency : null,
        recurring_name: (landRecurringChoice === '__new__') ? (landRecurringName || undefined) : undefined,
        recurring_type: landIsRecurring ? (landRecurringType || null) : null,
      });
      setShowLandModal(false);
      setLandRow(null);
      // Refresh month data for Expenses page
      // Build YYYY-MM-DD strings without UTC conversion to avoid off-by-one issues
      const [yy, mm] = month.split('-').map(Number);
      const endDay = new Date(yy, mm, 0).getDate();
      const start = `${yy}-${String(mm).padStart(2,'0')}-01`;
      const end = `${yy}-${String(mm).padStart(2,'0')}-${String(endDay).padStart(2,'0')}`;
      const [rvRes, caRes] = await Promise.allSettled([
        axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: start, end_date: end }  }),
        axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: start, end_date: end }  }),
      ]);
      if (rvRes.status === 'fulfilled') {
        const data = rvRes.value?.data;
        const list = Array.isArray(data) ? data : (data?.transactions || []);
        const rv = list.filter(t => {
          const type = (t.revolver_type || '').toLowerCase();
          return type === 'credit_card' || type === 'heloc';
        });
        setCcTxns(rv);
      }
      if (caRes.status === 'fulfilled') {
        const data = caRes.value?.data;
        const list = Array.isArray(data) ? data : (data?.transactions || []);
        setCashTxns(list);
      }
    } catch (e) {
      setLandError('Failed to link expense.');
    } finally {
      setLandLoading(false);
    }
  };

  // Home (Property/Structure) modal state
  const [showHomeModal, setShowHomeModal] = useState(false);
  const [homeRow, setHomeRow] = useState(null);
  const [homeUiCategory] = useState('');
  const [homeLoading, setHomeLoading] = useState(false);
  const [homeError, setHomeError] = useState('');
  const [homeSelectedPropertyId, setHomeSelectedPropertyId] = useState('');
  const [homeAssetOptions, setHomeAssetOptions] = useState([]);
  const [homeSelectedAssetId, setHomeSelectedAssetId] = useState('');
  const [homeIsRecurring, setHomeIsRecurring] = useState(false);
  const [homeFrequency, setHomeFrequency] = useState('monthly');
  const [homeSeriesOptions, setHomeSeriesOptions] = useState([]);
  const [homeRecurringMode, setHomeRecurringMode] = useState('existing'); // 'existing' | 'new'
  const [homeRecurringName, setHomeRecurringName] = useState('');
  const [homeRecurringType] = useState('variable');
  const [homeRecurringChoice, setHomeRecurringChoice] = useState(''); // existing id or '__new__'
  const [homeRecurringLoading, setHomeRecurringLoading] = useState(false);
  const HOME_UI_TO_ASSET = {
    home_repair: 'repair',
    home_maintenance: 'maintenance',
    home_enhancement: 'enhancement',
    home_decor: 'home_decor',
    home_furniture: 'home_furniture',
    home_artwork: 'home_artwork',
    home_pest_control: 'pest_control',
    home_inspection: 'home_inspection',
    home_tax_payment: 'taxes',
    home_insurance_payment: 'insurance',
    home_other: 'home_other',
  };
  const onChangePropertyForHome = async (pid) => {
    setHomeSelectedPropertyId(pid);
    setHomeAssetOptions([]);
    setHomeSelectedAssetId('');
    setHomeRecurringChoice('');
    setHomeSeriesOptions([]);
    if (!pid) return;
    setHomeLoading(true);
    setHomeError('');
    try {
      // Use enriched linkage-options to include property itself, structures, and land
      const resp = await axios.get(`/api/balance_sheet/assets/physical_assets/${pid}/linkage-options/`);
      const root = resp?.data || {};
      const children = Array.isArray(root.children) ? root.children : [];
      const options = [];
      const propertyLabel = root.label || (root.address ? root.address : `Residential Property - ${pid}`);
      
      // Add entire property option
      if (root.id) {
        options.push({ id: root.id, label: propertyLabel });
      }
      // Add all children (structures and land)
      for (const ch of children) {
        if (!ch || !ch.id) continue;
        const type2 = (ch.type || '').toLowerCase();
        let suffix = '';
        if (type2 === 'land') {
          suffix = ' - Land';
        } else {
          const rawType = ch.label || ch.type || 'Structure';
          suffix = ` - ${rawType}`;
        }
        options.push({ id: ch.id, label: `${propertyLabel}${suffix}` });
      }

      setHomeAssetOptions(options);

      if (!options.length) {
        setHomeError('No linkable assets found for the selected property. Please add a Structure or Land first.');
      }
    } catch (e) {
      setHomeError('Failed to load property details.');
      setHomeAssetOptions([]);
    } finally {
      setHomeLoading(false);
    }
  };

  // Load recurring series for selected Home property (canonical series, include children)
  useEffect(() => {
    const run = async () => {
      try {
        if (!homeSelectedPropertyId) { setHomeSeriesOptions([]); return; }
        setHomeRecurringLoading(true);
        const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${homeSelectedPropertyId}/recurring-series/`, {
          params: { include_children: 1, include_inactive: 1 }
         });
        const items = Array.isArray(res.data) ? res.data.map(s => ({ id: String(s.id), name: s.name })) : [];
        setHomeSeriesOptions(items);
      } catch (e) { setHomeSeriesOptions([]); }
      finally { setHomeRecurringLoading(false); }
    };
    run();
  }, [homeSelectedPropertyId]);

  const submitHomeLink = async () => {
    if (!homeRow || !homeSelectedAssetId || !homeUiCategory) {
      setHomeError('Please select a property and asset.');
      return;
    }
    setHomeLoading(true);
    setHomeError('');
    try {
      const url = homeRow.sourceType === 'revolver'
        ? `/api/balance_sheet/financing/revolvers/transactions/${homeRow.id}/link-asset-expense/`
        : `/api/balance_sheet/assets/cash_accounts/transactions/${homeRow.id}/link-asset-expense/`;
      const assetCat = HOME_UI_TO_ASSET[homeUiCategory] || 'maintenance';
      // Resolve recurring series id: either existing choice or create new via manage endpoint
      let recurringSeriesId = null;
      if (homeIsRecurring) {
        if (homeRecurringMode === 'existing') {
          if (homeRecurringChoice) {
            recurringSeriesId = Number(homeRecurringChoice);
          }
        } else if (homeRecurringMode === 'new') {
          const name = (homeRecurringName || homeRow.description || 'Recurring home expense');
          const body = { action: 'create', name, recurring_type: (homeRecurringType || 'variable'), recurring_frequency: homeFrequency, include_children: 1 };
          const cr = await axios.post(`/api/balance_sheet/assets/physical_assets/${homeSelectedPropertyId}/recurring-series/manage/`, body);
          if (cr?.data?.id != null) recurringSeriesId = Number(cr.data.id);
        }
      }
      await axios.post(url, {
        asset_id: Number(homeSelectedAssetId),
        expense_category: assetCat,
        description: homeRow.description,
        recurring: homeIsRecurring,
        // legacy fields only when creating a new series
        recurring_frequency: homeIsRecurring && homeRecurringMode === 'new' ? homeFrequency : null,
        recurring_name: homeIsRecurring && homeRecurringMode === 'new' ? (homeRecurringName || undefined) : undefined,
        recurring_type: homeIsRecurring && homeRecurringMode === 'new' ? (homeRecurringType || 'variable') : null,
        recurring_series_id: recurringSeriesId != null ? recurringSeriesId : undefined,
       });
      setShowHomeModal(false);
      setHomeRow(null);
      // refresh month data
      const [yy, mm] = month.split('-').map(Number);
      const endDay = new Date(yy, mm, 0).getDate();
      const start = `${yy}-${String(mm).padStart(2,'0')}-01`;
      const end = `${yy}-${String(mm).padStart(2,'0')}-${String(endDay).padStart(2,'0')}`;
      const [rvRes, caRes] = await Promise.allSettled([
        axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: start, end_date: end }  }),
        axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: start, end_date: end }  }),
      ]);
      if (rvRes.status === 'fulfilled') {
        const data = rvRes.value?.data;
        const list = Array.isArray(data) ? data : (data?.transactions || []);
        const rv = list.filter(t => {
          const type = (t.revolver_type || '').toLowerCase();
          return type === 'credit_card' || type === 'heloc';
        });
        setCcTxns(rv);
      }
      if (caRes.status === 'fulfilled') {
        const data = caRes.value?.data;
        const list = Array.isArray(data) ? data : (data?.transactions || []);
        setCashTxns(list);
      }
    } catch (e) {
      setHomeError('Failed to link expense.');
    } finally {
      setHomeLoading(false);
    }
  };

  // Waste Management (Property/Structure) modal state
  const [showWasteModal, setShowWasteModal] = useState(false);
  const [wasteRow, setWasteRow] = useState(null);
  const [wasteLoading, setWasteLoading] = useState(false);
  const [wasteError, setWasteError] = useState('');
  const [wasteSelectedPropertyId, setWasteSelectedPropertyId] = useState('');
  const [wasteAssetOptions, setWasteAssetOptions] = useState([]);
  const [wasteSelectedAssetId, setWasteSelectedAssetId] = useState('');
  const [wasteIsRecurring, setWasteIsRecurring] = useState(false);
  const [wasteFrequency, setWasteFrequency] = useState('monthly');
  const [wasteSeriesOptions, setWasteSeriesOptions] = useState([]); // [{name,type,last_frequency}]
  const [wasteRecurringMode, setWasteRecurringMode] = useState('existing'); // 'existing' | 'new'
  const [wasteExistingSeriesName, setWasteExistingSeriesName] = useState('');
  const [wasteRecurringName, setWasteRecurringName] = useState('');
  const [wasteRecurringType, setWasteRecurringType] = useState('variable');

  const onChangePropertyForWaste = async (pid) => {
    setWasteSelectedPropertyId(pid);
    setWasteAssetOptions([]);
    setWasteSelectedAssetId('');
    setWasteSeriesOptions([]);
    setWasteExistingSeriesName('');
    setWasteRecurringName('');
    setWasteRecurringMode('existing');
    if (!pid) return;
    setWasteLoading(true);
    setWasteError('');
    try {
      // Prefer linkage-options for labeled children, exclude land
      const opts = await axios.get(`/api/balance_sheet/assets/physical_assets/${pid}/linkage-options/`);
      const root = opts?.data || {};
      const children = (root && Array.isArray(root.children)) ? root.children : [];
      const propertyLabel = root.label || (root.address ? root.address : `Property ${pid}`);
      
      let structs = children.filter(ch => (String(ch.type || '').toLowerCase()) !== 'land');
      let options = structs.map(ch => ({ 
        id: ch.id, 
        label: `${propertyLabel} - ${ch.label || 'Structure'}`
      }));
      // Fallback via property details if needed
      if (!options.length) {
        try {
          const propRes = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${pid}/`);
          const pdata = propRes.data || {};
          const fallbackPropertyLabel = pdata.address?.street_address || pdata.label || `Property ${pid}`;
          const structsArr = Array.isArray(pdata.structures) ? pdata.structures : [];
          options = structsArr.map((s, idx) => ({ 
            id: s.id, 
            label: `${fallbackPropertyLabel} - Structure ${idx + 1}` 
          }));
        } catch (e) { /* ignore */ }
      }
      setWasteAssetOptions(options);
      if (!options.length) {
        setWasteError('No structures found for the selected property. Please add a structure first.');
      }
      // Reset series list until a structure is picked
      setWasteSeriesOptions([]);
      setWasteExistingSeriesName('');
      setWasteRecurringName('');
    } catch (e) {
      setWasteError('Failed to load property details.');
      setWasteAssetOptions([]);
    } finally {
      setWasteLoading(false);
    }
  };

  // Load recurring series when a property is chosen for Waste (canonical series, include children)
  useEffect(() => {
    const run = async () => {
      try {
        if (!wasteSelectedPropertyId) { setWasteSeriesOptions([]); setWasteExistingSeriesName(''); return; }
        const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${wasteSelectedPropertyId}/recurring-series/`, {
          params: { include_children: 1, include_inactive: 1 }
         });
        const items = Array.isArray(res.data) ? res.data.map(s => ({ id: String(s.id), name: s.name, type: s.type })) : [];
        setWasteSeriesOptions(items);
      } catch (e) { setWasteSeriesOptions([]); }
    };
    run();
  }, [wasteSelectedPropertyId]);

  const submitWasteLink = async () => {
    if (!wasteRow) return;
    if (!wasteSelectedPropertyId) { setWasteError('Please select a property.'); return; }
    if (!wasteSelectedAssetId) { setWasteError('Please select a structure.'); return; }
    setWasteLoading(true);
    setWasteError('');
    try {
      // Validate recurring selection
      if (wasteIsRecurring) {
        if (wasteRecurringMode === 'existing') {
          if (!wasteExistingSeriesName) {
            setWasteLoading(false);
            setWasteError('Please select an existing recurring series.');
            return;
          }
        } else {
          const name = (wasteRecurringName || '').trim();
          if (!name) {
            setWasteLoading(false);
            setWasteError('Please enter a name for the new recurring series.');
            return;
          }
          const dup = (wasteSeriesOptions || []).some(s => String(s.name).toLowerCase() === name.toLowerCase());
          if (dup) {
            setWasteLoading(false);
            setWasteError('A recurring series with this name already exists for this structure. Please choose a different name or select Existing.');
            return;
          }
        }
      }

      const url = wasteRow.sourceType === 'revolver'
        ? `/api/balance_sheet/financing/revolvers/transactions/${wasteRow.id}/link-asset-expense/`
        : `/api/balance_sheet/assets/cash_accounts/transactions/${wasteRow.id}/link-asset-expense/`;
      // Resolve recurringSeriesId
      let recurringSeriesId = null;
      if (wasteIsRecurring) {
        if (wasteRecurringMode === 'existing') {
          recurringSeriesId = Number(wasteExistingSeriesName);
        } else {
          const body = { action: 'create', name: (wasteRecurringName || wasteRow.description || 'Recurring expense'), recurring_type: (wasteRecurringType || 'variable'), recurring_frequency: wasteFrequency, include_children: 1 };
          const cr = await axios.post(`/api/balance_sheet/assets/physical_assets/${wasteSelectedPropertyId}/recurring-series/manage/`, body);
          if (cr?.data?.id != null) recurringSeriesId = Number(cr.data.id);
        }
      }

      await axios.post(url, {
        asset_id: Number(wasteSelectedAssetId),
        expense_category: 'maintenance',
        description: wasteRow.description,
        recurring: wasteIsRecurring,
        recurring_frequency: wasteIsRecurring && wasteRecurringMode === 'new' ? wasteFrequency : null,
        recurring_name: wasteIsRecurring && wasteRecurringMode === 'new' ? (wasteRecurringName || undefined) : undefined,
        recurring_type: wasteIsRecurring && wasteRecurringMode === 'new' ? (wasteRecurringType || 'variable') : null,
        recurring_series_id: recurringSeriesId != null ? recurringSeriesId : undefined,
       });
      setShowWasteModal(false);
      setWasteRow(null);
      // Refresh month data for Expenses page
      const [yy, mm] = month.split('-').map(Number);
      const endDay = new Date(yy, mm, 0).getDate();
      const start = `${yy}-${String(mm).padStart(2,'0')}-01`;
      const end = `${yy}-${String(mm).padStart(2,'0')}-${String(endDay).padStart(2,'0')}`;
      const [rvRes, caRes] = await Promise.allSettled([
        axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: start, end_date: end }  }),
        axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: start, end_date: end }  }),
      ]);
      if (rvRes.status === 'fulfilled') {
        const data = rvRes.value?.data;
        const list = Array.isArray(data) ? data : (data?.transactions || []);
        const rv = list.filter(t => {
          const type = (t.revolver_type || '').toLowerCase();
          return type === 'credit_card' || type === 'heloc';
        });
        setCcTxns(rv);
      }
      if (caRes.status === 'fulfilled') {
        const data = caRes.value?.data;
        const list = Array.isArray(data) ? data : (data?.transactions || []);
        setCashTxns(list);
      }
    } catch (e) {
      setWasteError('Failed to link expense.');
    } finally {
      setWasteLoading(false);
    }
  };


  const onChangeVehicle = async (vid) => {
    setAutoForm(prev => ({ ...prev, vehicleId: vid, scheduleId: '', loading: true, error: '' }));
    setSchedules([]);
    if (!vid) { setAutoForm(prev => ({ ...prev, loading: false })); return; }
    try {
      const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${vid}/maintenance-items/`);
      setSchedules(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.warn('Failed to load schedules for vehicle', vid, e);
      setSchedules([]);
    } finally {
      setAutoForm(prev => ({ ...prev, loading: false }));
    }
  };

  const submitAutoLink = async () => {
    if (!autoRow) return;
    const { vehicleId, assetExpenseCategory, isRecurring, scheduleMode, scheduleId, newSchedule } = autoForm;
    if (!vehicleId) {
      setAutoForm(prev => ({ ...prev, error: 'Please select a vehicle.' }));
      return;
    }
    setAutoForm(prev => ({ ...prev, loading: true, error: '' }));
    let schedId = '';
    try {
      // Create schedule if needed
      if (isRecurring && scheduleMode === 'new') {
        const payload = {
          asset: Number(vehicleId),
          description: newSchedule.description || autoRow.description || 'Recurring vehicle expense',
          recurring: true,
          recurring_frequency: newSchedule.frequency,
          expected_maintenance_date: newSchedule.expected_date,
          expected_expense_amount: Number(newSchedule.expected_amount) || Math.abs(Number(autoRow.amount || 0)) || 0,
        };
        const cr = await axios.post('/api/balance_sheet/assets/physical_assets/add-maintenance-item/', payload);
        schedId = cr?.data?.id;
      } else if (isRecurring && scheduleMode === 'existing') {
        schedId = scheduleId || '';
      }

      // Link transaction to asset expense
      const body = {
        asset_id: Number(vehicleId),
        expense_category: assetExpenseCategory || 'maintenance',
        schedule_id: schedId || undefined,
        description: autoRow.description,
      };
      const isRv = autoRow.sourceType === 'revolver';
      const url = isRv
        ? `/api/balance_sheet/financing/revolvers/transactions/${autoRow.id}/link-asset-expense/`
        : `/api/balance_sheet/assets/cash_accounts/transactions/${autoRow.id}/link-asset-expense/`;
      await axios.post(url, body);

      // Optimistically update local transaction to reflect linkage so the Links column renders immediately
      if (isRv) {
        setCcTxns(prev => prev.map(t => (t.id === autoRow.id ? { ...t, linked_asset_id: Number(vehicleId) } : t)));
      } else {
        setCashTxns(prev => prev.map(t => (t.id === autoRow.id ? { ...t, linked_asset_id: Number(vehicleId) } : t)));
      }

      // Close modal
      setShowAutoModal(false);
      setAutoRow(null);
    } catch (e) {
      console.error('Failed to link vehicle expense', e);
      setAutoForm(prev => ({ ...prev, error: 'Failed to link vehicle expense.' }));
    } finally {
      setAutoForm(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCategoryChange = async (row, newCat) => {
    try {
      const updatedRow = { ...row, category: newCat, expense_category: newCat };

      // --- Recurring series check: must happen BEFORE patching the transaction ---
      // If this transaction belongs to a standalone recurring series, warn the user
      // that all transactions in the series will be updated, and let them cancel.
      const standaloneSeriesId = row.standalone_recurring_series_id;
      if (standaloneSeriesId) {
        try {
          const seriesRes = await axios.get(`/api/balance_sheet/assets/standalone-recurring-series/${standaloneSeriesId}/`);
          const seriesCat = seriesRes.data?.expense_category || '';
          const seriesName = seriesRes.data?.display_name || `Series #${standaloneSeriesId}`;
          const txnCount = seriesRes.data?.transaction_count ?? '?';
          const catLabel = (cat) => {
            try {
              const items = categoryChoices?.items || [];
              for (const it of items) {
                if (it.value === cat) return it.label;
                for (const ch of (it.children || [])) {
                  if (ch.value === cat) return ch.label;
                }
              }
            } catch (e) {}
            return cat;
          };
          if (seriesCat && seriesCat !== newCat) {
            const confirmed = window.confirm(
              `This transaction belongs to recurring series "${seriesName}".\n\n` +
              `The series has ${txnCount} transaction(s) currently categorized as "${catLabel(seriesCat)}".\n\n` +
              `Changing to "${catLabel(newCat)}" will update ALL ${txnCount} transaction(s) in this series.\n\n` +
              `Click OK to update all, or Cancel to abort this category change.`
            );
            if (!confirmed) return; // abort — do not change anything
          }
        } catch (seriesErr) {
          console.error('Failed to check recurring series category', seriesErr);
        }
      }
      // --- End recurring series check ---

      if (row.sourceType === 'revolver') {
        await axios.patch(`/api/balance_sheet/financing/revolvers/transactions/${row.id}/`,  { expense_category: newCat });
        setCcTxns(prev => prev.map(t => (t.id === row.id ? { ...t, expense_category: newCat } : t)));
        setSearchResults(prev => prev.map(t => (t.id === row.id && t.sourceType === 'revolver' ? { ...t, expense_category: newCat, category: newCat } : t)));
        setAllTimeTransactions(prev => prev.map(t => (t.id === row.id && t.sourceType === 'revolver' ? { ...t, expense_category: newCat, category: newCat } : t)));
      } else if (row.sourceType === 'cash') {
        await axios.patch(`/api/balance_sheet/assets/cash_accounts/transactions/${row.id}/`,  { expense_category: newCat });
        setCashTxns(prev => prev.map(t => (t.id === row.id ? { ...t, expense_category: newCat } : t)));
        setSearchResults(prev => prev.map(t => (t.id === row.id && t.sourceType === 'cash' ? { ...t, expense_category: newCat, category: newCat } : t)));
        setAllTimeTransactions(prev => prev.map(t => (t.id === row.id && t.sourceType === 'cash' ? { ...t, expense_category: newCat, category: newCat } : t)));
        // If user assigned Payments on Expenses page, prompt to link to a non-revolving debt (auto/mortgage)
        if (newCat === 'payments' && !row.linkedDebtId) {
          openDebtModalForRow(updatedRow);
        }
      }

      // If transaction is in a standalone recurring series and user confirmed, update the series too
      // (PATCH to series auto-propagates category to all linked transactions via backend)
      if (standaloneSeriesId) {
        try {
          const seriesRes2 = await axios.get(`/api/balance_sheet/assets/standalone-recurring-series/${standaloneSeriesId}/`);
          const seriesCat2 = seriesRes2.data?.expense_category || '';
          if (seriesCat2 !== newCat) {
            await axios.patch(`/api/balance_sheet/assets/standalone-recurring-series/${standaloneSeriesId}/`, { expense_category: newCat });
            // Refresh local transaction lists to reflect bulk update
            const updateTxn = t => t.standalone_recurring_series_id === standaloneSeriesId
              ? { ...t, expense_category: newCat, category: newCat } : t;
            setSearchResults(prev => prev.map(updateTxn));
            setAllTimeTransactions(prev => prev.map(updateTxn));
            setCashTxns(prev => prev.map(updateTxn));
            setCcTxns(prev => prev.map(updateTxn));
          }
        } catch (seriesErr2) {
          console.error('Failed to update recurring series category', seriesErr2);
        }
      }
      // If Income categories selected, open the income link modal
      if (newCat === 'income' || newCat === 'income_nonrecurring' || newCat === 'income_recurring') {
        try {
          setIncomeLinkRow({ id: row.id, description: row.description, amount: row.amount, date: row.date || row.transaction_date });
          setShowIncomeLinkModal(true);
        } catch (e) { /* ignore */ }
      }
      // If any Automotive category -> open vehicle linking modal
      if (getDomainForCategory(newCat) === 'vehicle') {
        openUtilitiesModal(updatedRow);
      }
      // If any Waste Management category -> open waste/property linking modal
      if (getDomainForCategory(newCat) === 'waste_management') {
        openUtilitiesModal(updatedRow);
      }
      // If Landscaping -> open property/land linking modal
      if (newCat === 'landscaping') {
        openUtilitiesModal(updatedRow);
      }
      // If any Utilities child -> open utility bill linking modal
      const isUtilitiesChild = (slug) => {
        try {
          const tree = categoryChoices || { items: [] };
          for (const it of (tree.items || [])) {
            if (String(it.value) === 'utilities' && Array.isArray(it.children)) {
              return it.children.some(ch => String(ch.value) === String(slug));
            }
          }
        } catch (e) {}
        // Fallback to known set if tree not available
        const KNOWN = new Set(['water','gas','electricity','electric_gas','utilities__other']);
        return KNOWN.has(String(slug));
      };
      if (isUtilitiesChild(newCat)) {
        openUtilitiesModal(updatedRow);
      }
      // If Debt Disbursement selected, open debt disbursement modal
      if (newCat === 'debt_disbursement') {
        setDebtDisbursementRow(row);
        setShowDebtDisbursementModal(true);
      }
      // If Reimbursement selected, open the reimbursement link modal
      if (newCat === 'reimbursement') {
        setReimbursementRow({ ...row, type: row.sourceType });
        setShowReimbursementModal(true);
      }
      // If Taxes -> open linkage modal
      if (newCat === 'taxes' || newCat === 'home_tax_payment') {
        openUtilitiesModal(updatedRow);
      }
      // If any Waste Management child -> open waste linking modal (property + structure)
      const isWasteChild = (slug) => {
        try {
          const tree = categoryChoices || { items: [] };
          for (const it of (tree.items || [])) {
            if (String(it.value) === 'waste_management' && Array.isArray(it.children)) {
              return it.children.some(ch => String(ch.value) === String(slug));
            }
          }
        } catch (e) {}
        const KNOWN = new Set(['sanitation','waste_management__recycling','waste_management__yard_waste','waste_management__other']);
        return KNOWN.has(String(slug));
      };
      if (isWasteChild(newCat)) {
        openUtilitiesModal(updatedRow);
      }
      // If Home child selected, open property/structure modal
      if ([
        'home_repair','home_maintenance','home_enhancement','home_decor','home_furniture','home_artwork','home_pest_control',
        'home_inspection','home_insurance_payment', 'home_plants', 'home_tv', 'home_internet', 'home_apps', 'home_other'
      ].includes(newCat)) {
        openUtilitiesModal(updatedRow);
      }
    } catch (e) {
      console.error('Failed to update category', e);
      alert('Failed to update category.');
    }
  };

  // Styled category badge dropdown helpers
  const getCategoryStyle = (category) => {
    const styles = {
      'food_dining': { backgroundColor: '#FFF3E0', color: '#E65100', borderColor: '#E65100' },
      'gas_fuel': { backgroundColor: '#F3E5F5', color: '#7B1FA2', borderColor: '#7B1FA2' },
      'groceries': { backgroundColor: '#E8F5E8', color: '#2E7D32', borderColor: '#2E7D32' },
      'internet': { backgroundColor: '#E3F2FD', color: '#1565C0', borderColor: '#1565C0' },
      'apps': { backgroundColor: '#E8EAF6', color: '#5E35B1', borderColor: '#5E35B1' },
      'books': { backgroundColor: '#FEF3C7', color: '#78350F', borderColor: '#FBBF24' },
      'landscaping': { backgroundColor: '#E0F2E1', color: '#388E3C', borderColor: '#388E3C' },
      'shopping': { backgroundColor: '#FFE0E6', color: '#C2185B', borderColor: '#C2185B' },
      'entertainment': { backgroundColor: '#E1F5FE', color: '#0277BD', borderColor: '#0277BD' },
      'travel': { backgroundColor: '#FFF8E1', color: '#F57C00', borderColor: '#F57C00' },
      'automotive': { backgroundColor: '#E0F2F1', color: '#00695C', borderColor: '#00695C' },
      'healthcare': { backgroundColor: '#FFEBEE', color: '#C62828', borderColor: '#C62828' },
      'utilities': { backgroundColor: '#FFF3E0', color: '#EF6C00', borderColor: '#EF6C00' },
      'subscription': { backgroundColor: '#F3E5F5', color: '#8E24AA', borderColor: '#8E24AA' },
      'insurance': { backgroundColor: '#E8EAF6', color: '#3F51B5', borderColor: '#3F51B5' },
      'taxes': { backgroundColor: '#FFEBEE', color: '#B71C1C', borderColor: '#B71C1C' },
      'kids__other': { backgroundColor: '#E8F5E8', color: '#43A047', borderColor: '#43A047' },
      'education': { backgroundColor: '#E0F7FA', color: '#0097A7', borderColor: '#0097A7' },
      'business': { backgroundColor: '#ECEFF1', color: '#455A64', borderColor: '#455A64' },
      'personal_care': { backgroundColor: '#FCE4EC', color: '#AD1457', borderColor: '#AD1457' },
      'dental_products': { backgroundColor: '#FCE4EC', color: '#AD1457', borderColor: '#AD1457' },
      'workout_gym_products': { backgroundColor: '#F7FEE7', color: '#4D7C0F', borderColor: '#4D7C0F' },
      'home_improvement': { backgroundColor: '#F1F8E9', color: '#558B2F', borderColor: '#558B2F' },
      'gifts_charity': { backgroundColor: '#FFF9C4', color: '#9E9D24', borderColor: '#9E9D24' },
      'fees_charges': { backgroundColor: '#FFEBEE', color: '#D32F2F', borderColor: '#D32F2F' },
      'transfer': { backgroundColor: '#E3F2FD', color: '#1976D2', borderColor: '#1976D2' },
      'payments': { backgroundColor: '#E8F5E8', color: '#2E7D32', borderColor: '#2E7D32' },
      'rewards_cashback': { backgroundColor: '#FFF8E1', color: '#F57C00', borderColor: '#F57C00' },
      'other': { backgroundColor: '#F5F5F5', color: '#616161', borderColor: '#616161' },
      'none': { backgroundColor: '#FFFFFF', color: '#000000', borderColor: '#000000' },
    };
    return styles[category] || styles['none'];
  };

  const toggleDropdown = (e, rowId) => {
    e.stopPropagation();
    setOpenDropdownId(prev => prev === rowId ? null : rowId);
  };

  // (removed duplicate showUtilityModal / utilityRow declarations — originals are defined above near other modal state)



  // Open the shared asset expense modal for a given transaction row
  const openUtilitiesModal = (row) => {
    try {
      const r2 = {
        id: row.id,
        sourceType: row.sourceType,
        description: row.description,
        amount: row.amount,
        transaction_date: row.transaction_date || row.date || '',
        post_date: row.post_date || '',
        date: row.transaction_date || row.post_date || row.date || '',
        linked_asset_id: row.linked_asset_id || row.linkedAssetId || '',
        linked_asset_expense_id: row.linked_asset_expense_id || row.linkedAssetExpenseId || '',
        linked_utility_bill_id: row.linked_utility_bill_id || row.linkedUtilityBillId || '',
        linked_recurring_series_id: row.linked_recurring_series_id || row.linkedRecurringSeriesId || '',
        linked_property_id: row.linked_property_id || row.linkedPropertyId || '',
        expense_category: row.category || row.expense_category || '',
      };
      setUtilityRow(r2);
      setShowUtilityModal(true);
    } catch (e) {
      // fallback: minimal
      setUtilityRow({ id: row.id, sourceType: row.sourceType, description: row.description, amount: row.amount, expense_category: row.category || row.expense_category || '' });
      setShowUtilityModal(true);
    }
  };

  // Open bulk series modal — load all series options
  const openBulkSeriesModal = () => {
    setBulkSeriesMode('existing');
    setBulkSelectedSeriesId('');
    setBulkNewName('');
    setBulkNewType('fixed');
    setBulkNewFrequency('monthly');
    setBulkNewVendor('');
    setBulkNewUserDescription('');
    setBulkNewIsAssetLinked(false);
    setBulkNewAssetId('');
    setBulkNewCategory('');
    // Load physical assets for asset-linked series creation
    axios.get('/api/balance_sheet/assets/physical_assets/minimal/')
      .then(res => setBulkPhysicalAssets(Array.isArray(res.data) ? res.data : []))
      .catch(() => setBulkPhysicalAssets([]));
    setBulkSeriesError('');
    setBulkConflictCategories([]);
    setBulkResolvedCategory('');
    setBulkPendingSeriesId(null);
    setBulkSeriesSearch('');
    setBulkSeriesLoadingOptions(true);
    axios.get('/api/balance_sheet/assets/all-recurring-series/', { params: { include_inactive: 1 } })
      .then(res => setBulkSeriesOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setBulkSeriesOptions([]))
      .finally(() => setBulkSeriesLoadingOptions(false));
    setShowBulkSeriesModal(true);
  };

  // Execute the actual bulk-link calls (shared by initial save and conflict resolution)
  const executeBulkLink = async (seriesKey, resolvedCategory) => {
    // seriesKey is either a number (standalone, legacy) or 'standalone__ID' / 'asset__ID'
    let seriesType = 'standalone';
    let seriesId;
    if (typeof seriesKey === 'string' && seriesKey.includes('__')) {
      const parts = seriesKey.split('__');
      seriesType = parts[0];
      seriesId = Number(parts[1]);
    } else {
      seriesId = Number(seriesKey);
    }
    const endpoint = seriesType === 'asset'
      ? '/api/balance_sheet/assets/asset-recurring-series/bulk-link/'
      : '/api/balance_sheet/assets/standalone-recurring-series/bulk-link/';
    const cashIds = [];
    const revolverIds = [];
    for (const key of selectedSearchRows) {
      const [type, id] = key.split('-');
      if (type === 'cash') cashIds.push(Number(id));
      else revolverIds.push(Number(id));
    }
    const payload = (ids, accountType) => ({
      series_id: seriesId,
      transaction_ids: ids,
      account_type: accountType,
      ...(resolvedCategory ? { resolved_category: resolvedCategory, force: true } : {}),
    });
    if (cashIds.length > 0) {
      await axios.post(endpoint, payload(cashIds, 'cash'));
    }
    if (revolverIds.length > 0) {
      await axios.post(endpoint, payload(revolverIds, 'revolver'));
    }
  };

  // Save bulk series link — groups selected rows by account type and calls bulk endpoint
  const handleBulkSeriesSave = async () => {
    setBulkSeriesError('');
    setBulkSeriesSaving(true);
    let seriesId = null;
    try {
      if (bulkSeriesMode === 'existing') {
        if (!bulkSelectedSeriesId) { setBulkSeriesError('Please select an existing series.'); setBulkSeriesSaving(false); return; }
        seriesId = bulkSelectedSeriesId; // may be 'standalone__ID' or 'asset__ID'
      } else {
        if (!bulkNewName.trim()) { setBulkSeriesError('Please enter a series name.'); setBulkSeriesSaving(false); return; }
        if (bulkNewIsAssetLinked) {
          if (!bulkNewAssetId) { setBulkSeriesError('Please select an asset.'); setBulkSeriesSaving(false); return; }
          if (!bulkNewCategory) { setBulkSeriesError('Please select an expense category for this asset series.'); setBulkSeriesSaving(false); return; }
          const res = await axios.post('/api/balance_sheet/assets/asset-recurring-series/create/', {
            asset_id: Number(bulkNewAssetId),
            display_name: bulkNewName.trim(),
            recurring_type: bulkNewType,
            recurring_frequency: bulkNewFrequency,
            vendor_payee: bulkNewVendor.trim() || null,
            expense_category: bulkNewCategory,
          });
          seriesId = `asset__${res.data.id}`;
        } else {
          const res = await axios.post('/api/balance_sheet/assets/standalone-recurring-series/', {
            display_name: bulkNewName.trim(),
            recurring_type: bulkNewType,
            recurring_frequency: bulkNewFrequency,
            vendor_payee: bulkNewVendor.trim() || null,
            series_user_description: bulkNewUserDescription.trim() || null,
          });
          seriesId = res.data.id;
        }
      }
      await executeBulkLink(seriesId, bulkNewIsAssetLinked ? bulkNewCategory : null);
      setShowBulkSeriesModal(false);
      setSelectedSearchRows(new Set());
      handleSearch();
      fetchAll();
      if (pageView === 'timeseries') refreshTsData();
    } catch (e) {
      const conflictData = e?.response?.data || e?.data;
      if (conflictData?.conflict) {
        // Show conflict resolution step — store the resolved seriesId for the confirm handler
        setBulkConflictCategories(conflictData.categories || []);
        setBulkResolvedCategory(conflictData.categories?.[0] || '');
        setBulkPendingSeriesId(seriesId);  // works for both existing and newly-created series
        setBulkSeriesError('');
      } else {
        setBulkSeriesError('Failed to save. Please try again.');
      }
    } finally {
      setBulkSeriesSaving(false);
    }
  };

  // Open bulk series modal for a single transaction row (from Budget/TimeSeries/Search inline button)
  const openSingleSeriesModal = (r) => {
    setSelectedSearchRows(new Set([`${r.sourceType}-${r.id}`]));
    openBulkSeriesModal();
  };

  // Confirm category conflict resolution and proceed with linking
  const handleBulkConflictConfirm = async () => {
    if (!bulkResolvedCategory) { setBulkSeriesError('Please select a category.'); return; }
    setBulkSeriesError('');
    setBulkSeriesSaving(true);
    try {
      await executeBulkLink(bulkPendingSeriesId, bulkResolvedCategory);
      setBulkConflictCategories([]);
      setBulkPendingSeriesId(null);
      setShowBulkSeriesModal(false);
      setSelectedSearchRows(new Set());
      handleSearch();
      fetchAll();
      if (pageView === 'timeseries') refreshTsData();
    } catch (e) {
      setBulkSeriesError('Failed to save. Please try again.');
    } finally {
      setBulkSeriesSaving(false);
    }
  };

  useEffect(() => {
    const onDocClick = () => setOpenDropdownId(null);
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  // Determine active budget for the selected period (use budget active at the start of the period)
  const activeBudget = useMemo(() => {
    if (!Array.isArray(budgets) || budgets.length === 0) return null;
    const start = dateRange[0];
    const startOfMonth = new Date(start.getFullYear(), start.getMonth(), 1);
    const endOfMonth = new Date(start.getFullYear(), start.getMonth()+1, 0, 23,59,59,999);
    const matches = budgets.filter(b => {
      const bs = new Date(b.start_date);
      const be = b.end_date ? new Date(b.end_date) : null;
      const startsBeforeMonthEnd = bs <= endOfMonth;
      const endsAfterMonthStart = (be == null) || (be >= startOfMonth);
      return startsBeforeMonthEnd && endsAfterMonthStart;
    }).sort((a,b) => new Date(b.start_date) - new Date(a.start_date));
    return matches[0] || null;
  }, [budgets, dateRange]);

  // Months in selected period (inclusive)
  const monthsInPeriod = useMemo(() => {
    const s = dateRange[0];
    const e = dateRange[1];
    if (!s || !e) return 1;
    const sm = s.getFullYear() * 12 + s.getMonth();
    const em = e.getFullYear() * 12 + e.getMonth();
    return Math.max(1, (em - sm + 1));
  }, [dateRange]);

  // Compute actual spend total for the selected period
  const actualSpend = useMemo(() => {
    return (chartTransactions || []).reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
  }, [chartTransactions]);

  const periodLabel = useMemo(() => {
    if (!showMulti) {
      const [y, m] = month.split('-').map(Number);
      const d = new Date(y, m - 1, 1);
      return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    const [ys, ms] = startMonth.split('-').map(Number);
    const [ye, me] = endMonth.split('-').map(Number);
    const ds = new Date(ys, ms - 1, 1);
    const de = new Date(ye, me - 1, 1);
    const s = ds.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    const e = de.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    return `${s} – ${e}`;
  }, [showMulti, month, startMonth, endMonth]);

  // Aggregate actuals by category slug (child level)
  const actualByCategory = useMemo(() => {
    const map = new Map();
    const EXCLUDE = new Set(['income_nonrecurring','income_recurring','debt_disbursement','rewards_cashback','tax_refund','transfer','debt_refinancing']);
    for (const t of (chartTransactions || [])) {
      const cat = String(t.category || t.expense_category || 'none');
      if (EXCLUDE.has(cat)) continue;
      const prev = map.get(cat) || 0;
      const amt = Number(t.amount) || 0;
      map.set(cat, prev + amt);
    }
    return map; // slug -> amount
  }, [chartTransactions]);

  // Sorting state for the Budget table (Category view)
  const [budgetSortField, setBudgetSortField] = useState('category'); // 'category' | 'budget' | 'actual' | 'diff'
  const [budgetSortDirection, setBudgetSortDirection] = useState('asc');
  const handleBudgetSort = (field) => {
    if (budgetSortField === field) {
      setBudgetSortDirection(budgetSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setBudgetSortField(field);
      setBudgetSortDirection('asc');
    }
  };
  const BudgetSortIcon = ({ field }) => {
    if (budgetSortField !== field) return <span className="text-muted ms-1" style={{ cursor: 'pointer' }}>⇅</span>;
    return (
      <span className="text-primary ms-1" style={{ cursor: 'pointer' }}>
        {budgetSortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Scaled budget category map for selected period
  const budgetCategoryMapScaled = useMemo(() => {
    const rawMonthly = activeBudget?.monthly_category_amounts ?? null;
    const rawOverrides = activeBudget?.category_monthly_overrides ?? null;
    const rawFixed = activeBudget?.category_amounts ?? null;

    if ((!rawMonthly || typeof rawMonthly !== 'object') && (!rawFixed || typeof rawFixed !== 'object')) return null;

    const out = {};
    const keys = new Set([
      ...Object.keys(rawMonthly || {}),
      ...Object.keys(rawFixed || {}),
      ...Object.keys(rawOverrides || {})
    ]);

    // Determine which months are covered by the current selection
    const start = dateRange[0];
    const end = dateRange[1];
    const months = [];
    let curr = new Date(start.getFullYear(), start.getMonth(), 1);
    const stop = new Date(end.getFullYear(), end.getMonth(), 1);
    while (curr <= stop) {
      const y = curr.getFullYear();
      const m = String(curr.getMonth() + 1).padStart(2, '0');
      months.push(`${y}-${m}`);
      curr.setMonth(curr.getMonth() + 1);
      if (months.length > 120) break; // Safety
    }

    keys.forEach(k => {
      const catOverrides = rawOverrides?.[k];
      if (catOverrides && typeof catOverrides === 'object' && Object.keys(catOverrides).length > 0) {
        // Use specific overrides for the selected months
        let total = 0;
        months.forEach(m => {
          if (catOverrides[m] !== undefined) {
            total += Number(catOverrides[m]);
          } else {
            // Fallback to average monthly if specific month is missing in overrides?
            // Or fallback to fixed amount?
            // Usually, if a category is in "monthly" mode, we should use the override or 0.
            // But let's check what the modal does. Modal defaults to 0 if not set.
            total += 0;
          }
        });
        out[k] = total;
      } else {
        // Fallback to fixed/monthly average scaled by number of months
        const monthlyAmt = Number(rawMonthly?.[k] ?? rawFixed?.[k] ?? 0);
        out[k] = monthlyAmt * (showMulti ? monthsInPeriod : 1);
      }
    });

    // Add parent rollup entries so the pie chart (which groups by parent) gets correct month-specific totals.
    // For parents with children: sum child values. For leaf parents: use own value.
    const items = Array.isArray(categoryChoices?.items) ? categoryChoices.items : [];
    for (const p of items) {
      const pVal = String(p.value);
      const children = Array.isArray(p.children) ? p.children : [];
      if (children.length > 0) {
        out[pVal] = children.reduce((sum, ch) => sum + Number(out[String(ch.value)] || 0), 0);
      } else if (!(pVal in out)) {
        // leaf parent not already set — use its own value if present
        const monthlyAmt = Number(rawMonthly?.[pVal] ?? rawFixed?.[pVal] ?? 0);
        out[pVal] = monthlyAmt * (showMulti ? monthsInPeriod : 1);
      }
    }

    return out;
  }, [activeBudget, showMulti, monthsInPeriod, dateRange, categoryChoices]);

  // Collapse/expand state for parent categories in Budget table
  const [collapsedParents, setCollapsedParents] = useState(new Set());
  const toggleParentCollapse = (parentValue) => {
    setCollapsedParents(prev => {
      const next = new Set(prev);
      if (next.has(parentValue)) next.delete(parentValue); else next.add(parentValue);
      return next;
    });
  };

  const toggleAllParents = () => {
    const parentValues = (Array.isArray(categoryTableRows) ? categoryTableRows : [])
      .filter(r => r.isParent && r.hasChildren)
      .map(r => String(r.value));
    const allCollapsed = parentValues.every(v => collapsedParents.has(v));
    if (allCollapsed) {
      // If all currently collapsed -> expand all
      setCollapsedParents(new Set());
    } else {
      // Otherwise collapse all
      setCollapsedParents(new Set(parentValues));
    }
  };


  // Build category table rows: parents with children indented
  const categoryTableRows = useMemo(() => {
    try {
      // Fast lookup for budget amounts (scaled by period length if applicable)
      const budgetMap = budgetCategoryMapScaled || {};

      const rows = [];
      const items = Array.isArray(categoryChoices.items) ? categoryChoices.items : [];

      // Helper: compute parent actual. If parent has children, define parent actual as the sum of children ONLY (no direct parent rows).
      // If parent has no children, use the parent’s own actual value.
      const getParentActual = (parent) => {
        const pVal = String(parent.value);
        const kids = Array.isArray(parent.children) ? parent.children : [];
        if (kids.length > 0) {
          let total = 0;
          for (const ch of kids) {
            const v = String(ch.value);
            total += Number(actualByCategory.get(v) || 0);
          }
          return total;
        }
        return Number(actualByCategory.get(pVal) || 0);
      };

      const EXCLUDE = new Set(['income_nonrecurring','income_recurring','debt_disbursement','rewards_cashback','tax_refund','transfer']);
      for (const p of items) {
        const pVal = String(p.value);
        if (EXCLUDE.has(pVal)) continue; // Skip non-expense categories entirely
        const children = Array.isArray(p.children) ? p.children : [];
        const pActual = getParentActual(p);
        // For parents with children, aggregate budget from children so the displayed
        // parent total always reflects the period-correct child amounts.
        const pBudget = children.length > 0
          ? children.reduce((sum, ch) => sum + Number(budgetMap[String(ch.value)] || 0), 0)
          : Number(budgetMap[pVal] || 0);
        const pDiff = pBudget - pActual; // Over(-)/Under(+) semantics
        rows.push({
          value: pVal,
          label: String(p.label || pVal),
          indent: 0,
          isParent: true,
          hasChildren: children.length > 0,
          budget: pBudget,
          actual: pActual,
          diff: pDiff,
        });
        if (children.length) {
          for (const ch of children) {
            const v = String(ch.value);
            const actual = Number(actualByCategory.get(v) || 0);
            const budget = Number(budgetMap[v] || 0);
            const diff = budget - actual;
            // Show all child rows regardless of values to ensure full visibility
            rows.push({
              value: v,
              label: String(ch.label || v),
              indent: 1,
              isParent: false,
              budget,
              actual,
              diff,
            });
          }
        }
      }
      return rows;
    } catch (e) {
      return [];
    }
  }, [categoryChoices, actualByCategory, budgetCategoryMapScaled]);

  // Group parents with children and sort by parent only
  const processedCategoryRows = useMemo(() => {
    const rows = Array.isArray(categoryTableRows) ? categoryTableRows : [];
    if (!rows.length) return [];

    // Build blocks: [ { parent, children: [] } ]
    const blocks = [];
    let current = null;
    for (const r of rows) {
      if (r.isParent) {
        current = { parent: r, children: [] };
        blocks.push(current);
      } else if (current) {
        current.children.push(r);
      }
    }

    // Comparator based on budgetSortField using parent row values
    const dir = budgetSortDirection === 'asc' ? 1 : -1;
    const sortedBlocks = [...blocks].sort((a, b) => {
      let av, bv;
      switch (budgetSortField) {
        case 'budget': av = Number(a.parent.budget || 0); bv = Number(b.parent.budget || 0); break;
        case 'actual': av = Number(a.parent.actual || 0); bv = Number(b.parent.actual || 0); break;
        case 'diff': av = Number(a.parent.diff || 0); bv = Number(b.parent.diff || 0); break;
        case 'category':
        default:
          av = String(a.parent.label || '').toLowerCase();
          bv = String(b.parent.label || '').toLowerCase();
          break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });

    // Flatten back: parent then its (maybe hidden) children
    const out = [];
    for (const blk of sortedBlocks) {
      out.push(blk.parent);
      const isCollapsed = collapsedParents.has(String(blk.parent.value));
      if (!isCollapsed) {
        for (const ch of blk.children) out.push(ch);
      }
    }
    return out;
  }, [categoryTableRows, budgetSortField, budgetSortDirection, collapsedParents]);

  // Sorting for the Time Series table
  const handleTsSort = (field) => {
    const f = String(field);
    if (tsSortField === f) {
      setTsSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setTsSortField(f);
      setTsSortDirection('desc');
    }
  };

  const TsSortIcon = ({ field }) => {
    if (tsSortField !== field) return <span className="text-muted ms-1" style={{ cursor: 'pointer' }}>⇅</span>;
    return (
      <span className="text-primary ms-1" style={{ cursor: 'pointer' }}>
        {tsSortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Sorting state for the Expenses table
  const handleSort = (field) => {
    const f = String(field);
    if (sortField === f) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(f);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="text-muted ms-1" style={{ cursor: 'pointer' }}>⇅</span>;
    return (
      <span className="text-primary ms-1" style={{ cursor: 'pointer' }}>
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const handleExport = () => {
    const columns = [
      { header: 'Date', key: t => {
        const d = t.date || t.transaction_date;
        return d ? new Date(d).toLocaleDateString() : '';
      }},
      { header: 'Description', key: 'description' },
      { header: 'User Description', key: 'user_description' },
      { header: 'Amount', key: 'amount' },
      { header: 'Category', key: t => getCategoryLabel(t.category || t.expense_category) },
      { header: 'Source', key: 'source' }
    ];

    const isTimeSeries = pageView === 'timeseries';
    const dataToExport = isTimeSeries ? tsProcessedRows : tableRows;
    const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '_');
    const prefix = isTimeSeries ? 'Expenses_TimeSeries' : 'Expenses_Budget';
    const filename = `${prefix}_Export_${dateStr}`;
    
    exportToExcel(dataToExport, filename, columns);
  };

  // Sorting helpers
  const ExcelIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
      <polyline points="10 9 9 9 8 9"></polyline>
    </svg>
  );




  const budgetName = activeBudget?.name || 'Budget';
  const budgetAmount = useMemo(() => {
    if (!budgetCategoryMapScaled || !categoryChoices?.items) return null;
    const EXCLUDE = new Set(['income_nonrecurring', 'income_recurring', 'debt_disbursement', 'rewards_cashback', 'tax_refund', 'transfer', 'debt_refinancing']);
    const items = Array.isArray(categoryChoices.items) ? categoryChoices.items : [];
    // Build a set of all child slugs so we can distinguish parents-with-children from leaves
    const childSlugs = new Set();
    const parentWithChildren = new Set();
    for (const p of items) {
      const kids = Array.isArray(p.children) ? p.children : [];
      if (kids.length > 0) {
        parentWithChildren.add(String(p.value));
        kids.forEach(ch => childSlugs.add(String(ch.value)));
      }
    }
    // Sum: leaf parents (no children) + child-level amounts (for parents-with-children).
    // Avoid summing parent slug when children are already counted to prevent double-counting.
    let total = 0;
    for (const p of items) {
      const pVal = String(p.value);
      if (EXCLUDE.has(pVal)) continue;
      const kids = Array.isArray(p.children) ? p.children : [];
      if (kids.length > 0) {
        // Sum child slugs for this parent
        let childrenSum = 0;
        for (const ch of kids) {
          childrenSum += Number(budgetCategoryMapScaled[String(ch.value)] || 0);
        }
        // If children sum is 0 but parent has a value (e.g. payments block uses debt slugs
        // not in categoryChoices), fall back to the parent's own budget amount
        total += childrenSum > 0 ? childrenSum : Number(budgetCategoryMapScaled[pVal] || 0);
      } else {
        // Leaf parent — use its own budget amount
        total += Number(budgetCategoryMapScaled[pVal] || 0);
      }
    }
    return total;
  }, [budgetCategoryMapScaled, categoryChoices]);

  if (loading) return <div className="content-container"><h2>Loading expenses…</h2></div>;
  if (error) return <div className="content-container"><h2>{error}</h2></div>;
  const overUnder = (budgetAmount != null) ? (budgetAmount - actualSpend) : null;
  const overUnderRounded = overUnder != null ? Math.round(overUnder) : null;
  const overUnderColor = overUnderRounded != null ? (overUnderRounded >= 0 ? '#16a34a' : '#ef4444') : undefined;


  const startEditing = (row, field, currentValue) => {
    setEditingCell({ sourceType: row.sourceType, id: row.id, field });
    if (field === 'transaction_date') {
      // r.date is already YYYY-MM-DD
      setEditValue(row.date || '');
    } else if (field === 'amount') {
      // Show absolute value for editing to avoid confusion with negatives
      const absVal = Math.abs(Number(row.amount) || 0);
      setEditValue(String(absVal));
    } else {
      setEditValue(currentValue || '');
    }
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEditing();
  };

  const saveEdit = async () => {
    if (!editingCell || saving) return;
    setSaving(true);
    try {
      const { sourceType, id, field } = editingCell;
      let payload = {};
      if (field === 'amount') {
        const num = parseFloat(editValue);
        if (isNaN(num)) { alert('Please enter a valid number'); setSaving(false); return; }
        // Revolver API expects positive for purchases; Cash expects negative for expenses
        if (sourceType === 'revolver') payload.amount = num; else payload.amount = -Math.abs(num);
      } else if (field === 'transaction_date') {
        // Validate date
        const d = new Date(editValue);
        if (isNaN(d.getTime())) { alert('Please enter a valid date'); setSaving(false); return; }
        payload.transaction_date = editValue;
      } else if (field === 'description') {
        const v = (editValue || '').trim();
        if (!v) { alert('Description cannot be empty'); setSaving(false); return; }
        payload.description = v;
      } else if (field === 'user_description') {
        payload.user_description = editValue || '';
      } else {
        setSaving(false);
        return;
      }

      if (sourceType === 'revolver') {
        await axios.patch(`/api/balance_sheet/financing/revolvers/transactions/${id}/`, payload);
        // Update local state for ccTxns
        setCcTxns(prev => prev.map(t => t.id === id ? { ...t, ...payload } : t));
        // Update local state for cashTxns if it happens to be there (shared rows)
        setCashTxns(prev => prev.map(t => t.id === id && t.sourceType === 'revolver' ? { ...t, ...payload } : t));
        // Update search results
        setSearchResults(prev => prev.map(t => t.id === id && t.sourceType === 'revolver' ? { ...t, ...payload, amount: payload.amount !== undefined ? Math.abs(payload.amount) : t.amount } : t));
      } else {
        await axios.patch(`/api/balance_sheet/assets/cash_accounts/transactions/${id}/`, payload);
        setCashTxns(prev => prev.map(t => t.id === id ? { ...t, ...payload } : t));
        // Update local state for ccTxns if it happens to be there
        setCcTxns(prev => prev.map(t => t.id === id && t.sourceType === 'cash' ? { ...t, ...payload } : t));
        // Update search results
        setSearchResults(prev => prev.map(t => t.id === id && t.sourceType === 'cash' ? { ...t, ...payload, amount: payload.amount !== undefined ? Math.abs(payload.amount) : t.amount } : t));
      }
      setEditingCell(null);
      setEditValue('');
    } catch (e) {
      console.error('Failed to save edit', e);
      alert('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const renderEditableCell = (row, field, displayValue, className = '') => {
    const isEditing = editingCell && editingCell.sourceType === row.sourceType && editingCell.id === row.id && editingCell.field === field;
    if (isEditing) {
      return (
        <div className={`d-flex align-items-center gap-2 ${className}`} onClick={(e)=> e.stopPropagation()}>
          <input
            type={field === 'transaction_date' ? 'date' : field === 'amount' ? 'number' : 'text'}
            className="form-control form-control-sm"
            value={editValue}
            onChange={(e)=> setEditValue(e.target.value)}
            onKeyDown={handleKeyPress}
            autoFocus
            step={field === 'amount' ? '0.01' : undefined}
            style={{ maxWidth: field === 'amount' ? 120 : undefined }}
          />
          <button className="btn btn-sm btn-success" onClick={saveEdit} disabled={saving} title="Save (Enter)">{saving ? '...' : '✓'}</button>
          <button className="btn btn-sm btn-secondary" onClick={cancelEditing} disabled={saving} title="Cancel (Esc)">✗</button>
        </div>
      );
    }
    return (
      <div
        className={`editable-cell ${className}`}
        style={{ position:'relative', cursor:'pointer', padding:'4px' }}
        onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); startEditing(row, field, field === 'transaction_date' ? row.date : row[field]); }}
        onMouseEnter={(e)=>{ const icon = e.currentTarget.querySelector('.edit-icon'); if (icon) icon.style.opacity = '1'; }}
        onMouseLeave={(e)=>{ const icon = e.currentTarget.querySelector('.edit-icon'); if (icon) icon.style.opacity = '0'; }}
      >
        <span>{displayValue}</span>
        <span className="edit-icon" style={{ opacity:0, marginLeft:8, fontSize:12, color:'#6c757d', transition:'opacity 0.2s', pointerEvents:'none' }}>✏️</span>
      </div>
    );
  };

  const renderSourceCell = (r) => {
    let url = '';
    if (r.sourceType === 'cash') {
      if (r.source_account_id) {
        url = `/balance_sheet/assets/cash_assets/${r.source_account_id}`;
      }
    } else if (r.sourceType === 'revolver') {
      if (r.revolver_id) {
        if (r.revolver_type === 'heloc') {
          url = `/balance_sheet/financing/revolvers/helocs/${r.revolver_id}`;
        } else {
          url = `/balance_sheet/financing/revolvers/credit-cards/${r.revolver_id}`;
        }
      }
    }

    if (!url) return <span>{r.source}</span>;

    return (
      <Link 
        to={url} 
        style={{ color: '#93c5fd', textDecoration: 'none' }}
        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
      >
        {r.source}
      </Link>
    );
  };

  const renderCategoryCell = (r) => {
    const category = (r.category && typeof r.category === 'string' && r.category.length) ? r.category : 'none';
    const tree = categoryChoices;
    const getLabelForVal = (val) => {
      const v = String(val);
      return categoryLabelMap.get(v) || (tree.noneItem?.label || 'None');
    };
    const normalizedCategory = category; // show current as-is
    const label = getLabelForVal(normalizedCategory);
    const isOpen = openDropdownId === `${r.sourceType}-${r.id}`;
    const style = getCategoryStyle(normalizedCategory);
    const isParentExpanded = (parentVal) => {
      const set = expandedParents[`${r.sourceType}-${r.id}`];
      return !!(set && set.has(parentVal));
    };
    const toggleParentExpand = (parentVal) => {
      setExpandedParents(prev => {
        const key = `${r.sourceType}-${r.id}`;
        const cur = new Set(prev[key] || []);
        if (cur.has(parentVal)) cur.delete(parentVal); else cur.add(parentVal);
        return { ...prev, [key]: cur };
      });
    };
    return (
      <div className="position-relative" style={{ display: 'inline-block' }}>
        <button
          className="btn btn-sm"
          type="button"
          onClick={(e) => toggleDropdown(e, `${r.sourceType}-${r.id}`)}
          style={{ backgroundColor: style.backgroundColor, color: style.color, borderRadius: '12px', fontSize: '0.75rem', padding: '4px 8px', cursor: 'pointer', border: `1px solid ${style.borderColor}`, whiteSpace:'nowrap' }}
        >
          {label} ▼
        </button>
        {isOpen && (
          <div
            className="position-absolute"
            style={{ top: '100%', left: 0, zIndex: 1000, minWidth: '320px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '0.375rem', boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.3)', marginTop: 2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ maxHeight: '480px', overflowY: 'auto', padding: '4px 0' }}>
              {tree.items.map((it) => (
                <div key={it.value}>
                  <button
                    className="w-100 text-start"
                    style={{ display: 'block', padding: '8px 16px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                    onClick={() => {
                      if (Array.isArray(it.children) && it.children.length > 0) {
                        toggleParentExpand(it.value);
                      } else {
                        handleCategoryChange(r, String(it.value));
                        setOpenDropdownId(null);
                      }
                    }}
                  >
                    {(Array.isArray(it.children) && it.children.length > 0) ? (isParentExpanded(it.value) ? '▼ ' : '► ') : ''}
                    {it.label}
                  </button>
                  {(Array.isArray(it.children) && it.children.length > 0 && isParentExpanded(it.value)) && (
                    <div>
                      {it.children.map(ch => (
                        <button
                          key={ch.value}
                          className="w-100 text-start"
                          style={{ display: 'block', padding: '6px 16px 6px 28px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                          onClick={() => { handleCategoryChange(r, String(ch.value)); setOpenDropdownId(null); }}
                        >
                          {ch.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div style={{ borderTop: '1px solid #374151', margin: '4px 0' }} />
              <button
                className="w-100 text-start"
                style={{ display: 'block', padding: '8px 16px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                onClick={() => { handleCategoryChange(r, String(tree.noneItem.value)); setOpenDropdownId(null); }}
              >
                {tree.noneItem.label || 'None'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const deleteTransaction = async (row) => {
    const key = `${row.sourceType}-${row.id}`;
    if (!window.confirm('Are you sure you want to delete this transaction? This cannot be undone.')) return;
    setDeleting(prev => new Set([...prev, key]));
    try {
      if (row.sourceType === 'revolver') {
        await axios.delete(`/api/balance_sheet/financing/revolvers/transactions/${row.id}/`);
        setCcTxns(prev => prev.filter(t => t.id !== row.id));
        setSearchResults(prev => prev.filter(t => !(t.id === row.id && t.sourceType === 'revolver')));
      } else {
        await axios.delete(`/api/balance_sheet/assets/cash_accounts/transactions/${row.id}/`);
        setCashTxns(prev => prev.filter(t => t.id !== row.id));
        setSearchResults(prev => prev.filter(t => !(t.id === row.id && t.sourceType === 'cash')));
      }
    } catch (e) {
      console.error('Failed to delete transaction', e);
      alert('Failed to delete transaction.');
    } finally {
      setDeleting(prev => { const ns = new Set(prev); ns.delete(key); return ns; });
    }
  };

  return (
    <div className="content-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0 }}>Expenses</h2>
        <div className="btn-group" role="group">
          <button 
            type="button" 
            className={`btn ${pageView === 'budget' ? 'btn-primary' : 'btn-outline-primary'}`} 
            onClick={() => setPageView('budget')}
          >
            Budget
          </button>
          <button 
            type="button" 
            className={`btn ${pageView === 'timeseries' ? 'btn-primary' : 'btn-outline-primary'}`} 
            onClick={() => setPageView('timeseries')}
          >
            Time Series
          </button>
          <button
            type="button"
            className={`btn ${pageView === 'recurring' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setPageView('recurring')}
          >
            Recurring
          </button>
          <button 
            type="button" 
            className={`btn ${pageView === 'search' ? 'btn-primary' : 'btn-outline-primary'}`} 
            onClick={() => setPageView('search')}
          >
            Transaction Search
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="chart-controls" style={{ marginBottom: '12px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div className="control-group">
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }} >
            {pageView === 'budget' ? (
              <>
                {!showMulti ? (
                  <>
                    <label className="control-label" htmlFor="expense-month-picker">Calendar Month:</label>
                    <input id="expense-month-picker" name="expense-month-picker" type="month" value={month} onChange={e => setMonth(e.target.value)} className="control-select" />
                  </>
                ) : (
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <label className="control-label" htmlFor="expense-start-month">Start Month:</label>
                    <input id="expense-start-month" name="expense-start-month" type="month" value={startMonth} onChange={e => setStartMonth(e.target.value)} className="control-select" />
                    <label className="control-label" htmlFor="expense-end-month">End Month:</label>
                    <input id="expense-end-month" name="expense-end-month" type="month" value={endMonth} onChange={e => setEndMonth(e.target.value)} className="control-select" />
                  </div>
                )}
                <label className="form-check-label" htmlFor="showMultiCheckbox" style={{ display:'flex', alignItems:'center', gap:6, marginLeft:16, whiteSpace:'nowrap' }}>
                  <input id="showMultiCheckbox" name="showMultiCheckbox" type="checkbox" className="form-check-input" checked={showMulti} onChange={(e)=> setShowMulti(e.target.checked)} />
                  Show multiple months
                </label>
              </>
            ) : pageView === 'timeseries' ? (
              <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <label className="control-label" htmlFor="ts-start-date">Start Date:</label>
                  <input id="ts-start-date" type="date" value={tsStartDate} onChange={e => setTsStartDate(e.target.value)} className="control-select" />
                  <label className="control-label" htmlFor="ts-end-date">End Date:</label>
                  <input id="ts-end-date" type="date" value={tsEndDate} onChange={e => setTsEndDate(e.target.value)} className="control-select" />
                </div>
                <button 
                  onClick={handleExport}
                  className="btn btn-outline-success btn-sm d-flex align-items-center gap-2"
                  style={{ height: 'fit-content', whiteSpace: 'nowrap', padding: '6px 12px' }}
                  title="Export to Excel"
                >
                  <ExcelIcon />
                  <span>Export to Excel</span>
                </button>
              </div>
            ) : pageView === 'search' ? (
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <input
                  type="text"
                  placeholder="Search description..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="form-control"
                  style={{ width: 240 }}
                />
                <label className="control-label" htmlFor="search-start-date">Start:</label>
                <input id="search-start-date" type="date" value={searchStartDate} onChange={e => setSearchStartDate(e.target.value)} className="control-select" />
                <label className="control-label" htmlFor="search-end-date">End:</label>
                <input id="search-end-date" type="date" value={searchEndDate} onChange={e => setSearchEndDate(e.target.value)} className="control-select" />
                <label className="control-label" htmlFor="search-min-amount">Min $:</label>
                <input id="search-min-amount" type="number" step="0.01" value={searchMinAmount} onChange={e => setSearchMinAmount(e.target.value)} className="form-control" style={{ width: 110 }} />
                <label className="control-label" htmlFor="search-max-amount">Max $:</label>
                <input id="search-max-amount" type="number" step="0.01" value={searchMaxAmount} onChange={e => setSearchMaxAmount(e.target.value)} className="form-control" style={{ width: 110 }} />
                <label className="control-label" htmlFor="search-category">Category:</label>
                <select id="search-category" value={searchCategory} onChange={e => setSearchCategory(e.target.value)} className="form-select">
                  <option value="">Any</option>
                  {Array.isArray(categoryChoices.items) && categoryChoices.items.map(p => {
                    const parentLabel = p.label || String(p.value);
                    return (
                      <React.Fragment key={`p-${p.value}`}>
                        <option value={p.value}>{parentLabel}</option>
                        {Array.isArray(p.children) && p.children.map(ch => (
                          <option key={`c-${p.value}-${ch.value}`} value={ch.value}>
                            {parentLabel} - {ch.label || String(ch.value)}
                          </option>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {categoryChoices.noneItem && <option value={categoryChoices.noneItem.value}>{categoryChoices.noneItem.label}</option>}
                </select>
                <label className="control-label" htmlFor="search-asset">Asset:</label>
                <select id="search-asset" value={searchAsset} onChange={e => setSearchAsset(e.target.value)} className="form-select">
                  <option value="">Any</option>
                  {Array.isArray(allAssets) && allAssets.map(a => (
                    <option key={a.id} value={a.id}>{buildAssetLabel ? buildAssetLabel(a) : (a.name || a.label || a.id)}</option>
                  ))}
                </select>
                <button className="btn btn-primary" onClick={handleSearch} disabled={searchLoading}>
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
                <label style={{ display:'flex', alignItems:'center', gap:6, color:'#9ca3af', fontSize:'0.85rem', cursor:'pointer', marginLeft:4 }}>
                  <input type="checkbox" checked={searchExcludeWithSeries} onChange={e => setSearchExcludeWithSeries(e.target.checked)} />
                  Exclude Linked to Series
                </label>
              </div>
            ) : null}
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          {pageView === 'budget' && (
            <>
              <span style={{ color:'#9ca3af' }}>View:</span>
              <div className="btn-group btn-group-sm" role="group" aria-label="View toggle">
                <button type="button" className={`btn ${chartView === 'pie' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=> setChartView('pie')}>Pie Chart</button>
                <button type="button" className={`btn ${chartView === 'table' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=> setChartView('table')}>Table</button>
              </div>
            </>
          )}
        </div>
      </div>

      {pageView === 'budget' ? (
        <div style={{ background:'#0b1220', padding:'10px', borderRadius:'8px', border:'1px solid #374151', marginBottom:'16px' }}>
          {/* Header: Budget Summary (toggle in controls above) */}
          <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom: 8, color:'#e5e7eb' }}>
            <div style={{ flex:1 }}>
              {activeBudget ? (
                <div>
                  <div style={{ display:'grid', gridTemplateColumns:'auto 140px', columnGap: 8, rowGap: 2, alignItems:'baseline' }}>
                    <div style={{ fontWeight: 700 }}>{budgetName}:</div>
                    <div style={{ textAlign:'right', fontWeight: 700, fontVariantNumeric:'tabular-nums', fontFeatureSettings:'"tnum" 1' }}>
                      {formatCurrency(Math.round(budgetAmount ?? 0), 0)}
                    </div>

                    <div>{periodLabel} Spend:</div>
                    <div style={{ textAlign:'right', fontVariantNumeric:'tabular-nums', fontFeatureSettings:'"tnum" 1' }}>
                      {formatCurrency(Math.round(actualSpend), 0)}
                    </div>
                  </div>
                  <div style={{ borderBottom: '1px solid #374151', margin: '6px 0' }}></div>
                  <div style={{ display:'grid', gridTemplateColumns:'auto 140px', columnGap: 8, alignItems:'baseline' }}>
                    <div>Budget Over/Under:</div>
                    <div style={{ textAlign:'right', fontVariantNumeric:'tabular-nums', fontFeatureSettings:'"tnum" 1', color: overUnderColor }}>
                      {overUnderRounded != null ? formatCurrency(overUnderRounded, 0) : 'N/A'}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ color:'#9ca3af' }}>No budget found for this month.</div>
              )}
            </div>
          </div>

          {chartView === 'pie' ? (
            <TransactionsPieChart
              transactions={chartTransactions}
              negateForExpenses={false}
              includePayments={true}
              excludedCategories={["income_nonrecurring","income_recurring","income","debt_disbursement","rewards_cashback","tax_refund","transfer","debt_refinancing"]}
              budgetCategoryAmounts={budgetCategoryMapScaled}
              budgetMonthlyTotal={budgetAmount}
              categoryHierarchy={categoryChoices.items}
            />
          ) : (
            <>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems: 'center', marginBottom: 6 }}>
                <button 
                  onClick={handleExport} 
                  className="btn btn-outline-success btn-sm"
                  title="Export to Excel"
                >
                  <ExcelIcon /> Export
                </button>
                <div className="btn-group btn-group-sm" role="group" aria-label="Collapse">
                  <button type="button" className="btn btn-outline-secondary" onClick={toggleAllParents}>Collapse</button>
                </div>
              </div>
              <div className="table-responsive" style={{ marginTop:8 }}>
                <table className="table" style={{ margin:0, color:'#e5e7eb', background:'#0b1220', '--bs-table-bg':'#0b1220', '--bs-table-color':'#e5e7eb', '--bs-table-striped-bg':'#0f172a', '--bs-table-accent-bg':'transparent' }}>
                  <thead style={{ background:'#111827', color:'#e5e7eb' }}>
                    <tr>
                      <th style={{ whiteSpace:'nowrap', cursor:'pointer' }} onClick={()=>handleBudgetSort('category')}>Category <BudgetSortIcon field="category" /></th>
                      <th style={{ textAlign:'right', cursor:'pointer' }} onClick={()=>handleBudgetSort('budget')}>Budgeted <BudgetSortIcon field="budget" /></th>
                      <th style={{ textAlign:'right', cursor:'pointer' }} onClick={()=>handleBudgetSort('actual')}>Actual <BudgetSortIcon field="actual" /></th>
                      <th style={{ textAlign:'right', cursor:'pointer' }} onClick={()=>handleBudgetSort('diff')}>Over(-)/Under(+) <BudgetSortIcon field="diff" /></th>
                    </tr>
                  </thead>
                  <tbody style={{ background:'#0b1220' }}>
                    {processedCategoryRows.length ? processedCategoryRows.map((row, idx) => (
                    <tr key={`${row.value}-${idx}`} style={{ background: row.isParent ? (idx % 2 ? '#0f172a' : '#111827') : (idx % 2 ? '#0f172a' : '#111827') }}>
                      <td style={{ padding:'8px' }}>
                        <div
                          style={{ paddingLeft: row.indent ? 20 : 0, fontWeight: row.isParent ? 600 : 400, display:'flex', alignItems:'center', gap:6, cursor: (row.isParent && row.hasChildren) ? 'pointer' : 'default' }}
                          onClick={(row.isParent && row.hasChildren) ? () => toggleParentCollapse(String(row.value)) : undefined}
                          title={(row.isParent && row.hasChildren) ? (collapsedParents.has(String(row.value)) ? 'Expand' : 'Collapse') : undefined}
                        >
                          {(row.isParent && row.hasChildren) ? (
                            <span>{collapsedParents.has(String(row.value)) ? '►' : '▼'}</span>
                          ) : (
                            <span style={{ width: '1em' }}></span>
                          )}
                          <span>{row.label}</span>
                        </div>
                      </td>
                      <td style={{ padding:'8px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontFeatureSettings:'"tnum" 1' }}>{formatCurrency(Math.round(row.budget || 0), 0)}</td>
                      <td style={{ padding:'8px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontFeatureSettings:'"tnum" 1' }}>{formatCurrency(Math.round(row.actual || 0), 0)}</td>
                      <td style={{ padding:'8px', textAlign:'right', fontVariantNumeric:'tabular-nums', fontFeatureSettings:'"tnum" 1', color: (row.diff || 0) >= 0 ? '#22c55e' : '#ef4444' }}>{formatCurrency(Math.round(row.diff || 0), 0)}</td>
                    </tr>
                  )) : (
                    <tr style={{ background:'#111827' }}>
                      <td colSpan={4} className="text-center" style={{ color:'#9ca3af', padding:'12px' }}>No categories to display.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      ) : pageView === 'timeseries' ? (
        <div style={{ background:'#0b1220', padding:'20px', borderRadius:'8px', border:'1px solid #374151', marginBottom:'16px' }}>
          {loadingAllTime ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Fetching historical transactions...</p>
            </div>
          ) : (
            <>
              {tsSelectedCategory && (
                <div className="mb-3 d-flex align-items-center gap-2">
                  <span className="badge bg-primary d-flex align-items-center gap-2 p-2">
                    {tsSelectedCategory === 'all' ? 'All Expenses' : categoryLabelMap.get(tsSelectedCategory.includes('|') ? tsSelectedCategory.split('|')[1] : tsSelectedCategory) || tsSelectedCategory}
                    {tsSelectedPeriods.length > 0 && ` (${tsSelectedPeriods.join(', ')})`}
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      style={{ fontSize: '0.65rem' }}
                      onClick={() => { setTsSelectedCategory(null); setTsSelectedPeriods([]); }}
                    ></button>
                  </span>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => { setTsSelectedCategory(null); setTsSelectedPeriods([]); }}
                  >
                    Clear selection
                  </button>
                </div>
              )}
              <ExpensesTimeSeriesChart 
                transactions={allTimeTransactions}
                budgets={budgets}
                negateForExpenses={false}
                enforceExpenseSign={false}
                excludeTransfers={true}
                excludedCategories={["income_nonrecurring","income_recurring","income","debt_disbursement","rewards_cashback","tax_refund","transfer","debt_refinancing"]}
                categoryHierarchy={categoryChoices.items}
                startDate={tsStartDate}
                endDate={tsEndDate}
                onSelectCategory={setTsSelectedCategory}
                onSelectPeriods={setTsSelectedPeriods}
                selectedCategory={tsSelectedCategory}
                selectedPeriods={tsSelectedPeriods}
                excludeDebtPayments={tsExcludeDebtPayments}
                onExcludeDebtPaymentsChange={setTsExcludeDebtPayments}
              />
            </>
          )}
        </div>
      ) : null}

      {/* Transactions Table - Time Series view (full-featured, dynamically filtered by graph selection) */}
      {pageView === 'timeseries' && (
        <div style={{ marginTop: '24px' }}>
          <div className="d-flex justify-content-between align-items-end mb-3">
            <div>
              <h4 className="mb-0">
                Transactions for {!tsSelectedCategory || tsSelectedCategory === 'all' ? 'All Expenses' : (tsSelectedCategory === 'none' ? 'Uncategorized' : (categoryLabelMap.get(tsSelectedCategory.includes('|') ? tsSelectedCategory.split('|')[1] : tsSelectedCategory) || tsSelectedCategory))}
                {tsSelectedPeriods && tsSelectedPeriods.length > 0 && ` - ${tsSelectedPeriods.join(', ')}`}
              </h4>
            </div>
            <div className="text-end d-flex align-items-center gap-3">
              <div style={{ fontSize: '1.1rem', fontWeight: '500', color: '#e5e7eb' }}>
                Total Sum: <span style={{ color: '#3b82f6' }}>{formatCurrency(tsProcessedRows.reduce((sum, t) => sum + t.amount, 0))}</span>
              </div>
            </div>
          </div>
          <div className="data-table-container" style={{ margin: 0, overflowX: 'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', color:'#e5e7eb', minWidth: 0 }}>
              <thead style={{ background:'#1f2937' }}>
                <tr>
                  <th style={{ textAlign:'left', padding:'8px', minWidth:'140px', whiteSpace:'nowrap', cursor:'pointer' }} onClick={() => handleTsSort('date')}>Date <TsSortIcon field="date" /></th>
                  <th style={{ textAlign:'left', padding:'8px', cursor:'pointer' }} onClick={() => handleTsSort('description')}>Description <TsSortIcon field="description" /></th>
                  <th style={{ textAlign:'left', padding:'8px', cursor:'pointer' }} onClick={() => handleTsSort('user_description')}>User Description <TsSortIcon field="user_description" /></th>
                  <th style={{ textAlign:'left', padding:'8px', minWidth:'220px', whiteSpace:'nowrap', cursor:'pointer' }} onClick={() => handleTsSort('category')}>Category <TsSortIcon field="category" /></th>
                  <th style={{ textAlign:'right', padding:'8px', cursor:'pointer' }} onClick={() => handleTsSort('amount')}>Amount <TsSortIcon field="amount" /></th>
                  <th style={{ textAlign:'left', padding:'8px', cursor:'pointer' }} onClick={() => handleTsSort('source')}>Source <TsSortIcon field="source" /></th>
                  <th style={{ textAlign:'center', padding:'8px', minWidth:'70px' }}>Asset</th>
                  <th style={{ textAlign:'center', padding:'8px', minWidth:'60px' }}>To-Do</th>
                  <th style={{ textAlign:'center', padding:'8px', minWidth:'60px' }}>Series</th>
                  <th style={{ textAlign:'center', padding:'8px', minWidth:'60px' }}>Debt</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Receipt</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tsProcessedRows.length === 0 ? (
                  <tr>
                    <td colSpan={12} style={{ padding:'10px', color:'#9ca3af' }}>
                      No transactions found for the selected category and period.
                    </td>
                  </tr>
                ) : (
                  tsFinalRows.map((r, idx) => (
                    <tr
                      key={`${r.sourceType}-${r.id}-${idx}`}
                      style={{
                        backgroundColor: r._isChild ? (r._isReimbursementChild || r._isReimbursementOriginal ? '#0f2a1a' : 'rgba(255,255,255,0.03)') : (idx % 2 ? '#0f172a' : '#111827'),
                        borderLeft: r._isReimbursementChild ? '3px solid #34d399' : r._isReimbursementOriginal ? '3px solid #6ee7b7' : r._isChild ? '3px solid #60a5fa' : 'none',
                        borderBottom: '1px solid #374151'
                      }}
                    >
                      <td style={{ padding:'8px', minWidth:'140px', whiteSpace:'nowrap' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        {(!r._isChild && r._hasKids) ? (
                          <button
                            className="btn btn-link btn-sm p-0"
                            style={{ color:'#93c5fd', width:20, textDecoration:'none' }}
                            onClick={(e) => {
                              e.preventDefault();
                              const key = `${r.sourceType}-${r.id}`;
                              setTsExpandedTxns(prev => {
                                const next = new Set(prev);
                                if (next.has(key)) next.delete(key); else next.add(key);
                                return next;
                              });
                            }}
                          >
                            {tsExpandedTxns.has(`${r.sourceType}-${r.id}`) ? '▼' : '▶'}
                          </button>
                        ) : (
                          <span style={{ width: r._isChild ? 36 : 20, display:'inline-block' }} />
                        )}
                        <span>{renderEditableCell(r, 'transaction_date', r.date || r.transaction_date || '')}</span>
                        </div>
                      </td>
                      <td style={{ padding:'8px', maxWidth:'160px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.description || ''}>
                        {renderEditableCell(r, 'description', r.description || '')}
                      </td>
                      <td style={{ padding:'8px', maxWidth:'140px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.user_description || ''}>
                        {renderEditableCell(r, 'user_description', r.user_description || '')}
                      </td>
                      <td style={{ padding:'8px', minWidth:'220px', whiteSpace:'nowrap' }}>
                        {renderCategoryCell(r)}
                      </td>
                      <td style={{ padding:'8px', textAlign:'right' }}>
                        {renderEditableCell(r, 'amount', formatCurrency(r.amount))}
                      </td>
                      <td style={{ padding:'8px' }}>{renderSourceCell(r)}</td>
                      <td style={{ padding:'8px', textAlign:'center' }}>
                        <span style={{ display:'inline-flex', gap:4, alignItems:'center', justifyContent:'center' }}>
                          {(() => {
                            if (!r.linkedAssetId) {
                              const cat = String(r.category || r.expense_category || '');
                              const domain = getDomainForCategory(cat);
                              const isYardWaste = cat.includes('yard_waste');
                              const isWaste = domain === 'waste_management' || ((cat.startsWith('waste_') || cat.includes('sanitation')) && !isYardWaste);
                              const isLinkable = domain !== 'generic' || isWaste || isYardWaste;
                              if (!isLinkable) return null;
                              let labelBtn = 'Link Asset';
                              if (domain === 'waste_management' || isWaste) labelBtn = 'Link Waste';
                              else if (domain === 'utilities') labelBtn = 'Link Utilities';
                              else if (domain === 'home') labelBtn = 'Link Home';
                              else if (domain === 'landscaping' || isYardWaste) labelBtn = 'Link Landscaping';
                              else if (domain === 'vehicle') labelBtn = 'Link Vehicle';
                              return (
                                <button className="btn btn-link btn-sm" style={{ color:'#93c5fd', padding:0, fontSize:'0.75rem' }} title={labelBtn} onClick={(e) => { e.preventDefault(); openUtilitiesModal(r); }}>
                                  🔗
                                </button>
                              );
                            }
                            const asset = assetMaps.byId.get(String(r.linkedAssetId));
                            const url = buildAssetUrl(asset);
                            const cat = String(r.category || r.expense_category || '');
                            const domain = getDomainForCategory(cat);
                            const label = buildAssetLabel(asset, { ...assetMaps, transactionDomain: domain });
                            return (
                              <>
                                {url ? <Link to={url} title={label} style={{ color:'#93c5fd', fontSize:'1rem', lineHeight:1 }}>🔗</Link> : <span title={label} style={{ color:'#93c5fd', fontSize:'1rem' }}>🔗</span>}
                                <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.7rem' }} title="Edit Asset Link" onClick={(e) => { e.preventDefault(); openUtilitiesModal(r); }}>✏️</button>
                              </>
                            );
                          })()}
                        </span>
                      </td>
                      <td style={{ padding:'8px', textAlign:'center' }}>
                        <span style={{ display:'inline-flex', gap:4, alignItems:'center', justifyContent:'center' }}>
                          {(() => {
                            const todoInfo = r.linked_todo_info;
                            if (!todoInfo) return null;
                            let url = null;
                            if (todoInfo.asset_type === 'vehicle') url = `/balance_sheet/assets/physical_assets/vehicles/${todoInfo.asset_id}?tab=todo`;
                            else if (todoInfo.property_id) url = `/balance_sheet/assets/physical_assets/residential/${todoInfo.property_id}?tab=todo`;
                            const statusColor = todoInfo.status === 'completed' ? '#22c55e' : (todoInfo.status === 'in_progress' ? '#3b82f6' : '#9ca3af');
                            const badge = <span style={{ fontSize:'1rem', cursor:'pointer' }} title={`To-Do: ${todoInfo.title}\nStatus: ${todoInfo.status_display}`}>✅</span>;
                            return url ? <Link to={url} style={{ color: statusColor }}>{badge}</Link> : badge;
                          })()}
                        </span>
                      </td>
                      <td style={{ padding:'8px', textAlign:'center' }}>
                        <span style={{ display:'inline-flex', gap:4, alignItems:'center', justifyContent:'center' }}>
                          {(() => {
                            const expInfo = r.linked_expense_info;
                            const incInfo = r.linked_income_info;
                            if (r.standalone_recurring_series_id || r.standalone_recurring_series_name) {
                              const sid = r.standalone_recurring_series_id;
                              const sname = r.standalone_recurring_series_name || 'Recurring';
                              const url = `/income_statement/expenses?view=recurring&tab=series&series_id=${sid}&series_type=standalone`;
                              return (
                                <>
                                  <a href={url} target="_blank" rel="noreferrer" style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{sname}</a>
                                  <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.7rem' }} title="Edit Series Link" onClick={(e) => { e.preventDefault(); openSingleSeriesModal(r); }}>✏️</button>
                                </>
                              );
                            } else if (expInfo && expInfo.recurring_name) {
                              const url = r.linked_recurring_series_id ? `/income_statement/expenses?view=recurring&tab=series&series_id=${r.linked_recurring_series_id}&series_type=asset` : null;
                              return (
                                <>
                                  {url ? <a href={url} target="_blank" rel="noreferrer" style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{expInfo.recurring_name}</a> : <span style={{ color:'#e5e7eb' }}>{expInfo.recurring_name}</span>}
                                  <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.7rem' }} title="Edit Series Link" onClick={(e) => { e.preventDefault(); openUtilitiesModal(r); }}>✏️</button>
                                </>
                              );
                            } else if (incInfo && incInfo.display_name) {
                              const url = `/income?series_id=${r.linked_income_stream_id}`;
                              return (
                                <>
                                  <Link to={url} style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{incInfo.display_name}</Link>
                                  <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.7rem' }} title="Edit Series Link" onClick={(e) => { e.preventDefault(); setIncomeLinkRow(r); setShowIncomeLinkModal(true); }}>✏️</button>
                                </>
                              );
                            } else if (r.sourceType === 'cash' || r.sourceType === 'revolver') {
                              return (
                                <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.75rem' }} title="Link Recurring Series" onClick={(e) => { e.preventDefault(); openSingleSeriesModal(r); }}>🔗 Link Series</button>
                              );
                            }
                            return null;
                          })()}
                        </span>
                      </td>
                      <td style={{ padding:'8px', textAlign:'center' }}>
                        <span style={{ display:'inline-flex', gap:4, alignItems:'center', justifyContent:'center' }}>
                          {r.sourceType === 'cash' && r.linkedDebt && r.linkedDebtId ? (
                            <>
                              {(() => {
                                let url = null;
                                if (r.linkedDebt.type === 'auto_loan') url = `/balance_sheet/financing/auto_loans/${r.linkedDebtId}`;
                                else if (r.linkedDebt.type === 'mortgage') url = `/balance_sheet/financing/mortgages/${r.linkedDebtId}`;
                                else if (r.linkedDebt.type === 'credit_card' || r.linkedDebt.type === 'revolver') url = `/balance_sheet/financing/revolvers/credit-cards/${r.linkedDebtId}`;
                                else if (r.linkedDebt.type === 'heloc') url = `/balance_sheet/financing/revolvers/helocs/${r.linkedDebtId}`;
                                else if (r.linkedDebt.type === '401k_loan') url = `/balance_sheet/financing/401k_loans/${r.linkedDebtId}`;
                                const debtTitle = r.linkedDebt.debt_label || r.linkedDebt.debt_name || 'Debt';
                                return url ? <a href={url} title={debtTitle} target="_blank" rel="noopener noreferrer" style={{ color:'#93c5fd', whiteSpace:'nowrap', textDecoration:'none', fontSize:'0.85rem' }}>{debtTitle}</a> : <span title={debtTitle} style={{ fontSize:'0.85rem' }}>💳 {debtTitle}</span>;
                              })()}
                              <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.7rem' }} title="Edit Debt Link" onClick={(e) => { e.preventDefault(); if (String(r.category || r.expense_category || '') === 'debt_disbursement') { setDebtDisbursementRow(r); setShowDebtDisbursementModal(true); } else { openDebtModalForRow(r); } }}>✏️</button>
                            </>
                          ) : (
                            r.sourceType === 'cash' && (String(r.category || r.expense_category || '') === 'payments' || String(r.category || r.expense_category || '') === 'debt_disbursement') && (
                              <button className="btn btn-link btn-sm" style={{ color:'#93c5fd', padding:0, fontSize:'0.75rem' }} title="Link Debt" onClick={(e) => { e.preventDefault(); if (String(r.category || r.expense_category || '') === 'debt_disbursement') { setDebtDisbursementRow(r); setShowDebtDisbursementModal(true); } else { openDebtModalForRow(r); } }}>🔗</button>
                            )
                          )}
                        </span>
                      </td>
                      <td style={{ padding:'8px' }}>
                        {r.receipt || r.receipt_url ? (
                          <button className="btn btn-sm btn-outline-primary" onClick={() => r.sourceType === 'revolver' ? openCcReceipt(r) : openCashReceipt(r)}>View</button>
                        ) : (
                          <label className="btn btn-sm btn-outline-secondary mb-0">Upload<input type="file" accept="image/*,application/pdf" style={{ display:'none' }} onChange={(e) => { const file = e.target.files && e.target.files[0]; if (file) { if (r.sourceType === 'revolver') uploadCcReceipt(r.id, file); else uploadCashReceipt(r.id, file); } e.target.value = ''; }} /></label>
                        )}
                      </td>
                      <td style={{ padding:'8px' }}>
                        <div className="btn-group btn-group-sm" role="group" aria-label="Actions">
                          <button className="btn btn-outline-primary btn-sm" onClick={() => { const absAmt = Math.abs(Number(r.amount) || 0); const initial = absAmt > 0 ? [ { key: 1, amount: Math.round((absAmt/2) * 100)/100, description: r.description }, { key: 2, amount: Math.round((absAmt - Math.round((absAmt/2) * 100)/100) * 100)/100, description: r.description }, ] : [{ key: 1, amount: 0, description: r.description }, { key: 2, amount: 0, description: r.description }]; setSplitRow(r); setSplitItems(initial); setSplitError(''); setShowSplitModal(true); }}>Split</button>
                          <button className="btn btn-danger btn-sm" onClick={() => deleteTransaction(r)} disabled={deleting.has(`${r.sourceType}-${r.id}`)} style={{ fontSize: '0.75rem', padding: '2px 8px' }}>{deleting.has(`${r.sourceType}-${r.id}`) ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Deleting...</> : 'Delete'}</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pageView === 'recurring' && (
        <AllRecurringSeriesPanel
          allAssets={allAssets}
          navigate={navigate}
          categoryChoices={categoryChoices}
        />
      )}

      {pageView === 'search' && (
        <div style={{ marginTop: '24px' }}>
          <div className="d-flex justify-content-between align-items-end mb-3">
            <div className="d-flex align-items-center gap-3">
              <h4 className="mb-0">Search Results</h4>
              {selectedSearchRows.size > 0 && (
                <div className="d-flex align-items-center gap-2">
                  <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>{selectedSearchRows.size} selected</span>
                  <button className="btn btn-sm btn-outline-info" onClick={openBulkSeriesModal}>Link to Recurring Series</button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedSearchRows(new Set())}>Clear</button>
                </div>
              )}
            </div>
            <div className="text-end">
              <div style={{ fontSize: '1.1rem', fontWeight: '500', color: '#e5e7eb' }}>
                Total Sum: <span style={{ color: '#3b82f6' }}>{formatCurrency(searchResults.reduce((sum, t) => sum + (t.amount ? Number(t.amount) : 0), 0))}</span>
              </div>
            </div>
          </div>
          <div className="table-responsive" style={{ background: '#0b1220', borderRadius: '8px', border: '1px solid #374151', padding: '16px' }}>
            <table className="table table-dark table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ textAlign:'center', padding:'8px 4px', width:'44px', boxSizing:'border-box' }}>
                    <input type="checkbox" className="form-check-input" style={{ margin:'0 auto', display:'block', cursor:'pointer' }}
                      checked={searchResults.length > 0 && selectedSearchRows.size === searchResults.length}
                      onChange={e => {
                        if (e.target.checked) setSelectedSearchRows(new Set(searchResults.map(r => `${r.sourceType}-${r.id}`)));
                        else setSelectedSearchRows(new Set());
                      }}
                    />
                  </th>
                  <th style={{ textAlign:'left', padding:'8px', minWidth:'140px' }}>Date</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Description</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>User Description</th>
                  <th style={{ textAlign:'left', padding:'8px', minWidth:'220px' }}>Category</th>
                  <th style={{ textAlign:'right', padding:'8px' }}>Amount</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Source</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Asset Link</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Linked To-Do</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Recurring Series</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Debt Link</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Receipt</th>
                  <th style={{ textAlign:'left', padding:'8px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {searchDisplayRows.map((r, idx) => (
                  <tr key={`${r.sourceType}-${r.id}-${idx}`} style={{ borderBottom: '1px solid #374151', background: r._isSearchChild ? '#0d1a2e' : undefined }}>
                    <td style={{ padding:'8px 4px', textAlign:'center', verticalAlign:'middle', width:'44px', boxSizing:'border-box' }}>
                      <input type="checkbox" className="form-check-input" style={{ margin:'0 auto', display:'block', cursor:'pointer', flexShrink:0 }}
                        checked={selectedSearchRows.has(`${r.sourceType}-${r.id}`)}
                        onChange={e => {
                          const key = `${r.sourceType}-${r.id}`;
                          setSelectedSearchRows(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(key); else next.delete(key);
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td style={{ padding:'8px' }}>{r._hasSearchChildren ? <button onClick={()=>setExpandedSearchParents(prev=>{const n=new Set(prev);const k=r.sourceType+'-'+r.id;n.has(k)?n.delete(k):n.add(k);return n;})} style={{background:'none',border:'none',color:'#9ca3af',cursor:'pointer',padding:'0 4px 0 0'}}>{expandedSearchParents.has(r.sourceType+'-'+r.id)?'▼':'▶'}</button>:null}{renderEditableCell(r, 'transaction_date', r.date || r.transaction_date)}</td>
                    <td style={{ padding:'8px', paddingLeft: r._isSearchChild ? '28px' : '8px' }}>{r._isSearchChild && <span style={{color:'#6b7280',marginRight:'4px'}}>↳</span>}{renderEditableCell(r, 'description', r.description)}</td>
                    <td style={{ padding:'8px' }}>{renderEditableCell(r, 'user_description', r.user_description || '')}</td>
                    <td style={{ padding:'8px' }}>{renderCategoryCell(r)}</td>
                    <td style={{ padding:'8px', textAlign:'right' }}>{renderEditableCell(r, 'amount', formatCurrency(r.amount))}</td>
                    <td style={{ padding:'8px' }}>{renderSourceCell(r)}</td>
                    <td style={{ padding:'8px' }}>
                      <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                        {(() => {
                          if (!r.linkedAssetId) {
                            const cat = String(r.category || r.expense_category || '');
                            const domain = getDomainForCategory(cat);
                            const isYardWaste = cat.includes('yard_waste');
                            const isWaste = domain === 'waste_management' || ((cat.startsWith('waste_') || cat.includes('sanitation')) && !isYardWaste);
                            const isLinkable = domain !== 'generic' || isWaste || isYardWaste;
                            if (!isLinkable) return null;
                            let labelBtn = 'Link Asset';
                            if (domain === 'waste_management' || isWaste) labelBtn = 'Link Waste';
                            else if (domain === 'utilities') labelBtn = 'Link Utilities';
                            else if (domain === 'home') labelBtn = 'Link Home';
                            else if (domain === 'landscaping' || isYardWaste) labelBtn = 'Link Landscaping';
                            else if (domain === 'vehicle') labelBtn = 'Link Vehicle';
                            return (
                              <button className="btn btn-link btn-sm" style={{ color:'#93c5fd', padding:0 }} onClick={(e) => { e.preventDefault(); openUtilitiesModal(r); }}>
                                {labelBtn}
                              </button>
                            );
                          }
                          const asset = assetMaps.byId.get(String(r.linkedAssetId));
                          const url = buildAssetUrl(asset);
                          const cat = String(r.category || r.expense_category || '');
                          const domain = getDomainForCategory(cat);
                          const label = buildAssetLabel(asset, { ...assetMaps, transactionDomain: domain });
                          return (
                            <>
                              {url ? <Link to={url} title={label} style={{ color:'#93c5fd', whiteSpace:'nowrap', background:'transparent', border:'none' }}>{label}</Link> : <span title={label} style={{ color:'#93c5fd' }}>{label}</span>}
                              <button className="btn btn-link btn-sm" style={{ color:'#93c5fd', padding:0, marginLeft:8 }} onClick={(e) => { e.preventDefault(); openUtilitiesModal(r); }}>Edit Link</button>
                            </>
                          );
                        })()}
                      </span>
                    </td>
                    <td style={{ padding:'8px' }}>
                      <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                        {(() => {
                          const todoInfo = r.linked_todo_info;
                          if (!todoInfo) return null;
                          let url = null;
                          if (todoInfo.asset_type === 'vehicle') url = `/balance_sheet/assets/physical_assets/vehicles/${todoInfo.asset_id}?tab=todo`;
                          else if (todoInfo.property_id) url = `/balance_sheet/assets/physical_assets/residential/${todoInfo.property_id}?tab=todo`;
                          const statusColor = todoInfo.status === 'completed' ? '#22c55e' : (todoInfo.status === 'in_progress' ? '#3b82f6' : '#9ca3af');
                          const content = <span style={{ color: '#93c5fd', cursor: 'pointer', whiteSpace: 'nowrap' }} title={`To-Do: ${todoInfo.title}\nStatus: ${todoInfo.status_display}`}>{todoInfo.title}</span>;
                          return (
                            <>
                              {url ? <Link to={url}>{content}</Link> : content}
                              <span style={{ fontSize: '10px', color: statusColor, border: `1px solid ${statusColor}`, borderRadius: '4px', padding: '0 4px', marginLeft: '4px' }}>{todoInfo.status_display}</span>
                            </>
                          );
                        })()}
                      </span>
                    </td>
                    <td style={{ padding:'8px' }}>
                      <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                        {(() => {
                          const expInfo = r.linked_expense_info;
                          const incInfo = r.linked_income_info;
                          if (r.standalone_recurring_series_id || r.standalone_recurring_series_name) {
                            const sid = r.standalone_recurring_series_id;
                            const sname = r.standalone_recurring_series_name || 'Recurring';
                            const url = `/income_statement/expenses?view=recurring&tab=series&series_id=${sid}&series_type=standalone`;
                            return (
                              <>
                                <a href={url} target="_blank" rel="noreferrer" style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{sname}</a>
                                <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, marginLeft:8, fontSize:'0.75rem' }} title="Edit Series Link" onClick={(e) => { e.preventDefault(); openSingleSeriesModal(r); }}>✏️</button>
                              </>
                            );
                          } else if (expInfo && expInfo.recurring_name) {
                            const url = r.linked_recurring_series_id ? `/income_statement/expenses?view=recurring&tab=series&series_id=${r.linked_recurring_series_id}&series_type=asset` : null;
                            return (
                              <>
                                {url ? <a href={url} target="_blank" rel="noreferrer" style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{expInfo.recurring_name}</a> : <span style={{ color:'#e5e7eb' }}>{expInfo.recurring_name}</span>}
                                <button className="btn btn-link btn-sm" style={{ color:'#93c5fd', padding:0, marginLeft:8 }} onClick={(e) => { e.preventDefault(); openUtilitiesModal(r); }}>Edit Link</button>
                              </>
                            );
                          } else if (incInfo && incInfo.display_name) {
                            const url = `/income?series_id=${r.linked_income_stream_id}`;
                            return (
                              <>
                                <Link to={url} style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{incInfo.display_name}</Link>
                                <button className="btn btn-link btn-sm" style={{ color:'#93c5fd', padding:0, marginLeft:8 }} onClick={(e) => { e.preventDefault(); setIncomeLinkRow(r); setShowIncomeLinkModal(true); }}>Edit Link</button>
                              </>
                            );
                          } else if (r.sourceType === 'cash' || r.sourceType === 'revolver') {
                            return (
                              <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.75rem' }} title="Link Recurring Series" onClick={(e) => { e.preventDefault(); openSingleSeriesModal(r); }}>🔗 Link Series</button>
                            );
                          }
                          return null;
                        })()}
                      </span>
                    </td>
                    <td style={{ padding:'8px' }}>
                      <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                        {r.sourceType === 'cash' && r.linkedDebt && r.linkedDebtId ? (
                          <>
                            {(() => {
                              let url = null;
                              if (r.linkedDebt.type === 'auto_loan') url = `/balance_sheet/financing/auto_loans/${r.linkedDebtId}`;
                              else if (r.linkedDebt.type === 'mortgage') url = `/balance_sheet/financing/mortgages/${r.linkedDebtId}`;
                              else if (r.linkedDebt.type === 'credit_card' || r.linkedDebt.type === 'revolver') url = `/balance_sheet/financing/revolvers/credit-cards/${r.linkedDebtId}`;
                              else if (r.linkedDebt.type === 'heloc') url = `/balance_sheet/financing/revolvers/helocs/${r.linkedDebtId}`;
                              else if (r.linkedDebt.type === '401k_loan') url = `/balance_sheet/financing/401k_loans/${r.linkedDebtId}`;
                              return url ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ color:'#93c5fd', whiteSpace:'nowrap', background:'transparent', border:'none' }} title={r.linkedDebt.display_text || ''}>{r.linkedDebt.debt_label || r.linkedDebt.debt_name || '🔗 Debt'}</a> : <span style={{ color:'#93c5fd', whiteSpace:'nowrap' }} title={r.linkedDebt.display_text || ''}>{r.linkedDebt.debt_label || r.linkedDebt.debt_name || '🔗 Debt'}</span>;
                            })()}
                            <button className="btn btn-link btn-sm" style={{ color:'#93c5fd', padding:0, marginLeft:8 }} onClick={(e) => { e.preventDefault(); if (String(r.category || r.expense_category || '') === 'debt_disbursement') { setDebtDisbursementRow(r); setShowDebtDisbursementModal(true); } else { openDebtModalForRow(r); } }}>Edit Link</button>
                          </>
                        ) : (
                          r.sourceType === 'cash' && (String(r.category || r.expense_category || '') === 'payments' || String(r.category || r.expense_category || '') === 'debt_disbursement') && (
                            <button className="btn btn-link btn-sm" style={{ color:'#93c5fd', padding:0 }} onClick={(e) => { e.preventDefault(); if (String(r.category || r.expense_category || '') === 'debt_disbursement') { setDebtDisbursementRow(r); setShowDebtDisbursementModal(true); } else { openDebtModalForRow(r); } }}>Link Debt</button>
                          )
                        )}
                      </span>
                    </td>
                    <td style={{ padding:'8px' }}>
                      {r.receipt || r.receipt_url ? (
                        <button className="btn btn-sm btn-outline-primary" onClick={() => r.sourceType === 'revolver' ? openCcReceipt(r) : openCashReceipt(r)}>View</button>
                      ) : (
                        <label className="btn btn-sm btn-outline-secondary mb-0">Upload<input type="file" accept="image/*,application/pdf" style={{ display:'none' }} onChange={(e) => { const file = e.target.files && e.target.files[0]; if (file) { if (r.sourceType === 'revolver') uploadCcReceipt(r.id, file); else uploadCashReceipt(r.id, file); } e.target.value = ''; }} /></label>
                      )}
                    </td>
                    <td style={{ padding:'8px' }}>
                      <div className="btn-group btn-group-sm" role="group" aria-label="Actions">
                        <button className="btn btn-outline-primary btn-sm" onClick={() => { const absAmt = Math.abs(Number(r.amount) || 0); const initial = absAmt > 0 ? [ { key: 1, amount: Math.round((absAmt/2) * 100)/100, description: r.description }, { key: 2, amount: Math.round((absAmt - Math.round((absAmt/2) * 100)/100) * 100)/100, description: r.description }, ] : [{ key: 1, amount: 0, description: r.description }, { key: 2, amount: 0, description: r.description }]; setSplitRow(r); setSplitItems(initial); setSplitError(''); setShowSplitModal(true); }}>Split</button>
                        <button className="btn btn-danger btn-sm" onClick={() => deleteTransaction(r)} disabled={deleting.has(`${r.sourceType}-${r.id}`)} style={{ fontSize: '0.75rem', padding: '2px 8px' }}>{deleting.has(`${r.sourceType}-${r.id}`) ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Deleting...</> : 'Delete'}</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!searchLoading && searchResults.length === 0) && (
                  <tr>
                    <td colSpan="13" className="text-center text-muted">No results</td>
                  </tr>
                )}
                {searchLoading && (
                  <tr>
                    <td colSpan="13" className="text-center text-muted">Loading...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bulk Link to Recurring Series Modal */}
      {showBulkSeriesModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2200 }} onClick={() => { if (!bulkSeriesSaving) { setShowBulkSeriesModal(false); setBulkConflictCategories([]); } }}>
          <div style={{ width:'min(540px,95vw)', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
              <div style={{ fontSize:17, fontWeight:700 }}>
                {bulkConflictCategories.length > 0 ? 'Resolve Category Conflict' : 'Link to Recurring Series'}
              </div>
              <button className="btn btn-sm btn-secondary" onClick={() => { setShowBulkSeriesModal(false); setBulkConflictCategories([]); }} disabled={bulkSeriesSaving} style={{ marginLeft:'auto' }}>✕</button>
            </div>
            <div style={{ padding:16 }}>
              {bulkSeriesError && <div className="alert alert-danger py-2" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{bulkSeriesError}</div>}

              {bulkConflictCategories.length > 0 ? (
                /* ── Conflict resolution step ── */
                <div>
                  <div style={{ marginBottom:12, color:'#fbbf24', fontSize:'0.9rem' }}>
                    ⚠️ The selected transactions span multiple expense categories. A series must have a single category.
                    Choose which category to apply to <strong>all</strong> transactions in this series (existing + new).
                  </div>
                  <label className="form-label">Select Category to Apply</label>
                  <select className="form-select form-select-sm" style={{ background:'#1f2937', color:'#e5e7eb', border:'1px solid #374151' }}
                    value={bulkResolvedCategory} onChange={e => setBulkResolvedCategory(e.target.value)}>
                    {bulkConflictCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <div style={{ marginTop:8, color:'#9ca3af', fontSize:'0.8rem' }}>
                    All transactions already linked to this series will also have their category updated.
                  </div>
                  <div className="d-flex justify-content-end gap-2" style={{ marginTop:16 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setShowBulkSeriesModal(false); setBulkConflictCategories([]); }} disabled={bulkSeriesSaving}>Cancel</button>
                    <button className="btn btn-warning btn-sm" onClick={handleBulkConflictConfirm} disabled={bulkSeriesSaving}>
                      {bulkSeriesSaving ? 'Saving…' : 'Confirm & Apply Category'}
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Normal series selection step ── */
                <div>
                  <div style={{ marginBottom:12, color:'#9ca3af', fontSize:'0.85rem' }}>
                    Linking <span style={{ color:'#e5e7eb', fontWeight:600 }}>{selectedSearchRows.size}</span> selected transaction{selectedSearchRows.size !== 1 ? 's' : ''} to a recurring series.
                  </div>
                  <div className="btn-group mb-3" style={{ width:'100%' }}>
                    <button className={`btn btn-sm ${bulkSeriesMode === 'existing' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setBulkSeriesMode('existing')}>Existing</button>
                    <button className={`btn btn-sm ${bulkSeriesMode === 'new' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setBulkSeriesMode('new')}>New</button>
                  </div>
                  {bulkSeriesMode === 'existing' ? (
                    <div>
                      <label className="form-label">Select Series</label>
                      {bulkSeriesLoadingOptions ? (
                        <div style={{ color:'#9ca3af' }}>Loading…</div>
                      ) : (
                        <>
                          <input
                            type="text"
                            placeholder="Search series…"
                            value={bulkSeriesSearch}
                            onChange={e => { setBulkSeriesSearch(e.target.value); setBulkSelectedSeriesId(''); }}
                            className="form-control form-control-sm mb-2"
                            style={{ background:'#1f2937', color:'#e5e7eb', border:'1px solid #374151' }}
                          />
                          <select className="form-select form-select-sm" style={{ background:'#1f2937', color:'#e5e7eb', border:'1px solid #374151' }}
                            value={bulkSelectedSeriesId} onChange={e => setBulkSelectedSeriesId(e.target.value)}>
                            <option value="">— Select a series —</option>
                            {(() => {
                              const q = bulkSeriesSearch.trim().toLowerCase();
                              const filtered = q ? bulkSeriesOptions.filter(s => {
                                const name = (s.display_name || '').toLowerCase();
                                const asset = (s.asset_description || '').toLowerCase();
                                return name.includes(q) || asset.includes(q);
                              }) : bulkSeriesOptions;
                              const assetOpts = filtered.filter(s => s.series_type === 'asset');
                              const standaloneOpts = filtered.filter(s => s.series_type === 'standalone');
                              return (
                                <>
                                  {assetOpts.length > 0 && (
                                    <optgroup label="── Asset-Linked ──">
                                      {assetOpts.map(s => (
                                        <option key={`asset__${s.id}`} value={`asset__${s.id}`}>
                                          {s.asset_description ? `${s.asset_description} - ${s.display_name}` : s.display_name} ({s.recurring_type} / {s.recurring_frequency}){!s.active ? ' [inactive]' : ''}
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}
                                  {standaloneOpts.length > 0 && (
                                    <optgroup label="── Non-Asset Linked ──">
                                      {standaloneOpts.map(s => (
                                        <option key={`standalone__${s.id}`} value={`standalone__${s.id}`}>
                                          {s.display_name} ({s.recurring_type} / {s.recurring_frequency}){!s.active ? ' [inactive]' : ''}
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}
                                  {filtered.length === 0 && <option disabled>No series match your search</option>}
                                </>
                              );
                            })()}
                          </select>
                        </>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div className="mb-3">
                        <label className="form-label">Series Type</label>
                        <div className="btn-group" style={{ width:'100%' }}>
                          <button className={`btn btn-sm ${!bulkNewIsAssetLinked ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setBulkNewIsAssetLinked(false)}>Standalone</button>
                          <button className={`btn btn-sm ${bulkNewIsAssetLinked ? 'btn-warning' : 'btn-outline-secondary'}`} onClick={() => { setBulkNewIsAssetLinked(true); setBulkNewCategory(''); }}>Asset-Linked</button>
                        </div>
                      </div>
                      {bulkNewIsAssetLinked && (
                        <div className="mb-2">
                          <label className="form-label">Link to Asset</label>
                          <select className="form-select form-select-sm" style={{ background:'#1f2937', color:'#e5e7eb', border:'1px solid #374151' }}
                            value={bulkNewAssetId} onChange={e => { setBulkNewAssetId(e.target.value); setBulkNewCategory(''); }}>
                            <option value="">— Select an asset —</option>
                            {(() => {
                              const allAssets = bulkPhysicalAssets;
                              const properties = allAssets.filter(a => a.asset_type_2 === 'resi_property' && a.description);
                              const children = allAssets.filter(a => a.asset_type_2 === 'structure' || a.asset_type_2 === 'land');
                              const assets = allAssets.filter(a => a.description);
                              const vehicles = assets.filter(a => a.asset_type_2 === 'vehicle');
                              // Standalone land/structure assets with no parent property
                              const orphans = children.filter(c => !c.residential_property || !c.residential_property.id);
                              const rows = [];
                              properties.forEach(prop => {
                                const kids = children.filter(c => c.residential_property && c.residential_property.id === prop.id);
                                if (kids.length > 0) {
                                  rows.push(<option key={`prop-hdr-${prop.id}`} value="" disabled style={{ color:'#9ca3af', fontStyle:'italic' }}>── {prop.description} ──</option>);
                                  kids.forEach(kid => {
                                    rows.push(<option key={kid.id} value={kid.id}>&nbsp;&nbsp;&nbsp;{kid.asset_type_1}</option>);
                                  });
                                } else {
                                  rows.push(<option key={prop.id} value={prop.id}>{prop.description}</option>);
                                }
                              });
                              // Orphan land/structure assets (no parent property) shown directly
                              orphans.forEach(o => {
                                rows.push(<option key={o.id} value={o.id}>{o.description || o.asset_type_1}</option>);
                              });
                              if (vehicles.length > 0) {
                                rows.push(<option key="veh-hdr" value="" disabled style={{ color:'#9ca3af', fontStyle:'italic' }}>── Vehicles ──</option>);
                                vehicles.forEach(v => rows.push(<option key={v.id} value={v.id}>{v.description}</option>));
                              }
                              return rows;
                            })()}
                          </select>
                        </div>
                      )}
                      {bulkNewIsAssetLinked && bulkNewAssetId && (() => {
                        const selAsset = bulkPhysicalAssets.find(a => String(a.id) === String(bulkNewAssetId));
                        const atype = selAsset ? selAsset.asset_type_2 : null;
                        let catOptions = [];
                        if (atype === 'vehicle') {
                          catOptions = [
                            { value: 'vehicle_maintenance', label: 'Maintenance' },
                            { value: 'vehicle_repair', label: 'Repair' },
                            { value: 'vehicle_enhancement', label: 'Enhancement' },
                            { value: 'licensing_tags', label: 'Licensing/Tags' },
                            { value: 'insurance', label: 'Insurance Payment' },
                            { value: 'taxes', label: 'Tax Payment' },
                            { value: 'vehicle_other', label: 'Other' },
                          ];
                        } else if (atype === 'structure') {
                          catOptions = [
                            { value: 'maintenance', label: 'Asset Maintenance' },
                            { value: 'repair', label: 'Asset Repair' },
                            { value: 'enhancement', label: 'Asset Enhancement' },
                            { value: 'cleaning', label: 'Cleaning' },
                            { value: 'pest_control', label: 'Pest Control' },
                            { value: 'home_decor', label: 'Home Decor' },
                            { value: 'home_furniture', label: 'Home Furniture' },
                            { value: 'home_artwork', label: 'Home Artwork' },
                            { value: 'home_inspection', label: 'Home Inspection' },
                            { value: 'plants', label: 'Plants' },
                            { value: 'landscaping', label: 'Landscaping' },
                            { value: 'insurance', label: 'Insurance Payment' },
                            { value: 'taxes', label: 'Tax Payment' },
                            { value: 'utilities', label: 'Utilities' },
                            { value: 'utilities_electric_gas', label: 'Electric & Gas' },
                            { value: 'utilities_water', label: 'Water' },
                            { value: 'utilities_trash', label: 'Trash/Waste' },
                            { value: 'utilities_sewer', label: 'Sewer' },
                            { value: 'utilities_internet', label: 'Internet (Utility)' },
                            { value: 'home_other', label: 'Home Other' },
                          ];
                        } else if (atype === 'land') {
                          catOptions = [
                            { value: 'landscaping', label: 'Landscaping' },
                            { value: 'taxes', label: 'Tax Payment' },
                          ];
                        }
                        if (catOptions.length === 0) return null;
                        return (
                          <div className="mb-2">
                            <label className="form-label">Expense Category <span style={{ color:'#f87171' }}>*</span></label>
                            <select className="form-select form-select-sm" style={{ background:'#1f2937', color:'#e5e7eb', border:'1px solid #374151' }}
                              value={bulkNewCategory} onChange={e => setBulkNewCategory(e.target.value)}>
                              <option value="">— Select a category —</option>
                              {catOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>
                        );
                      })()}
                      <div className="mb-2">
                        <label className="form-label">Series Name</label>
                        <input className="form-control form-control-sm" style={{ background:'#1f2937', color:'#e5e7eb', border:'1px solid #374151' }}
                          value={bulkNewName} onChange={e => setBulkNewName(e.target.value)} placeholder="e.g. Netflix" />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Type</label>
                        <select className="form-select form-select-sm" style={{ background:'#1f2937', color:'#e5e7eb', border:'1px solid #374151' }}
                          value={bulkNewType} onChange={e => setBulkNewType(e.target.value)}>
                          <option value="fixed">Fixed</option>
                          <option value="variable">Variable</option>
                        </select>
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Frequency</label>
                        <select className="form-select form-select-sm" style={{ background:'#1f2937', color:'#e5e7eb', border:'1px solid #374151' }}
                          value={bulkNewFrequency} onChange={e => setBulkNewFrequency(e.target.value)}>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="annually">Annually</option>
                          <option value="weekly">Weekly</option>
                          <option value="biweekly">Bi-Weekly</option>
                        </select>
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Vendor/Payee <span style={{ color:'#6b7280' }}>(optional)</span></label>
                        <input className="form-control form-control-sm" style={{ background:'#1f2937', color:'#e5e7eb', border:'1px solid #374151' }}
                          value={bulkNewVendor} onChange={e => setBulkNewVendor(e.target.value)} placeholder="e.g. Netflix Inc." />
                      </div>
                      {!bulkNewIsAssetLinked && (
                        <div className="mb-2">
                          <label className="form-label">Series User Description <span style={{ color:'#6b7280' }}>(optional — overwrites transaction descriptions)</span></label>
                          <input className="form-control form-control-sm" style={{ background:'#1f2937', color:'#e5e7eb', border:'1px solid #374151' }}
                            value={bulkNewUserDescription} onChange={e => setBulkNewUserDescription(e.target.value)} placeholder="e.g. Monthly Netflix subscription" />
                        </div>
                      )}
                    </div>
                  )}
                  <div className="d-flex justify-content-end gap-2" style={{ marginTop:16 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowBulkSeriesModal(false)} disabled={bulkSeriesSaving}>Cancel</button>
                    <button className="btn btn-success btn-sm" onClick={handleBulkSeriesSave} disabled={bulkSeriesSaving}>
                      {bulkSeriesSaving ? 'Saving…' : `Link ${selectedSearchRows.size} Transaction${selectedSearchRows.size !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showUtilityModal && utilityRow && (
        <AssetExpenseLinkModal
          visible={showUtilityModal}
          onClose={()=> { setShowUtilityModal(false); setUtilityRow(null); }}
          sourceType={utilityRow.sourceType === 'revolver' ? 'revolver' : 'cash'}
          transaction={{
            id: utilityRow.id,
            sourceType: utilityRow.sourceType,
            description: utilityRow.description,
            amount: utilityRow.amount,
            transaction_date: utilityRow.transaction_date || utilityRow.date || '',
            post_date: utilityRow.post_date || '',
            date: utilityRow.transaction_date || utilityRow.post_date || utilityRow.date || '',
            linked_asset_id: utilityRow.linked_asset_id || '',
            linked_asset_expense_id: (utilityRow.linked_asset_expense_id != null && utilityRow.linked_asset_expense_id !== '') ? String(utilityRow.linked_asset_expense_id) : '',
            linked_utility_bill_id: utilityRow.linked_utility_bill_id || '',
            linked_recurring_series_id: utilityRow.linked_recurring_series_id || '',
            linked_property_id: utilityRow.linked_property_id || '',
            expense_category: utilityRow.expense_category || '',
          }}
          expenseCategories={categoryChoices}
          domain={getDomainForCategory(utilityRow.expense_category)}
          mediaConfig={{
            categories: [
              { value: 'media_internet', label: 'Internet' },
              { value: 'media_cable_tv', label: 'Cable/TV' },
              { value: 'media_phone', label: 'Phone' },
              { value: 'media_streaming', label: 'Streaming Services' },
              { value: 'media_books', label: 'Books' },
              { value: 'media_other', label: 'Other Media' },
            ],
          }}
          utilitiesConfig={{
            propertyId: null,
            categories: [
              { value: 'utilities_electric_gas', label: 'Electric & Gas' },
              { value: 'utilities_water', label: 'Water' },
              { value: 'utilities_trash', label: 'Trash/Waste' },
              { value: 'utilities_sewer', label: 'Sewer' },
              { value: 'utilities_internet', label: 'Internet' },
              { value: 'utilities_cable_tv', label: 'Cable/TV' },
              { value: 'utilities_phone', label: 'Phone' },
            ],
          }}
          initialValues={{
            asset_id: utilityRow.linked_asset_id || utilityRow.linkedAssetId || '',
            expense_category: utilityRow.expense_category || utilityRow.category || '',
            // Pass through the linked asset expense id so the modal triggers the detail fetch
            linked_asset_expense_id: (utilityRow.linked_asset_expense_id != null && utilityRow.linked_asset_expense_id !== '') ? String(utilityRow.linked_asset_expense_id) : (utilityRow.linkedAssetExpenseId != null && utilityRow.linkedAssetExpenseId !== '' ? String(utilityRow.linkedAssetExpenseId) : ''),
            // Derive property id from the linked asset using the same approach as CashAccountDetail via assetMaps
            property_id: (()=>{ try {
              const aid = utilityRow.linked_asset_id || utilityRow.linkedAssetId;
              if (!aid) return utilityRow.linked_property_id || utilityRow.linkedPropertyId || '';
              const asset = assetMaps?.byId?.get ? assetMaps.byId.get(String(aid)) : null;
              if (!asset) return utilityRow.linked_property_id || utilityRow.linkedPropertyId || '';
              const t2 = String(asset?.asset_type_2 || asset?.type_2 || asset?.type2 || '').toLowerCase();
              if (t2 === 'resi_property') return String(asset.id);
              if (t2 === 'structure' || t2 === 'land') {
                const rp = asset.residential_property || asset.residentialProperty || asset.parent_property || asset.parent || null;
                const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
                return rpId ? String(rpId) : (utilityRow.linked_property_id || utilityRow.linkedPropertyId || '');
              }
              return utilityRow.linked_property_id || utilityRow.linkedPropertyId || '';
            } catch(e){ return utilityRow.linked_property_id || utilityRow.linkedPropertyId || ''; } })(),
            recurring_series_id: utilityRow.linked_recurring_series_id || utilityRow.linkedRecurringSeriesId || '',
            utility_bill_id: utilityRow.linked_utility_bill_id || utilityRow.linkedUtilityBillId || '',
          }}
          onSaved={async (payload)=>{
            const ae = payload || {};
            // Update local transaction state with the latest linkage info
            const updateFn = (prev) => prev.map(t => {
              if (t.id === utilityRow.id && t.sourceType === (utilityRow.sourceType === 'revolver' ? 'revolver' : 'cash')) {
                return { 
                  ...t, 
                  linked_asset_id: ae.asset_id || t.linked_asset_id || t.linkedAssetId,
                  linked_asset_expense_id: ae.asset_expense_id || t.linked_asset_expense_id || t.linkedAssetExpenseId,
                  linked_recurring_series_id: ae.recurring_series_id || t.linked_recurring_series_id || t.linkedRecurringSeriesId,
                  linked_utility_bill_id: ae.utility_bill_id || t.linked_utility_bill_id || t.linkedUtilityBillId,
                  // Also update camelCase versions if they exist
                  linkedAssetId: ae.asset_id || t.linked_asset_id || t.linkedAssetId,
                  linkedAssetExpenseId: ae.asset_expense_id || t.linked_asset_expense_id || t.linkedAssetExpenseId,
                  linkedRecurringSeriesId: ae.recurring_series_id || t.linked_recurring_series_id || t.linkedRecurringSeriesId,
                  linkedUtilityBillId: ae.utility_bill_id || t.linked_utility_bill_id || t.linkedUtilityBillId,
                };
              }
              return t;
            });
            setCcTxns(updateFn);
            setCashTxns(updateFn);
            setSearchResults(updateFn);

            // refresh data to ensure everything is in sync with server, respecting active date range
            const startDate = dateRange[0];
            const endDate = dateRange[1];
            const start = `${startDate.getFullYear()}-${String(startDate.getMonth()+1).padStart(2,'0')}-01`;
            const end = `${endDate.getFullYear()}-${String(endDate.getMonth()+1).padStart(2,'0')}-${String(endDate.getDate()).padStart(2,'0')}`;
            const [rvRes, caRes] = await Promise.allSettled([
              axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: start, end_date: end }  }),
              axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: start, end_date: end }  }),
            ]);
            if (rvRes.status === 'fulfilled') {
              const data = rvRes.value?.data;
              const list = Array.isArray(data) ? data : (data?.transactions || []);
              const rv = list.filter(t => {
                const type = (t.revolver_type || '').toLowerCase();
                return type === 'credit_card' || type === 'heloc';
              });
              setCcTxns(rv);
            }
            if (caRes.status === 'fulfilled') {
              const data = caRes.value?.data;
              const list = Array.isArray(data) ? data : (data?.transactions || []);
              setCashTxns(list);
            }
            if (pageView === 'timeseries') { refreshTsData(); }
            setShowUtilityModal(false);
            setUtilityRow(null);
          }}
          onUnlinked={async ()=>{
            // Update local transaction state with cleared linkage info
            const updateFn = (prev) => prev.map(t => {
              if (t.id === utilityRow.id && t.sourceType === (utilityRow.sourceType === 'revolver' ? 'revolver' : 'cash')) {
                return {
                  ...t,
                  linked_asset_id: null,
                  linked_asset_expense_id: null,
                  linked_recurring_series_id: null,
                  linked_utility_bill_id: null,
                  linkedAssetId: null,
                  linkedAssetExpenseId: null,
                  linkedRecurringSeriesId: null,
                  linkedUtilityBillId: null,
                };
              }
              return t;
            });
            setCcTxns(updateFn);
            setCashTxns(updateFn);
            setSearchResults(updateFn);

            // refresh data to ensure everything is in sync with server, respecting active date range
            const startDate = dateRange[0];
            const endDate = dateRange[1];
            const start = `${startDate.getFullYear()}-${String(startDate.getMonth()+1).padStart(2,'0')}-01`;
            const end = `${endDate.getFullYear()}-${String(endDate.getMonth()+1).padStart(2,'0')}-${String(endDate.getDate()).padStart(2,'0')}`;
            const [rvRes, caRes] = await Promise.allSettled([
              axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: start, end_date: end }  }),
              axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: start, end_date: end }  }),
            ]);
            if (rvRes.status === 'fulfilled') {
              const data = rvRes.value?.data;
              const list = Array.isArray(data) ? data : (data?.transactions || []);
              const rv = list.filter(t => {
                const type = (t.revolver_type || '').toLowerCase();
                return type === 'credit_card' || type === 'heloc';
              });
              setCcTxns(rv);
            }
            if (caRes.status === 'fulfilled') {
              const data = caRes.value?.data;
              const list = Array.isArray(data) ? data : (data?.transactions || []);
              setCashTxns(list);
            }

            if (pageView === 'timeseries') { refreshTsData(); }
            setShowUtilityModal(false);
            setUtilityRow(null);
          }}
        />
      )}

      {showReimbursementModal && reimbursementRow && (
        <ReimbursementLinkModal
          isOpen={showReimbursementModal}
          transaction={reimbursementRow}
          onClose={() => { setShowReimbursementModal(false); setReimbursementRow(null); }}
          onSaveSuccess={async () => {
            setShowReimbursementModal(false);
            setReimbursementRow(null);
            if (pageView === 'timeseries') { refreshTsData(); } else { fetchAll(); }
          }}
          formatCurrency={formatCurrency}
        />
      )}

      {showIncomeLinkModal && incomeLinkRow && (
        <IncomeLinkModal
          row={incomeLinkRow}
          onClose={() => { setShowIncomeLinkModal(false); setIncomeLinkRow(null); }}
          onLinked={() => {
            if (pageView === 'timeseries') { refreshTsData(); } else { fetchAll(); }
            if (pageView === 'search') handleSearch();
          }}
        />
      )}
      {showDebtDisbursementModal && debtDisbursementRow && (
        <DebtDisbursementLinkModal
          row={debtDisbursementRow}
          onClose={() => { setShowDebtDisbursementModal(false); setDebtDisbursementRow(null); }}
          onLinked={() => {
            if (pageView === 'timeseries') { refreshTsData(); } else { fetchAll(); }
            if (pageView === 'search') handleSearch();
          }}
        />
      )}

      {showIncomeSplitModal && incomeSplitRow && (
        <IncomeSplitModal
          row={incomeSplitRow}
          defaultKind={incomeSplitDefaultKind}
          onClose={()=> { setShowIncomeSplitModal(false); setIncomeSplitRow(null); }}
          onCompleted={async ()=> {
            // refresh month data
            const [yy, mm] = month.split('-').map(Number);
            const endDay = new Date(yy, mm, 0).getDate();
            const start = `${yy}-${String(mm).padStart(2,'0')}-01`;
            const end = `${yy}-${String(mm).padStart(2,'0')}-${String(endDay).padStart(2,'0')}`;
            const [rvRes, caRes] = await Promise.allSettled([
              axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: start, end_date: end }  }),
              axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: start, end_date: end }  }),
            ]);
            if (rvRes.status === 'fulfilled') {
              const data = rvRes.value?.data;
              const list = Array.isArray(data) ? data : (data?.transactions || []);
              const rv = list.filter(t => {
                const type = (t.revolver_type || '').toLowerCase();
                return type === 'credit_card' || type === 'heloc';
              });
              setCcTxns(rv);
            }
            if (caRes.status === 'fulfilled') {
              const data = caRes.value?.data;
              const list = Array.isArray(data) ? data : (data?.transactions || []);
              setCashTxns(list);
            }
            if (pageView === 'timeseries') { refreshTsData(); }
            if (pageView === 'search') { handleSearch(); }
          }}
        />
      )}

      <ReceiptModal
        isOpen={showCcReceiptModal}
        onClose={closeCcReceipt}
        url={ccReceiptPreviewUrl}
        title={ccReceiptModalTitle}
        onDelete={deleteCcReceipt}
      />
      <ReceiptModal
        isOpen={showCashReceiptModal}
        onClose={closeCashReceipt}
        url={cashReceiptPreviewUrl}
        title={cashReceiptModalTitle}
        onDelete={deleteCashReceipt}
      />

      {showHomeModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ width: '720px', maxWidth: '95vw', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
              <div style={{ fontSize:18, fontWeight:700 }}>Link Home Expense</div>
              <button onClick={()=>{ setShowHomeModal(false); setHomeRow(null); }} style={{ marginLeft:'auto' }} className="btn btn-sm btn-secondary">✕</button>
            </div>
            <div style={{ padding:16 }}>
              {homeError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{homeError}</div>}
              <div className="mb-3">
                <label className="form-label">Residential Property</label>
                <select className="form-select" value={homeSelectedPropertyId} onChange={(e)=> onChangePropertyForHome(e.target.value)} disabled={homeLoading}>
                  <option value="">Select property…</option>
                  {resiProperties.map(p => (<option key={p.id} value={p.id}>{p.address?.street_address || p.label || p.address || p.id}</option>))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Structure</label>
                <select className="form-select" value={homeSelectedAssetId} onChange={(e)=> setHomeSelectedAssetId(e.target.value)} disabled={homeLoading || !homeSelectedPropertyId}>
                  <option value="">{homeSelectedPropertyId ? 'Select structure…' : 'Select property first'}</option>
                  {homeAssetOptions.map(opt => (<option key={opt.id} value={opt.id}>{opt.label}</option>))}
                </select>
                <small className="form-text text-muted">If no structures are available, please add a structure first.</small>
              </div>
              <div className="mb-3">
                <label className="form-check">
                  <input className="form-check-input" type="checkbox" checked={homeIsRecurring} onChange={(e)=> setHomeIsRecurring(e.target.checked)} />
                  <span className="form-check-label" style={{ marginLeft:8 }}>Recurring expense?</span>
                </label>
                {homeIsRecurring && (
                  <div style={{ marginTop:8 }}>
                    <div className="btn-group btn-group-sm" role="group" aria-label="Recurring mode">
                      <button type="button" className={`btn ${homeRecurringMode==='existing' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=>{ setHomeRecurringMode('existing'); if (homeRecurringChoice === '__new__') setHomeRecurringChoice(''); }}>
                        Existing
                      </button>
                      <button type="button" className={`btn ${homeRecurringMode==='new' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=>{ setHomeRecurringMode('new'); setHomeRecurringChoice('__new__'); }}>
                        New
                      </button>
                    </div>

                    {homeRecurringMode === 'existing' && (
                      <div style={{ marginTop:8 }}>
                        <label className="form-label">Existing recurring series</label>
                        <select
                          className="form-select"
                          value={homeRecurringChoice}
                          onChange={(e)=> setHomeRecurringChoice(e.target.value)}
                          disabled={!homeSelectedAssetId || homeRecurringLoading}
                          onFocus={async()=> { if (homeSelectedPropertyId && !homeSeriesOptions.length) {
                            try {
                              setHomeRecurringLoading(true);
                              const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${homeSelectedPropertyId}/recurring-series/`, {
                                params: { include_children: 1, include_inactive: 1 }
                               });
                              const items = Array.isArray(res.data) ? res.data.map(s => ({ id: String(s.id), name: s.name })) : [];
                              setHomeSeriesOptions(items);
                            } catch (e) { setHomeSeriesOptions([]); }
                            finally { setHomeRecurringLoading(false); }
                          } }}
                        >
                          <option value="">{homeRecurringLoading ? 'Loading…' : 'Select existing series…'}</option>
                          {homeSeriesOptions.map(opt => (
                            <option key={opt.id} value={String(opt.id)}>
                              {opt.name}
                            </option>
                          ))}
                        </select>
                        {!homeSeriesOptions.length && <small className="form-text text-muted">No existing recurring series found for this asset.</small>}
                      </div>
                    )}

                    {homeRecurringMode === 'new' && (
                      <div style={{ marginTop:8 }}>
                        <div className="mb-2">
                          <label className="form-label">New series name</label>
                          <input className="form-control" type="text" value={homeRecurringName} onChange={(e)=> setHomeRecurringName(e.target.value)} placeholder="e.g., Pest Control" />
                        </div>
                        <div className="mb-2">
                          <label className="form-label">Frequency</label>
                          <select className="form-select" value={homeFrequency} onChange={(e)=> setHomeFrequency(e.target.value)}>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="bi_monthly">Bi-monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="semi_annually">Semi-annually</option>
                            <option value="annually">Annually</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-secondary" onClick={()=> { setShowHomeModal(false); setHomeRow(null); }} disabled={homeLoading}>Cancel</button>
                <button className="btn btn-primary" onClick={submitHomeLink} disabled={homeLoading || !homeSelectedPropertyId || !homeSelectedAssetId || !homeUiCategory}>Link Expense</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showWasteModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ width: '720px', maxWidth: '95vw', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
              <div style={{ fontSize:18, fontWeight:700 }}>Link Waste Management Expense</div>
              <button onClick={()=>{ setShowWasteModal(false); setWasteRow(null); }} style={{ marginLeft:'auto' }} className="btn btn-sm btn-secondary">✕</button>
            </div>
            <div style={{ padding:16 }}>
              {wasteError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{wasteError}</div>}
              <div className="mb-3">
                <label className="form-label">Residential Property</label>
                <select className="form-select" value={wasteSelectedPropertyId} onChange={(e)=> onChangePropertyForWaste(e.target.value)} disabled={wasteLoading}>
                  <option value="">Select property…</option>
                  {resiProperties.map(p => (<option key={p.id} value={p.id}>{p.address?.street_address || p.label || p.address || p.id}</option>))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Structure</label>
                <select className="form-select" value={wasteSelectedAssetId} onChange={(e)=> setWasteSelectedAssetId(e.target.value)} disabled={wasteLoading || !wasteSelectedPropertyId}>
                  <option value="">{wasteSelectedPropertyId ? 'Select structure…' : 'Select property first'}</option>
                  {wasteAssetOptions.map(opt => (<option key={opt.id} value={opt.id}>{opt.label}</option>))}
                </select>
                <small className="form-text text-muted">Expenses will roll up to the structure and aggregate to the property.</small>
              </div>
              <div className="mb-3">
                <label className="form-check">
                  <input className="form-check-input" type="checkbox" checked={wasteIsRecurring} onChange={(e)=> setWasteIsRecurring(e.target.checked)} />
                  <span className="form-check-label" style={{ marginLeft:8 }}>Recurring expense?</span>
                </label>
                {wasteIsRecurring && (
                  <div style={{ marginTop:8 }}>
                    {/* Toggle: Existing vs New */}
                    <div className="btn-group btn-group-sm" role="group" aria-label="Recurring mode">
                      <button type="button" className={`btn ${wasteRecurringMode==='existing' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=> setWasteRecurringMode('existing')}>Existing</button>
                      <button type="button" className={`btn ${wasteRecurringMode==='new' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={()=> setWasteRecurringMode('new')}>New</button>
                    </div>

                    {/* Existing series dropdown */}
                    {wasteRecurringMode === 'existing' && (
                      <div style={{ marginTop:8 }}>
                        <label className="form-label">Existing recurring series</label>
                        <select className="form-select" value={wasteExistingSeriesName} onChange={(e)=> setWasteExistingSeriesName(e.target.value)} disabled={!wasteSelectedAssetId}>
                          <option value="">-- Select series --</option>
                          {wasteSeriesOptions.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                        {!wasteSeriesOptions.length && <small className="form-text text-muted">No existing recurring series found for this structure.</small>}
                      </div>
                    )}

                    {/* New series inputs */}
                    {wasteRecurringMode === 'new' && (
                      <div style={{ marginTop:8 }}>
                        <div className="mb-2">
                          <label className="form-label">New series name</label>
                          <input className="form-control" type="text" value={wasteRecurringName} onChange={(e)=> setWasteRecurringName(e.target.value)} placeholder="e.g., Sanitation - Monthly" />
                        </div>
                        <div className="mb-2">
                          <label className="form-label">Frequency</label>
                          <select className="form-select" value={wasteFrequency} onChange={(e)=> setWasteFrequency(e.target.value)}>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="bi_monthly">Bi-monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="semi_annually">Semi-annually</option>
                            <option value="annually">Annually</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Series type</label>
                          <select className="form-select" value={wasteRecurringType} onChange={(e)=> setWasteRecurringType(e.target.value)}>
                            <option value="fixed">Fixed</option>
                            <option value="variable">Variable</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-secondary" onClick={()=> { setShowWasteModal(false); setWasteRow(null); }} disabled={wasteLoading}>Cancel</button>
                <button className="btn btn-primary" onClick={() => { if (typeof submitWasteLink === 'function') { submitWasteLink(); } else { console.error('submitWasteLink is not available'); alert('Unable to submit right now. Please try again.'); } }} disabled={wasteLoading || !wasteSelectedPropertyId || !wasteSelectedAssetId}>Link Expense</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pageView === 'budget' && (<>
      <div style={{ display:'flex', justifyContent:'flex-end', margin: '6px 0' }}>
        <div className="btn-group btn-group-sm" role="group" aria-label="Txn collapse">
          <button type="button" className="btn btn-outline-secondary" onClick={toggleAllTxnParents}>
            {parentRowKeysWithChildren.length > 0 && parentRowKeysWithChildren.every(k => expandedTxns.has(k)) ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
      </div>
      <div className="data-table-container" style={{ margin: 0, overflowX: 'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', color:'#e5e7eb' }}>
          <thead style={{ background:'#1f2937' }}>
            <tr>
              <th style={{ textAlign:'left', padding:'8px', minWidth:'140px', whiteSpace:'nowrap', cursor:'pointer' }} onClick={() => handleSort('date')}>
                Date <SortIcon field="date" />
              </th>
              <th style={{ textAlign:'left', padding:'8px', cursor:'pointer' }} onClick={() => handleSort('description')}>
                Description <SortIcon field="description" />
              </th>
              <th style={{ textAlign:'left', padding:'8px', cursor:'pointer' }} onClick={() => handleSort('user_description')}>
                User Description <SortIcon field="user_description" />
              </th>
              <th style={{ textAlign:'left', padding:'8px', minWidth:'220px', whiteSpace:'nowrap', cursor:'pointer' }} onClick={() => handleSort('category')}>
                Category <SortIcon field="category" />
              </th>
              <th style={{ textAlign:'right', padding:'8px', cursor:'pointer' }} onClick={() => handleSort('amount')}>
                Amount <SortIcon field="amount" />
              </th>
              <th style={{ textAlign:'left', padding:'8px', cursor:'pointer' }} onClick={() => handleSort('source')}>
                Source <SortIcon field="source" />
              </th>
              <th style={{ textAlign:'center', padding:'8px', minWidth:'60px' }}>Asset</th>
              <th style={{ textAlign:'center', padding:'8px', minWidth:'60px' }}>To-Do</th>
              <th style={{ textAlign:'center', padding:'8px', minWidth:'70px' }}>Series</th>
              <th style={{ textAlign:'center', padding:'8px', minWidth:'60px' }}>Debt</th>
              <th style={{ textAlign:'left', padding:'8px' }}>Receipt</th>
              <th style={{ textAlign:'left', padding:'8px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {processedRows.length === 0 && (
              <tr><td colSpan={12} style={{ padding:'10px', color:'#9ca3af' }}>No expenses found for this month.</td></tr>
            )}
            {processedRows.map((r, idx) => (
              <tr
                key={`${r.sourceType}-${r.id || r.realId}-${idx}`}
                style={{
                  backgroundColor: r._isChild ? (r._isReimbursementChild || r._isReimbursementOriginal ? '#0f2a1a' : 'rgba(255,255,255,0.03)') : (idx % 2 ? '#0f172a' : '#111827'),
                  borderLeft: r._isReimbursementChild ? '3px solid #34d399' : r._isReimbursementOriginal ? '3px solid #6ee7b7' : r._isChild ? '3px solid #60a5fa' : 'none',
                  borderBottom: '1px solid #374151'
                }}
              >
                <td style={{ padding:'8px', minWidth:'140px', whiteSpace:'nowrap' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  {(!r._isChild && (r.hasChildren || r.children_count > 0 || r._reimbursementGroupInfo || (() => { const key = `${r.sourceType}-${r.id}`; const kids = (r.children && r.children.length > 0) ? r.children : (hierarchy.childrenByParent.get(key) || []); return kids.length > 0; })())) ? (
                    <button
                      className="btn btn-link btn-sm p-0 me-2"
                      style={{ color:'#93c5fd', width:20, textDecoration:'none' }}
                      onClick={(e) => {
                        e.preventDefault();
                        const key = `${r.sourceType}-${r.id}`;
                        setExpandedTxns(prev => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key); else next.add(key);
                          return next;
                        });
                      }}
                    >
                      {expandedTxns.has(`${r.sourceType}-${r.id}`) ? '▼' : '▶'}
                    </button>
                  ) : (
                    <span style={{ width: r._isChild ? 36 : 20, display: 'inline-block' }} />
                  )}
                  <span>{renderEditableCell(r, 'transaction_date', r.date || '')}</span>
                  </div>
                </td>
                <td style={{ padding:'8px', maxWidth:'160px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.description || ''}>
                  {renderEditableCell(r, 'description', r.description || '')}
                </td>
                <td style={{ padding:'8px', maxWidth:'140px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.user_description || ''}>
                  {renderEditableCell(r, 'user_description', r.user_description || '')}
                </td>
                <td style={{ padding:'8px', minWidth:'220px', whiteSpace:'nowrap' }}>
                  {renderCategoryCell(r)}
                </td>
                <td style={{ padding:'8px', textAlign:'right' }}>
                  {renderEditableCell(r, 'amount', formatCurrency(r.amount))}
                </td>
                <td style={{ padding:'8px' }}>{renderSourceCell(r)}</td>
                <td style={{ padding:'8px' }}>
                  <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                    {(() => {
                      if (!r.linkedAssetId) {
                        // Show "Link Asset" button for linkable domains even if no link exists
                        const cat = String(r.category || r.expense_category || '');
                        const domain = getDomainForCategory(cat);
                        const isYardWaste = cat.includes('yard_waste');
                        const isWaste = domain === 'waste_management' || ((cat.startsWith('waste_') || cat.includes('sanitation')) && !isYardWaste);
                        const isLinkable = domain !== 'generic' || isWaste || isYardWaste;
                        if (!isLinkable) return null;

                        let labelBtn = 'Link Asset';
                        if (domain === 'waste_management' || isWaste) labelBtn = 'Link Waste';
                        else if (domain === 'utilities') labelBtn = 'Link Utilities';
                        else if (domain === 'home') labelBtn = 'Link Home';
                        else if (domain === 'landscaping' || isYardWaste) labelBtn = 'Link Landscaping';
                        else if (domain === 'vehicle') labelBtn = 'Link Vehicle';

                        return (
                          <button
                            className="btn btn-link btn-sm"
                            style={{ color:'#93c5fd', padding:0 }}
                            onClick={(e) => {
                              e.preventDefault();
                              openUtilitiesModal(r);
                            }}
                          >
                            {labelBtn}
                          </button>
                        );
                      }
                      const asset = assetMaps.byId.get(String(r.linkedAssetId));
                      const url = buildAssetUrl(asset);
                      const cat = String(r.category || r.expense_category || '');
                      const domain = getDomainForCategory(cat);
                      const label = buildAssetLabel(asset, { ...assetMaps, transactionDomain: domain });

                      return (
                        <>
                          {url ? <Link to={url} title={label} style={{ color:'#93c5fd', fontSize:'1rem', lineHeight:1 }}>🔗</Link> : <span title={label} style={{ color:'#93c5fd', fontSize:'1rem' }}>🔗</span>}
                          <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.7rem' }} title="Edit Asset Link" onClick={(e) => { e.preventDefault(); openUtilitiesModal(r); }}>✏️</button>
                        </>
                      );
                    })()}
                  </span>
                </td>
                <td style={{ padding:'8px' }}>
                  <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                    {(() => {
                      const todoInfo = r.linked_todo_info;
                      if (!todoInfo) return null;

                      let url = null;
                      if (todoInfo.asset_type === 'vehicle') {
                        url = `/balance_sheet/assets/physical_assets/vehicles/${todoInfo.asset_id}?tab=todo`;
                      } else if (todoInfo.property_id) {
                        url = `/balance_sheet/assets/physical_assets/residential/${todoInfo.property_id}?tab=todo`;
                      }

                      return (
                        <>
                          {url ? (
                            <Link to={url} title={`To-Do: ${todoInfo.title}\nStatus: ${todoInfo.status_display}`} style={{ fontSize:'1rem', lineHeight:1 }}>✅</Link>
                          ) : (
                            <span title={`To-Do: ${todoInfo.title}\nStatus: ${todoInfo.status_display}`} style={{ fontSize:'1rem', cursor:'default' }}>✅</span>
                          )}
                        </>
                      );
                    })()}
                  </span>
                </td>
                <td style={{ padding:'8px' }}>
                  <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                    {(() => {
                      const expInfo = r.linked_expense_info;
                      const incInfo = r.linked_income_info;

                      if (r.standalone_recurring_series_id || r.standalone_recurring_series_name) {
                        const sid = r.standalone_recurring_series_id;
                        const sname = r.standalone_recurring_series_name || 'Recurring';
                        const url = `/income_statement/expenses?view=recurring&tab=series&series_id=${sid}&series_type=standalone`;
                        return (
                          <>
                            <a href={url} target="_blank" rel="noreferrer" style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{sname}</a>
                            <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.7rem' }} title="Edit Series Link" onClick={(e) => { e.preventDefault(); openSingleSeriesModal(r); }}>✏️</button>
                          </>
                        );
                      } else if (expInfo && expInfo.recurring_name) {
                        const url = r.linked_recurring_series_id ? `/income_statement/expenses?view=recurring&tab=series&series_id=${r.linked_recurring_series_id}&series_type=asset` : null;
                        return (
                          <>
                            {url ? <a href={url} target="_blank" rel="noreferrer" style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{expInfo.recurring_name}</a> : <span style={{ color:'#e5e7eb' }}>{expInfo.recurring_name}</span>}
                            <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.7rem' }} title="Edit Series Link" onClick={(e) => { e.preventDefault(); openUtilitiesModal(r); }}>✏️</button>
                          </>
                        );
                      } else if (incInfo && incInfo.display_name) {
                        const url = `/income?series_id=${r.linked_income_stream_id}`;
                        return (
                          <>
                            <Link to={url} style={{ color:'#93c5fd', whiteSpace:'nowrap' }}>{incInfo.display_name}</Link>
                            <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.7rem' }} title="Edit Series Link" onClick={(e) => { e.preventDefault(); setIncomeLinkRow(r); setShowIncomeLinkModal(true); }}>✏️</button>
                          </>
                        );
                      } else if (r.sourceType === 'cash' || r.sourceType === 'revolver') {
                        return (
                          <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.75rem' }} title="Link Recurring Series" onClick={(e) => { e.preventDefault(); openSingleSeriesModal(r); }}>🔗 Link Series</button>
                        );
                      }
                      return null;
                    })()}
                  </span>
                </td>
                <td style={{ padding:'8px' }}>
                  <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                    {r.sourceType === 'cash' && r.linkedDebt && r.linkedDebtId ? (
                      <>
                        {(() => {
                          let url = null;
                          if (r.linkedDebt.type === 'auto_loan') url = `/balance_sheet/financing/auto_loans/${r.linkedDebtId}`;
                          else if (r.linkedDebt.type === 'mortgage') url = `/balance_sheet/financing/mortgages/${r.linkedDebtId}`;
                          else if (r.linkedDebt.type === 'credit_card' || r.linkedDebt.type === 'revolver') url = `/balance_sheet/financing/revolvers/credit-cards/${r.linkedDebtId}`;
                          else if (r.linkedDebt.type === 'heloc') url = `/balance_sheet/financing/revolvers/helocs/${r.linkedDebtId}`;

                          const debtLabel = r.linkedDebt.debt_label || r.linkedDebt.debt_name || '';
                          return url ? (
                            <a href={url} title={debtLabel} style={{ fontSize:'1rem', lineHeight:1 }}>💳</a>
                          ) : (
                            <span title={debtLabel} style={{ fontSize:'1rem' }}>💳</span>
                          );
                        })()}
                        <button className="btn btn-link btn-sm" style={{ color:'#6b7280', padding:0, fontSize:'0.7rem' }} title="Edit Debt Link" onClick={(e) => { e.preventDefault(); if (String(r.category || r.expense_category || '') === 'debt_disbursement') { setDebtDisbursementRow(r); setShowDebtDisbursementModal(true); } else { openDebtModalForRow(r); } }}>✏️</button>
                      </>
                    ) : (
                      r.sourceType === 'cash' && (String(r.category || r.expense_category || '') === 'payments' || String(r.category || r.expense_category || '') === 'debt_disbursement') && (
                        <button
                          className="btn btn-link btn-sm"
                          style={{ color:'#93c5fd', padding:0 }}
                          onClick={(e) => {
                            e.preventDefault();
                            if (String(r.category || r.expense_category || '') === 'debt_disbursement') {
                              setDebtDisbursementRow(r);
                              setShowDebtDisbursementModal(true);
                            } else {
                              openDebtModalForRow(r);
                            }
                          }}
                        >
                          Link Debt
                        </button>
                      )
                    )}
                  </span>
                </td>
                <td style={{ padding:'8px' }}>
                  {r.receipt || r.receipt_url ? (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => r.sourceType === 'revolver' ? openCcReceipt(r) : openCashReceipt(r)}
                    >View</button>
                  ) : (
                    <label className="btn btn-sm btn-outline-secondary mb-0">
                      Upload
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        style={{ display:'none' }}
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0];
                          if (file) {
                            if (r.sourceType === 'revolver') uploadCcReceipt(r.id, file);
                            else uploadCashReceipt(r.id, file);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </td>
                <td style={{ padding:'8px' }}>
                  <div className="btn-group btn-group-sm" role="group" aria-label="Actions">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        const absAmt = Math.abs(Number(r.amount) || 0);
                        const initial = absAmt > 0 ? [
                          { key: 1, amount: Math.round((absAmt/2) * 100)/100, description: r.description },
                          { key: 2, amount: Math.round((absAmt - Math.round((absAmt/2) * 100)/100) * 100)/100, description: r.description },
                        ] : [{ key: 1, amount: 0, description: r.description }, { key: 2, amount: 0, description: r.description }];
                        setSplitRow(r);
                        setSplitItems(initial);
                        setSplitError('');
                        setShowSplitModal(true);
                      }}
                    >
                      Split
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => deleteTransaction(r)}
                      disabled={deleting.has(`${r.sourceType}-${r.id}`)}
                      style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                    >
                      {deleting.has(`${r.sourceType}-${r.id}`) ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          Deleting...
                        </>
                      ) : (
                        'Delete'
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>)}
      {showSplitModal && splitRow && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 }} onClick={()=> { if (!splitSubmitting) { setShowSplitModal(false); setSplitRow(null); } }}>
          <div style={{ width:'min(720px, 95vw)', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }} onClick={(e)=> e.stopPropagation()}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
              <div style={{ fontSize:18, fontWeight:700 }}>Split Transaction</div>
              <button className="btn btn-sm btn-secondary" onClick={()=> { if (!splitSubmitting) { setShowSplitModal(false); setSplitRow(null); } }} style={{ marginLeft:'auto' }}>✕</button>
            </div>
            <div style={{ padding:16 }}>
              {splitError && <div className="alert alert-danger" style={{ background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{splitError}</div>}
              <div style={{ marginBottom:8, color:'#9ca3af' }}>
                <div><strong>Date:</strong> {splitRow.date || ''}</div>
                <div><strong>Description:</strong> {splitRow.description || ''}</div>
                <div><strong>Parent Amount:</strong> {formatCurrency(Math.abs(Number(splitRow.amount)||0))}</div>
                <small>Enter positive amounts for each child. They must sum to the parent amount.</small>
              </div>
              <table style={{ width:'100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign:'left', padding:8 }}>Child</th>
                    <th style={{ textAlign:'right', padding:8, width:160 }}>Amount</th>
                    <th style={{ textAlign:'left', padding:8 }}>Description</th>
                    <th style={{ textAlign:'left', padding:8 }}>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {splitItems.map((it, idx) => (
                    <tr key={it.key}>
                      <td style={{ padding:8 }}>#{idx+1}</td>
                      <td style={{ padding:8, textAlign:'right' }}>
                        <input type="number" step="0.01" className="form-control form-control-sm" value={it.amount}
                          onChange={(e)=> {
                            const v = e.target.value;
                            setSplitItems(prev => prev.map(x => x.key===it.key ? { ...x, amount: v } : x));
                          }} />
                      </td>
                      <td style={{ padding:8 }}>
                        <input type="text" className="form-control form-control-sm" value={it.description || ''}
                          onChange={(e)=> setSplitItems(prev => prev.map(x => x.key===it.key ? { ...x, description: e.target.value } : x))} />
                      </td>
                      <td style={{ padding:8 }}>
                        <button className="btn btn-sm btn-outline-secondary" onClick={()=> setSplitItems(prev => prev.filter(x => x.key !== it.key))} disabled={splitItems.length <= 2 || splitSubmitting}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="d-flex justify-content-between align-items-center" style={{ marginTop:12 }}>
                <button className="btn btn-outline-secondary btn-sm" onClick={()=> setSplitItems(prev => [...prev, { key: (prev[prev.length-1]?.key||0)+1, amount: 0, description: splitRow.description }])} disabled={splitSubmitting}>+ Add Split</button>
                <div style={{ color:'#9ca3af' }}>
                  Sum: {formatCurrency((splitItems.reduce((s,it)=> s + (parseFloat(it.amount)||0), 0)))} of {formatCurrency(Math.abs(Number(splitRow.amount)||0))}
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2" style={{ marginTop:12 }}>
                <button className="btn btn-secondary" onClick={()=> { setShowSplitModal(false); setSplitRow(null); }} disabled={splitSubmitting}>Cancel</button>
                <button className="btn btn-success" disabled={splitSubmitting} onClick={async ()=>{
                  setSplitError('');
                  // Validate
                  const parentAbs = Math.abs(Number(splitRow.amount)||0);
                  const total = splitItems.reduce((s,it)=> s + (parseFloat(it.amount)||0), 0);
                  if (Math.abs(total - parentAbs) > 0.01) { setSplitError('Split amounts must sum exactly to the parent amount.'); return; }
                  if (splitItems.length < 2) { setSplitError('Add at least two split rows.'); return; }
                  setSplitSubmitting(true);
                  try {
                    const url = splitRow.sourceType === 'revolver'
                      ? `/api/balance_sheet/financing/revolvers/transactions/${splitRow.id}/split/`
                      : `/api/balance_sheet/assets/cash_accounts/transactions/${splitRow.id}/split/`;
                    const payload = { splits: splitItems.map(it => ({ amount: parseFloat(it.amount)||0, description: (it.description||'').trim() || null })) };
                    await axios.post(url, payload);
                    setShowSplitModal(false);
                    setSplitRow(null);
                    // Refresh month data for Expenses page
                    const [yy, mm] = month.split('-').map(Number);
                    const endDay = new Date(yy, mm, 0).getDate();
                    const start = `${yy}-${String(mm).padStart(2,'0')}-01`;
                    const end = `${yy}-${String(mm).padStart(2,'0')}-${String(endDay).padStart(2,'0')}`;
                    const [rvRes, caRes] = await Promise.allSettled([
                      axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: start, end_date: end }  }),
                      axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: start, end_date: end }  }),
                    ]);
                    if (rvRes.status === 'fulfilled') {
                      const data = Array.isArray(rvRes.value?.data) ? rvRes.value.data : [];
                      const rv = data.filter(t => {
                        const type = (t.revolver_type || '').toLowerCase();
                        return type === 'credit_card' || type === 'heloc';
                      });
                      setCcTxns(rv);
                    }
                    if (caRes.status === 'fulfilled') {
                      setCashTxns(Array.isArray(caRes.value?.data) ? caRes.value.data : []);
                    }
                  } catch (e) {
                    setSplitError('Failed to create split.');
                  } finally {
                    setSplitSubmitting(false);
                  }
                }}>{splitSubmitting ? 'Saving…' : 'Save Split'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDebtModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ width: '720px', maxWidth: '95vw', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
              <div style={{ fontSize:18, fontWeight:700 }}>Link Payment to Debt</div>
              <button onClick={()=>{ setShowDebtModal(false); setLinkingRow(null); }} style={{ marginLeft:'auto', background:'transparent', color:'#9ca3af', border:'none', fontSize:18, cursor:'pointer' }} aria-label="Close">×</button>
            </div>
            <div style={{ padding:16 }}>
              {modalError && <div style={{ color:'#f87171', marginBottom:8 }}>{modalError}</div>}
              {modalLoading && <div>Loading…</div>}
              {!modalLoading && linkingRow && (
                <>
                  <div style={{ marginBottom:12, color:'#9ca3af' }}>
                    Transaction: <strong>{linkingRow.description}</strong> on {linkingRow.date} for {formatCurrency(Math.abs(linkingRow.amount))}
                  </div>
                  {modalPhase === 'chooseDebt' && (
                    <div>
                      <div style={{ marginBottom:8 }}>Select a debt to link this payment to:</div>
                      <div style={{ maxHeight: '300px', overflowY:'auto', border:'1px solid #374151', borderRadius:6 }}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                          <thead style={{ position:'sticky', top:0, background:'#111827' }}>
                            <tr>
                              <th style={{ textAlign:'left', padding:8 }}>Type</th>
                              <th style={{ textAlign:'left', padding:8 }}>Lender</th>
                              <th style={{ textAlign:'left', padding:8 }}>Details</th>
                              <th style={{ textAlign:'right', padding:8 }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(debtList || []).map((d, i) => (
                              <tr key={`${d.id}-${i}`} style={{ background: i % 2 ? '#0f172a' : '#111827' }}>
                                <td style={{ padding:8 }}>{d.debt_type === 'auto_loan' ? 'Auto Loan' : d.debt_type === 'mortgage' ? 'Mortgage' : d.debt_type === 'credit_card' ? 'Credit Card' : d.debt_type === 'heloc' ? 'HELOC' : d.debt_type}</td>
                                <td style={{ padding:8 }}>{d.lender_name || d.lender || 'Lender'}</td>
                                <td style={{ padding:8 }}>{d.asset_info?.description || ''}</td>
                                <td style={{ padding:8, textAlign:'right' }}>
                                  <button className="btn" onClick={()=>{ setSelectedDebt(d); loadDebtCandidates(d); }} style={{ padding:'6px 10px' }}>Select</button>
                                </td>
                              </tr>
                            ))}
                            {(!debtList || debtList.length === 0) && (
                              <tr><td colSpan={4} style={{ padding:8, color:'#9ca3af' }}>No debts found.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {modalPhase === 'chooseCandidate' && (
                    <div>
                      <div style={{ marginBottom:8 }}>Select a matching {selectedDebt?.debt_type === 'credit_card' || selectedDebt?.debt_type === 'heloc' ? 'transaction' : 'scheduled payment'} for <strong>{selectedDebt?.lender_name || selectedDebt?.lender || ''} {selectedDebt?.debt_type === 'auto_loan' ? 'Auto Loan' : selectedDebt?.debt_type === 'mortgage' ? 'Mortgage' : selectedDebt?.debt_type === 'credit_card' ? 'Credit Card' : selectedDebt?.debt_type === 'heloc' ? 'HELOC' : selectedDebt?.debt_type}</strong>:</div>
                      <div style={{ maxHeight:'300px', overflowY:'auto', border:'1px solid #374151', borderRadius:6 }}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                          <thead style={{ position:'sticky', top:0, background:'#111827' }}>
                            <tr>
                              <th style={{ textAlign:'left', padding:8 }}>Date</th>
                              <th style={{ textAlign:'right', padding:8 }}>Amount</th>
                              <th style={{ textAlign:'right', padding:8 }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(debtCandidates || []).map((c, i) => (
                              <tr key={`${c.id}-${i}`} style={{ background: i % 2 ? '#0f172a' : '#111827' }}>
                                <td style={{ padding:8 }}>{c.payment_date || c.post_date || c.transaction_date}</td>
                                <td style={{ padding:8, textAlign:'right' }}>{formatCurrency(Math.abs(c.amount || 0))}</td>
                                <td style={{ padding:8, textAlign:'right' }}>
                                  <button className="btn" onClick={()=> confirmLinkToCandidate(c)} style={{ padding:'6px 10px' }}>Link</button>
                                </td>
                              </tr>
                            ))}
                            {(!debtCandidates || debtCandidates.length === 0) && (
                              <tr><td colSpan={3} style={{ padding:8, color:'#9ca3af' }}>No matching {selectedDebt?.debt_type === 'credit_card' || selectedDebt?.debt_type === 'heloc' ? 'transactions' : 'scheduled payments'} found in the expected window. You can close and pick a different debt.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ marginTop:10, display:'flex', gap:8 }}>
                        <button className="btn" onClick={()=>{ setModalPhase('chooseDebt'); setDebtCandidates([]); }}>&laquo; Back</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showAutoModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ width: '760px', maxWidth: '95vw', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
              <div style={{ fontSize:18, fontWeight:700 }}>Link Vehicle Expense</div>
              <button onClick={()=>{ setShowAutoModal(false); setAutoRow(null); }} style={{ marginLeft:'auto', background:'transparent', color:'#9ca3af', border:'none', fontSize:18, cursor:'pointer' }} aria-label="Close">×</button>
            </div>
            <div style={{ padding:16 }}>
              {autoForm.error && <div style={{ color:'#f87171', marginBottom:8 }}>{autoForm.error}</div>}
              <div style={{ marginBottom:12, color:'#9ca3af' }}>
                {autoRow && (<>
                  Transaction: <strong>{autoRow.description}</strong> on {autoRow.date || autoRow.transaction_date} for {formatCurrency(Math.abs(autoRow.amount || 0))}
                </>)}
              </div>
              <div className="row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label className="form-label">Vehicle</label>
                  <select className="form-select" value={autoForm.vehicleId} onChange={e=> onChangeVehicle(e.target.value)}>
                    <option value="">Select vehicle…</option>
                    {vehicles.map(v => (
                      <option key={v.id} value={v.id}>{v.description || `Vehicle #${v.id}`}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Asset Expense Category</label>
                  <select className="form-select" value={autoForm.assetExpenseCategory} onChange={e=> setAutoForm(prev=>({ ...prev, assetExpenseCategory: e.target.value }))}>
                    {(assetExpenseChoices.length ? assetExpenseChoices : [
                      { value:'maintenance', label:'Asset Maintenance' },
                      { value:'cleaning', label:'Cleaning' },
                      { value:'repair', label:'Asset Repair' },
                      { value:'enhancement', label:'Asset Enhancement' },
                      { value:'insurance', label:'Insurance Payment' },
                      { value:'taxes', label:'Tax Payment' }
                    ]).map(c => (<option key={c.value} value={c.value}>{c.label}</option>))}
                  </select>
                </div>
              </div>
              <div style={{ marginTop:12 }}>
                <label className="form-check">
                  <input className="form-check-input" type="checkbox" checked={autoForm.isRecurring} onChange={e=> setAutoForm(prev=> ({ ...prev, isRecurring: e.target.checked }))} />
                  <span className="form-check-label" style={{ marginLeft:8 }}>Recurring expense?</span>
                </label>
              </div>
              {autoForm.isRecurring && (
                <div style={{ marginTop:12, border:'1px solid #374151', borderRadius:6, padding:12 }}>
                  <div style={{ marginBottom:8 }}>
                    <label className="form-check" style={{ marginRight:12 }}>
                      <input className="form-check-input" type="radio" name="schedmode" checked={autoForm.scheduleMode==='existing'} onChange={()=> setAutoForm(prev=> ({ ...prev, scheduleMode:'existing' }))} />
                      <span className="form-check-label" style={{ marginLeft:8 }}>Link to existing schedule</span>
                    </label>
                    <label className="form-check" style={{ marginLeft:16 }}>
                      <input className="form-check-input" type="radio" name="schedmode" checked={autoForm.scheduleMode==='new'} onChange={()=> setAutoForm(prev=> ({ ...prev, scheduleMode:'new' }))} />
                      <span className="form-check-label" style={{ marginLeft:8 }}>Create new schedule</span>
                    </label>
                  </div>

                  {autoForm.scheduleMode==='existing' ? (
                    <div>
                      <label className="form-label">Maintenance Schedule</label>
                      <select className="form-select" value={autoForm.scheduleId} onChange={e=> setAutoForm(prev=> ({ ...prev, scheduleId: e.target.value }))}>
                        <option value="">Select…</option>
                        {schedules.map(s => (
                          <option key={s.id} value={s.id}>{s.description} — expected {s.expected_maintenance_date}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                      <div>
                        <label className="form-label">Description</label>
                        <input className="form-control" type="text" value={autoForm.newSchedule.description} onChange={e=> setAutoForm(prev=> ({ ...prev, newSchedule: { ...prev.newSchedule, description: e.target.value } }))} />
                      </div>
                      <div>
                        <label className="form-label">Frequency</label>
                        <select className="form-select" value={autoForm.newSchedule.frequency} onChange={e=> setAutoForm(prev=> ({ ...prev, newSchedule: { ...prev.newSchedule, frequency: e.target.value } }))}>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="bi_monthly">Bi-monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="semi_annually">Semi-annually</option>
                          <option value="annually">Annually</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Expected Date</label>
                        <input className="form-control" type="date" value={autoForm.newSchedule.expected_date} onChange={e=> setAutoForm(prev=> ({ ...prev, newSchedule: { ...prev.newSchedule, expected_date: e.target.value } }))} />
                      </div>
                      <div>
                        <label className="form-label">Expected Amount</label>
                        <input className="form-control" type="number" step="0.01" value={autoForm.newSchedule.expected_amount} onChange={e=> setAutoForm(prev=> ({ ...prev, newSchedule: { ...prev.newSchedule, expected_amount: e.target.value } }))} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginTop:16, display:'flex', justifyContent:'flex-end', gap:8 }}>
                <button className="btn btn-secondary" onClick={()=> { setShowAutoModal(false); setAutoRow(null); }}>Cancel</button>
                <button className="btn btn-success" onClick={submitAutoLink} disabled={autoForm.loading || !autoForm.vehicleId}>
                  {autoForm.loading ? 'Linking…' : 'Link Expense'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLandModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ width: '720px', maxWidth: '95vw', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
              <div style={{ fontSize:18, fontWeight:700 }}>Link Landscaping Expense</div>
              <button onClick={()=>{ setShowLandModal(false); setLandRow(null); }} style={{ marginLeft:'auto', background:'transparent', color:'#9ca3af', border:'none', fontSize:18, cursor:'pointer' }} aria-label="Close">×</button>
            </div>
            <div style={{ padding:16 }}>
              {landError && <div style={{ color:'#f87171', marginBottom:8 }}>{landError}</div>}
              <div className="mb-3">
                <label className="form-label">Residential Property</label>
                <select className="form-select" value={selectedPropertyId} onChange={(e)=> onChangePropertyForLand(e.target.value)} disabled={landLoading}>
                  <option value="">Select property…</option>
                  {resiProperties.map(p => (
                    <option key={p.id} value={p.id}>{p.address?.street_address || p.label || p.address || p.id}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Land</label>
                <div style={{ padding:8, border:'1px solid #374151', borderRadius:6, background:'#111827' }}>
                  {selectedPropertyId ? (
                    landLoading ? 'Loading land…' : (landChild ? (
                      landChild.label
                    ) : (
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                        <span>No Land configured for selected property.</span>
                        <button className="btn btn-sm btn-outline-info" onClick={()=> setShowAddLand(true)} disabled={landLoading}>Add Land</button>
                      </div>
                    ))
                  ) : (
                    <span style={{ color:'#9ca3af' }}>Select a property to see its Land.</span>
                  )}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-check">
                  <input className="form-check-input" type="checkbox" checked={landIsRecurring} onChange={(e)=> setLandIsRecurring(e.target.checked)} />
                  <span className="form-check-label" style={{ marginLeft:8 }}>Recurring expense?</span>
                </label>
                {landIsRecurring && (
                  <div style={{ marginTop:8 }}>
                    <label className="form-label">Frequency</label>
                    <select className="form-select" value={landFrequency} onChange={(e)=> setLandFrequency(e.target.value)}>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="bi_monthly">Bi-monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="semi_annually">Semi-annually</option>
                      <option value="annually">Annually</option>
                    </select>
                    <div style={{ marginTop:12 }}>
                      <label className="form-label">Recurring Series</label>
                      <select className="form-select" value={landRecurringChoice} onChange={(e)=> setLandRecurringChoice(e.target.value)}
                              disabled={!landChild || landRecurringLoading}
                              onFocus={async()=> { if (landChild && !landSeriesOptions.length) { try { setLandRecurringLoading(true); const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${landChild.id}/maintenance-items/`); const items = Array.isArray(res.data) ? res.data.map(m => ({ id: m.id, name: m.description || `Series ${m.id}`, frequency: m.recurring_frequency })) : []; setLandSeriesOptions(items); } catch (e) { setLandSeriesOptions([]); } finally { setLandRecurringLoading(false); } } }}>
                        <option value="">{landRecurringLoading ? 'Loading…' : 'Select existing series…'}</option>
                        {landSeriesOptions.map(opt => (
                          <option key={opt.id} value={String(opt.id)}>
                            {opt.name} {opt.frequency ? `(${opt.frequency})` : ''}
                          </option>
                        ))}
                        <option value="__new__">Create new series…</option>
                      </select>
                      {landRecurringChoice === '__new__' && (
                        <div style={{ marginTop:8 }}>
                          <label className="form-label">New Series Name</label>
                          <input className="form-control" value={landRecurringName} onChange={e=> setLandRecurringName(e.target.value)} placeholder="e.g., Lawn Service" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-secondary" onClick={()=> { setShowLandModal(false); setLandRow(null); }} disabled={landLoading}>Cancel</button>
                <button className="btn btn-primary" onClick={submitLandLink} disabled={landLoading || !selectedPropertyId || !landChild}>Link Expense</button>
              </div>

              {showAddLand && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
                  <div style={{ width:'720px', maxWidth:'95vw', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }}>
                    <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
                      <div style={{ fontSize:18, fontWeight:700 }}>Add Land</div>
                      <button onClick={()=> setShowAddLand(false)} style={{ marginLeft:'auto' }} className="btn btn-sm btn-secondary">✕</button>
                    </div>
                    <div style={{ padding:16 }}>
                      {landFormError && <div style={{ color:'#f87171', marginBottom:8 }}>{landFormError}</div>}
                      <div className="row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                        <div>
                          <label className="form-label">Legal Description</label>
                          <input className="form-control" value={landForm.legal_description} onChange={e=> setLandForm(prev=> ({ ...prev, legal_description: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Parcel Number</label>
                          <input className="form-control" value={landForm.parcel_number} onChange={e=> setLandForm(prev=> ({ ...prev, parcel_number: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Lot Number</label>
                          <input className="form-control" type="number" value={landForm.lot_number} onChange={e=> setLandForm(prev=> ({ ...prev, lot_number: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Plat Number</label>
                          <input className="form-control" type="number" value={landForm.plat_number} onChange={e=> setLandForm(prev=> ({ ...prev, plat_number: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Tax District</label>
                          <input className="form-control" value={landForm.tax_district} onChange={e=> setLandForm(prev=> ({ ...prev, tax_district: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Area (acres)</label>
                          <input className="form-control" type="number" step="0.0001" value={landForm.area_in_acres} onChange={e=> setLandForm(prev=> ({ ...prev, area_in_acres: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Allocate Financing to Land (optional)</label>
                          <input className="form-control" type="number" step="0.01" value={landForm.financing_allocation} onChange={e=> setLandForm(prev=> ({ ...prev, financing_allocation: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Latitude (optional)</label>
                          <input className="form-control" type="number" step="0.000001" value={landForm.latitude} onChange={e=> setLandForm(prev=> ({ ...prev, latitude: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label">Longitude (optional)</label>
                          <input className="form-control" type="number" step="0.000001" value={landForm.longitude} onChange={e=> setLandForm(prev=> ({ ...prev, longitude: e.target.value }))} />
                        </div>
                        <div style={{ gridColumn:'1 / -1' }}>
                          <label className="form-label">Survey Description (optional)</label>
                          <textarea className="form-control" rows={3} value={landForm.survey_description} onChange={e=> setLandForm(prev=> ({ ...prev, survey_description: e.target.value }))} />
                        </div>
                      </div>
                      <div className="d-flex justify-content-end gap-2" style={{ marginTop:12 }}>
                        <button className="btn btn-secondary" onClick={()=> setShowAddLand(false)} disabled={landFormLoading}>Cancel</button>
                        <button className="btn btn-success" onClick={submitAddLand} disabled={landFormLoading || !selectedPropertyId || !landForm.legal_description || !landForm.parcel_number || !landForm.lot_number || !landForm.plat_number || !landForm.tax_district || !landForm.area_in_acres}>
                          {landFormLoading ? 'Saving…' : 'Save Land'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── RecurringSummaryTab ─────────────────────────────────────────────────────
// Summary tab shown inside the Recurring view — metrics + time-series chart
function RecurringSummaryTab({ allSeries }) {
  const today = new Date();
  const ytdStart = `${today.getFullYear()}-01-01`;
  const todayStr = today.toISOString().slice(0, 10);

  const [startDate, setStartDate] = React.useState(ytdStart);
  const [endDate, setEndDate] = React.useState(todayStr);
  const [grouping, setGrouping] = React.useState('monthly');
  const [separateDebt, setSeparateDebt] = React.useState(false);
  const [isCumulative, setIsCumulative] = React.useState(false);
  const [showByAsset, setShowByAsset] = React.useState(false);
  const [chartType, setChartType] = React.useState('bar'); // 'bar' | 'line'
  const [isStacked, setIsStacked] = React.useState(true); // default stacked for bar
  const [activePeriod, setActivePeriod] = React.useState('ytd'); // track which quick period button is active
  const [txData, setTxData] = React.useState([]); // flat list of all transactions with series metadata
  const [loadingTx, setLoadingTx] = React.useState(false);
  const canvasRef = React.useRef(null);
  const chartRef = React.useRef(null);
  const seriesBreakdownRef = React.useRef({}); // period -> bucket -> { seriesName -> { amount, id, series_type } }
  const tooltipDivRef = React.useRef(null); // custom HTML tooltip overlay
  const tooltipHideTimerRef = React.useRef(null); // delay timer for hiding tooltip

  // Quick period buttons
  const applyQuickPeriod = (key) => {
    setActivePeriod(key);
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const pad = n => String(n).padStart(2, '0');
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    if (key === 'curr_month') {
      setStartDate(`${y}-${pad(m+1)}-01`);
      setEndDate(fmt(now));
    } else if (key === '1m') {
      const d = new Date(now); d.setMonth(d.getMonth()-1);
      setStartDate(fmt(d)); setEndDate(fmt(now));
    } else if (key === '3m') {
      const d = new Date(now); d.setMonth(d.getMonth()-3);
      setStartDate(fmt(d)); setEndDate(fmt(now));
    } else if (key === '6m') {
      const d = new Date(now); d.setMonth(d.getMonth()-6);
      setStartDate(fmt(d)); setEndDate(fmt(now));
    } else if (key === 'ytd') {
      setStartDate(`${y}-01-01`); setEndDate(fmt(now));
    } else if (key === '1y') {
      const d = new Date(now); d.setFullYear(d.getFullYear()-1);
      setStartDate(fmt(d)); setEndDate(fmt(now));
    } else if (key === 'all') {
      setStartDate('2000-01-01'); setEndDate(fmt(now));
    }
  };

  // Fetch all transactions for all series whenever allSeries changes
  React.useEffect(() => {
    if (!allSeries || allSeries.length === 0) return;
    setLoadingTx(true);
    const fetchAll = async () => {
      try {
        // Single bulk endpoint returns all recurring transactions (asset + standalone)
        const res = await axios.get('/api/balance_sheet/assets/all-recurring-transactions/');
        const list = Array.isArray(res.data) ? res.data : [];

        // Build series lookup map from allSeries for enrichment
        const seriesMap = {};
        allSeries.forEach(s => { seriesMap[`${s.series_type}__${s.id}`] = s; });

        const rows = list.map(t => {
          const s = seriesMap[`${t.series_type}__${t.series_id}`] || null;
          return { ...t, _series: s, _source: t.series_type };
        }).filter(t => t._series !== null);

        setTxData(rows);
      } catch (_) {
        setTxData([]);
      } finally {
        setLoadingTx(false);
      }
    };
    fetchAll();
  }, [allSeries]);

  // Filter + group transactions into chart data
  const { chartLabels, assetDatasets, standaloneDataset, metrics } = React.useMemo(() => {
    // Filter by date range
    const isDebtPayment = (t) => {
      const cat = (t.expense_category || t.category || '').toLowerCase();
      return cat === 'financing_principal' || cat === 'financing_interest' || cat === 'financing_inter' || cat === 'payments';
    };
    const filtered = txData.filter(t => {
      const d = t.payment_date || t.transaction_date || t.date || '';
      const dateStr = d ? d.slice(0, 10) : '';
      if (!dateStr) return false;
      if (startDate && dateStr < startDate) return false;
      if (endDate && dateStr > endDate) return false;
      return true;
    });

    // Build period key
    const getPeriodKey = (dateStr) => {
      if (!dateStr) return '';
      if (grouping === 'daily') return dateStr.slice(0, 10);
      if (grouping === 'weekly') {
        const d = new Date(dateStr + 'T00:00:00');
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        const mon = new Date(d); mon.setDate(diff);
        return `${mon.getFullYear()}-${String(mon.getMonth()+1).padStart(2,'0')}-${String(mon.getDate()).padStart(2,'0')}`;
      }
      if (grouping === 'quarterly') {
        const m = parseInt(dateStr.slice(5, 7), 10);
        return `${dateStr.slice(0, 4)}-Q${Math.ceil(m/3)}`;
      }
      if (grouping === 'annual') return dateStr.slice(0, 4);
      return dateStr.slice(0, 7); // monthly
    };

    const getAmt = (t) => {
      const raw = Number(t.amount || t.payment_amount || 0);
      // Normalize to positive spend
      if (t._source === 'cash') return raw < 0 ? Math.abs(raw) : raw;
      if (t._source === 'revolver') return raw > 0 ? raw : Math.abs(raw);
      return Math.abs(raw);
    };

    // Generate all period keys between startDate and endDate (fill gaps with $0)
    const generateAllPeriods = () => {
      if (!startDate || !endDate) {
        // Fallback: collect from data
        const s = new Set();
        filtered.forEach(t => {
          const d = t.payment_date || t.transaction_date || t.date || '';
          const k = getPeriodKey(d.slice(0, 10));
          if (k) s.add(k);
        });
        return Array.from(s).sort();
      }
      const periods = [];
      const cur = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T00:00:00');
      while (cur <= end) {
        const k = getPeriodKey(cur.toISOString().slice(0, 10));
        if (k && (periods.length === 0 || periods[periods.length - 1] !== k)) periods.push(k);
        // Advance by grouping
        if (grouping === 'daily') {
          cur.setDate(cur.getDate() + 1);
        } else if (grouping === 'weekly') {
          cur.setDate(cur.getDate() + 7);
        } else if (grouping === 'monthly') {
          cur.setMonth(cur.getMonth() + 1);
        } else if (grouping === 'quarterly') {
          cur.setMonth(cur.getMonth() + 3);
        } else if (grouping === 'annual') {
          cur.setFullYear(cur.getFullYear() + 1);
        } else {
          cur.setMonth(cur.getMonth() + 1);
        }
      }
      return periods;
    };
    const labels = generateAllPeriods();

    // Standalone totals per period
    const standalonePeriod = {};
    labels.forEach(l => { standalonePeriod[l] = 0; });
    filtered.filter(t => t._series?.series_type === 'standalone').forEach(t => {
      const d = t.payment_date || t.transaction_date || t.date || '';
      const k = getPeriodKey(d.slice(0, 10));
      if (k && standalonePeriod[k] !== undefined) standalonePeriod[k] += getAmt(t);
    });

    // Asset totals — either aggregated, broken down by asset name, or split debt/non-debt
    const assetTxns = filtered.filter(t => t._series?.series_type === 'asset');
    const assetSeriesNames = new Set();
    assetTxns.forEach(t => {
      assetSeriesNames.add(t._series?.asset_description || t._series?.display_name || 'Asset');
    });
    const assetNameList = Array.from(assetSeriesNames).sort();

    const assetPeriodMap = {}; // key -> { period -> amount }
    if (showByAsset) {
      assetNameList.forEach(name => {
        assetPeriodMap[name] = {};
        labels.forEach(l => { assetPeriodMap[name][l] = 0; });
      });
      assetTxns.forEach(t => {
        const name = t._series?.asset_description || t._series?.display_name || 'Asset';
        const d = t.payment_date || t.transaction_date || t.date || '';
        const k = getPeriodKey(d.slice(0, 10));
        if (k && assetPeriodMap[name] && assetPeriodMap[name][k] !== undefined) {
          assetPeriodMap[name][k] += getAmt(t);
        }
      });
    } else if (separateDebt) {
      assetPeriodMap['Asset-Linked · Debt Payments'] = {};
      assetPeriodMap['Asset-Linked · Non-Debt'] = {};
      labels.forEach(l => {
        assetPeriodMap['Asset-Linked · Debt Payments'][l] = 0;
        assetPeriodMap['Asset-Linked · Non-Debt'][l] = 0;
      });
      assetTxns.forEach(t => {
        const d = t.payment_date || t.transaction_date || t.date || '';
        const k = getPeriodKey(d.slice(0, 10));
        if (!k) return;
        const bucket = isDebtPayment(t) ? 'Asset-Linked · Debt Payments' : 'Asset-Linked · Non-Debt';
        assetPeriodMap[bucket][k] += getAmt(t);
      });
    } else {
      assetPeriodMap['Asset-Linked'] = {};
      labels.forEach(l => { assetPeriodMap['Asset-Linked'][l] = 0; });
      assetTxns.forEach(t => {
        const d = t.payment_date || t.transaction_date || t.date || '';
        const k = getPeriodKey(d.slice(0, 10));
        if (k) assetPeriodMap['Asset-Linked'][k] += getAmt(t);
      });
    }

    // Apply cumulative if needed
    const applyCumulative = (arr) => {
      if (!isCumulative) return arr;
      let running = 0;
      return arr.map(v => { running += v; return running; });
    };

    const standaloneValues = applyCumulative(labels.map(l => standalonePeriod[l] || 0));
    const paletteColors = ['#3b82f6','#f59e0b','#10b981','#a78bfa','#34d399','#60a5fa','#fbbf24'];
    const debtColorMap = {
      'Asset-Linked · Debt Payments': '#f87171',
      'Asset-Linked · Non-Debt': '#60a5fa',
    };
    const assetDatasetList = Object.entries(assetPeriodMap).map(([name, periodData], i) => {
      const color = debtColorMap[name] || paletteColors[i % paletteColors.length];
      return { name, values: applyCumulative(labels.map(l => periodData[l] || 0)), color };
    });

    // Metrics
    const totalAsset = assetTxns.reduce((s, t) => s + getAmt(t), 0);
    const totalAssetDebt = assetTxns.filter(t => isDebtPayment(t)).reduce((s, t) => s + getAmt(t), 0);
    const totalAssetNonDebt = totalAsset - totalAssetDebt;
    const totalStandalone = filtered.filter(t => t._series?.series_type === 'standalone').reduce((s, t) => s + getAmt(t), 0);
    const total = totalAsset + totalStandalone;

    // Per-series breakdown for tooltip top-5
    // bucket = dataset label; series name = _series.display_name
    const breakdownMap = {}; // period -> bucket -> { seriesName -> amount }
    const addToBreakdown = (period, bucket, series, amt) => {
      if (!breakdownMap[period]) breakdownMap[period] = {};
      if (!breakdownMap[period][bucket]) breakdownMap[period][bucket] = {};
      const key = series.display_name || 'Unknown';
      if (!breakdownMap[period][bucket][key]) {
        breakdownMap[period][bucket][key] = { amount: 0, id: series.id, series_type: series.series_type };
      }
      breakdownMap[period][bucket][key].amount += amt;
    };
    // Standalone breakdown
    filtered.filter(t => t._series?.series_type === 'standalone').forEach(t => {
      const d = t.payment_date || t.transaction_date || t.date || '';
      const k = getPeriodKey(d.slice(0, 10));
      if (k) addToBreakdown(k, 'Non-Asset (Standalone)', t._series || { display_name: 'Unknown' }, getAmt(t));
    });
    // Asset breakdown
    assetTxns.forEach(t => {
      const d = t.payment_date || t.transaction_date || t.date || '';
      const k = getPeriodKey(d.slice(0, 10));
      if (!k) return;
      let bucket;
      if (showByAsset) {
        bucket = t._series?.asset_description || t._series?.display_name || 'Asset';
      } else if (separateDebt) {
        bucket = isDebtPayment(t) ? 'Asset-Linked · Debt Payments' : 'Asset-Linked · Non-Debt';
      } else {
        bucket = 'Asset-Linked';
      }
      addToBreakdown(k, bucket, t._series || { display_name: 'Unknown' }, getAmt(t));
    });
    seriesBreakdownRef.current = breakdownMap;

    // Helper: compute avg and stddev for an array of per-period values
    const calcStats = (periodValues) => {
      const n = periodValues.length;
      if (n === 0) return { avg: 0, stdDev: 0 };
      const avg = periodValues.reduce((s, v) => s + v, 0) / n;
      const variance = periodValues.reduce((s, v) => s + Math.pow(v - avg, 2), 0) / n;
      return { avg, stdDev: Math.sqrt(variance) };
    };

    const numPeriods = labels.length;

    // Per-period totals for each bucket
    const assetPeriodTotals = labels.map(l => {
      return assetTxns.filter(t => {
        const d = t.payment_date || t.transaction_date || t.date || '';
        return getPeriodKey(d.slice(0, 10)) === l;
      }).reduce((s, t) => s + getAmt(t), 0);
    });
    const assetDebtPeriodTotals = labels.map(l => {
      return assetTxns.filter(t => {
        const d = t.payment_date || t.transaction_date || t.date || '';
        return isDebtPayment(t) && getPeriodKey(d.slice(0, 10)) === l;
      }).reduce((s, t) => s + getAmt(t), 0);
    });
    const assetNonDebtPeriodTotals = labels.map(l => {
      return assetTxns.filter(t => {
        const d = t.payment_date || t.transaction_date || t.date || '';
        return !isDebtPayment(t) && getPeriodKey(d.slice(0, 10)) === l;
      }).reduce((s, t) => s + getAmt(t), 0);
    });
    const standalonePeriodTotals = labels.map(l => standalonePeriod[l] || 0);
    const totalPeriodTotals = labels.map((l, i) => assetPeriodTotals[i] + standalonePeriodTotals[i]);

    const statsTotal = calcStats(totalPeriodTotals);
    const statsAsset = calcStats(assetPeriodTotals);
    const statsAssetDebt = calcStats(assetDebtPeriodTotals);
    const statsAssetNonDebt = calcStats(assetNonDebtPeriodTotals);
    const statsStandalone = calcStats(standalonePeriodTotals);

    return {
      chartLabels: labels,
      assetDatasets: assetDatasetList,
      standaloneDataset: { values: standaloneValues },
      assetNames: assetNameList,
      metrics: {
        total, totalAsset, totalAssetDebt, totalAssetNonDebt, totalStandalone,
        numPeriods,
        statsTotal, statsAsset, statsAssetDebt, statsAssetNonDebt, statsStandalone,
      },
    };
  }, [txData, startDate, endDate, grouping, separateDebt, isCumulative, showByAsset]);

  // Render chart
  React.useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    if (chartLabels.length === 0) return;

    const standaloneColor = '#86efac';
    const useStacked = chartType === 'bar' && isStacked;
    const datasets = [
      ...assetDatasets.map(ds => ({
        label: ds.name,
        data: ds.values,
        backgroundColor: chartType === 'bar' ? ds.color + 'cc' : ds.color,
        borderColor: ds.color,
        borderWidth: chartType === 'line' ? 2 : 0,
        fill: false,
        tension: 0.3,
        type: chartType,
      })),
      {
        label: 'Non-Asset (Standalone)',
        data: standaloneDataset.values,
        backgroundColor: chartType === 'bar' ? standaloneColor + 'cc' : standaloneColor,
        borderColor: standaloneColor,
        borderWidth: chartType === 'line' ? 2 : 0,
        fill: false,
        tension: 0.3,
        type: chartType,
      },
    ];

    // Custom HTML tooltip handler
    const externalTooltipHandler = (context) => {
      const { chart, tooltip } = context;
      const tooltipEl = tooltipDivRef.current;
      if (!tooltipEl) return;

      if (tooltip.opacity === 0) {
        // Delay hiding so user can move mouse onto tooltip to click links
        tooltipHideTimerRef.current = setTimeout(() => {
          if (tooltipEl) {
            tooltipEl.style.opacity = '0';
            tooltipEl.style.pointerEvents = 'none';
          }
        }, 600);
        return;
      }
      // Cancel any pending hide if tooltip is being shown again
      if (tooltipHideTimerRef.current) {
        clearTimeout(tooltipHideTimerRef.current);
        tooltipHideTimerRef.current = null;
      }

      const periodLabel = tooltip.title?.[0] || '';
      const items = tooltip.dataPoints || [];
      const periodTotal = items.reduce((sum, item) => sum + (item.parsed.y || 0), 0);
      const fmtAmt = v => '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const fmtPct = (v, total) => total > 0 ? `(${((v / total) * 100).toFixed(1)}%)` : '';
      const breakdown = seriesBreakdownRef.current[periodLabel] || {};

      let html = `<div style="font-family:monospace;font-size:12px;color:#e5e7eb;margin-bottom:6px;font-weight:600">${periodLabel}</div>`;
      html += `<div style="font-family:monospace;font-size:12px;color:#9ca3af;margin-bottom:4px">Period Total: <span style="float:right;margin-left:16px">${fmtAmt(periodTotal)} <span style="color:#6b7280">(100.0%)</span></span></div>`;
      html += `<div style="border-top:1px solid #374151;margin:4px 0"></div>`;

      items.forEach(item => {
        const val = item.parsed.y || 0;
        const lbl = item.dataset.label || '';
        const color = item.dataset.borderColor || '#9ca3af';
        const pctStr = fmtPct(val, periodTotal);
        html += `<div style="margin-top:6px">`;
        html += `<div style="font-family:monospace;font-size:12px"><span style="display:inline-block;width:10px;height:10px;background:${color};border-radius:2px;margin-right:4px;vertical-align:middle"></span><span style="color:#e5e7eb">${lbl}</span><span style="float:right;margin-left:16px;color:#d1d5db">${fmtAmt(val)} <span style="color:#6b7280">${pctStr}</span></span></div>`;
        // Sub-series links
        const seriesMap = breakdown[lbl] || {};
        const sorted = Object.entries(seriesMap).sort((a, b) => b[1].amount - a[1].amount).slice(0, 5);
        sorted.forEach(([name, info]) => {
          const url = `/income_statement/expenses?view=recurring&tab=series&series_id=${info.id}&series_type=${info.series_type}`;
          const pct = fmtPct(info.amount, periodTotal);
          html += `<div style="font-family:monospace;font-size:11px;padding-left:16px;margin-top:2px"><a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#93c5fd;text-decoration:none" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">${name.length > 22 ? name.slice(0, 21) + '…' : name}</a><span style="float:right;margin-left:8px;color:#9ca3af">${fmtAmt(info.amount)} <span style="color:#6b7280">${pct}</span></span></div>`;
        });
        html += `</div>`;
      });

      tooltipEl.innerHTML = html;
      tooltipEl.style.opacity = '1';
      tooltipEl.style.pointerEvents = 'auto';

      const canvasRect = chart.canvas.getBoundingClientRect();
      // eslint-disable-next-line no-unused-vars
      const containerRect = tooltipEl.parentElement?.getBoundingClientRect() || canvasRect;
      let left = chart.canvas.offsetLeft + tooltip.caretX + 12;
      let top = chart.canvas.offsetTop + tooltip.caretY - 10;
      // Clamp so tooltip stays within container
      tooltipEl.style.left = left + 'px';
      tooltipEl.style.top = top + 'px';
    };

    chartRef.current = new Chart(canvasRef.current, {
      type: chartType,
      data: { labels: chartLabels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        clip: false,
        plugins: {
          legend: { labels: { color: '#9ca3af', font: { size: 12 } } },
          tooltip: {
            enabled: false,
            mode: 'index',
            intersect: false,
            external: externalTooltipHandler,
          },
        },
        scales: {
          x: { stacked: useStacked, ticks: { color: '#6b7280', maxRotation: 45 }, grid: { color: '#1f2937' } },
          y: { stacked: useStacked, ticks: { color: '#6b7280', callback: v => '$' + v.toLocaleString() }, grid: { color: '#1f2937' } },
        },
      },
    });
    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [chartLabels, assetDatasets, standaloneDataset, chartType, isStacked]);

  const pct = (v, total) => total > 0 ? ((v / total) * 100).toFixed(1) + '%' : '—';
  const fmtC = v => '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const quickBtns = [
    { key: 'curr_month', label: 'Curr. Month' },
    { key: '1m', label: '1M' },
    { key: '3m', label: '3M' },
    { key: '6m', label: '6M' },
    { key: 'ytd', label: 'YTD' },
    { key: '1y', label: '1Y' },
    { key: 'all', label: 'ALL' },
  ];

  return (
    <div style={{ marginTop: 8 }}>
      {/* Controls row */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:12, alignItems:'center', marginBottom:16, background:'#0b1220', border:'1px solid #374151', borderRadius:8, padding:'12px 16px' }}>
        {/* Quick period buttons */}
        <div style={{ display:'flex', gap:4 }}>
          {quickBtns.map(b => (
            <button key={b.key} onClick={() => applyQuickPeriod(b.key)}
              style={{ padding:'3px 10px', fontSize:'0.78rem', borderRadius:4, border:'1px solid #374151', background: activePeriod === b.key ? '#1e3a5f' : '#111827', color: activePeriod === b.key ? '#93c5fd' : '#9ca3af', cursor:'pointer', fontWeight: activePeriod === b.key ? 600 : 400 }}>
              {b.label}
            </button>
          ))}
        </div>
        {/* Date pickers */}
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          <label style={{ color:'#6b7280', fontSize:'0.8rem' }}>From</label>
          <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setActivePeriod(null); }}
            style={{ background:'#111827', border:'1px solid #374151', borderRadius:4, color:'#e5e7eb', padding:'2px 6px', fontSize:'0.8rem' }} />
          <label style={{ color:'#6b7280', fontSize:'0.8rem' }}>To</label>
          <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setActivePeriod(null); }}
            style={{ background:'#111827', border:'1px solid #374151', borderRadius:4, color:'#e5e7eb', padding:'2px 6px', fontSize:'0.8rem' }} />
        </div>
        {/* Grouping */}
        <div style={{ display:'flex', gap:4 }}>
          {['daily','weekly','monthly','quarterly','annual'].map(g => (
            <button key={g} onClick={() => setGrouping(g)}
              style={{ padding:'3px 10px', fontSize:'0.78rem', borderRadius:4, border:'1px solid #374151', background: grouping === g ? '#1e3a5f' : '#111827', color: grouping === g ? '#93c5fd' : '#9ca3af', cursor:'pointer', textTransform:'capitalize' }}>
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
        {/* Toggles */}
        <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
          <label style={{ display:'flex', alignItems:'center', gap:5, color:'#9ca3af', fontSize:'0.82rem', cursor:'pointer' }}>
            <input type="checkbox" checked={separateDebt} onChange={e => setSeparateDebt(e.target.checked)} />
            Separate Debt Payments
          </label>
          <label style={{ display:'flex', alignItems:'center', gap:5, color:'#9ca3af', fontSize:'0.82rem', cursor:'pointer' }}>
            <input type="checkbox" checked={isCumulative} onChange={e => setIsCumulative(e.target.checked)} />
            Cumulative
          </label>
          <label style={{ display:'flex', alignItems:'center', gap:5, color:'#9ca3af', fontSize:'0.82rem', cursor:'pointer' }}>
            <input type="checkbox" checked={showByAsset} onChange={e => setShowByAsset(e.target.checked)} />
            Show by Asset
          </label>
          {/* Chart type toggle */}
          <div className="btn-group btn-group-sm" role="group">
            <button className={`btn ${chartType === 'bar' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setChartType('bar')} style={{ fontSize:'0.78rem', padding:'2px 10px' }}>Bar</button>
            <button className={`btn ${chartType === 'line' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setChartType('line')} style={{ fontSize:'0.78rem', padding:'2px 10px' }}>Line</button>
          </div>
          {chartType === 'bar' && (
            <label style={{ display:'flex', alignItems:'center', gap:5, color:'#9ca3af', fontSize:'0.82rem', cursor:'pointer' }}>
              <input type="checkbox" checked={isStacked} onChange={e => setIsStacked(e.target.checked)} />
              Stacked
            </label>
          )}
        </div>
      </div>

      {/* Metrics cards */}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
        {[
          { label: 'Total Spend', value: fmtC(metrics.total), sub: null, color: '#e5e7eb', stats: metrics.statsTotal },
          { label: 'Asset-Linked', value: fmtC(metrics.totalAsset), sub: pct(metrics.totalAsset, metrics.total) + ' of total', color: '#93c5fd', stats: metrics.statsAsset },
          ...(separateDebt ? [
            { label: 'Asset · Debt Payments', value: fmtC(metrics.totalAssetDebt), sub: pct(metrics.totalAssetDebt, metrics.total) + ' of total', color: '#f87171', stats: metrics.statsAssetDebt },
            { label: 'Asset · Non-Debt', value: fmtC(metrics.totalAssetNonDebt), sub: pct(metrics.totalAssetNonDebt, metrics.total) + ' of total', color: '#60a5fa', stats: metrics.statsAssetNonDebt },
          ] : []),
          { label: 'Non-Asset (Standalone)', value: fmtC(metrics.totalStandalone), sub: pct(metrics.totalStandalone, metrics.total) + ' of total', color: '#86efac', stats: metrics.statsStandalone },
        ].map(card => (
          <div key={card.label} style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, padding:'10px 18px', minWidth:180 }}>
            <div style={{ fontSize:'0.7rem', color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>{card.label}</div>
            <div style={{ fontSize:'1.15rem', fontWeight:700, color: card.color, fontVariantNumeric:'tabular-nums' }}>{card.value}</div>
            {card.sub && <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:2 }}>{card.sub}</div>}
            {card.stats && (
              <div style={{ marginTop:6, borderTop:'1px solid #1f2937', paddingTop:5, display:'flex', flexDirection:'column', gap:2 }}>
                <div style={{ display:'flex', justifyContent:'space-between', gap:12, fontSize:'0.7rem', color:'#6b7280' }}>
                  <span>Avg / {grouping.charAt(0).toUpperCase() + grouping.slice(1)}</span>
                  <span style={{ color:'#fbbf24', fontVariantNumeric:'tabular-nums' }}>{fmtC(card.stats.avg)}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', gap:12, fontSize:'0.7rem', color:'#6b7280' }}>
                  <span>Std Dev</span>
                  <span style={{ color:'#a78bfa', fontVariantNumeric:'tabular-nums' }}>{fmtC(card.stats.stdDev)}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, padding:'16px', position:'relative', overflow:'visible', paddingBottom: 240 }}>
        {loadingTx && (
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(11,18,32,0.7)', borderRadius:8, zIndex:2 }}>
            <span style={{ color:'#9ca3af' }}>Loading transactions…</span>
          </div>
        )}
        {!loadingTx && chartLabels.length === 0 && (
          <div style={{ textAlign:'center', color:'#6b7280', padding:40 }}>No data for selected period.</div>
        )}
        <div style={{ height: 380, position: 'relative' }}>
          <canvas ref={canvasRef} />
          <div ref={tooltipDivRef}
            onMouseEnter={() => {
              // Cancel hide timer when mouse enters tooltip
              if (tooltipHideTimerRef.current) {
                clearTimeout(tooltipHideTimerRef.current);
                tooltipHideTimerRef.current = null;
              }
            }}
            onMouseLeave={() => {
              // Hide tooltip when mouse leaves it
              const tooltipEl = tooltipDivRef.current;
              if (tooltipEl) {
                tooltipEl.style.opacity = '0';
                tooltipEl.style.pointerEvents = 'none';
              }
            }}
            style={{
            position: 'absolute', opacity: 0, pointerEvents: 'none',
            background: '#1f2937', border: '1px solid #374151', borderRadius: 6,
            padding: '10px 14px', minWidth: 280, maxWidth: 380,
            zIndex: 9999, transition: 'opacity 0.15s',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          }} />
        </div>
      </div>
    </div>
  );
}

// ─── SeriesInspectPopup ─────────────────────────────────────────────────────
// Read-only popup showing transactions for a given series (standalone only).
// Shown when user clicks a series name link inside TransactionSearchModal.
function SeriesInspectPopup({ seriesId, seriesName, onClose }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [sortField, setSortField] = React.useState('transaction_date');
  const [sortDir, setSortDir] = React.useState('desc');

  React.useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      try {
        const [cashRes, rvRes] = await Promise.allSettled([
          axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { standalone_recurring_series_id: seriesId } }),
          axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { standalone_recurring_series_id: seriesId } }),
        ]);
        if (cancelled) return;
        const items = [];
        if (cashRes.status === 'fulfilled') {
          const d = cashRes.value?.data;
          (Array.isArray(d) ? d : (d?.transactions || [])).forEach(t => items.push({ ...t, _src: 'cash' }));
        }
        if (rvRes.status === 'fulfilled') {
          const d = rvRes.value?.data;
          (Array.isArray(d) ? d : (d?.transactions || [])).forEach(t => items.push({ ...t, _src: 'revolver' }));
        }
        setRows(items);
      } catch (e) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [seriesId]);

  const fmtC = v => '$' + Math.abs(Number(v || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const sorted = React.useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let av = a[sortField] ?? '';
      let bv = b[sortField] ?? '';
      if (sortField === 'amount') { av = Number(av) || 0; bv = Number(bv) || 0; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ color:'#4b5563', marginLeft:4 }}>⇅</span>;
    return <span style={{ color:'#93c5fd', marginLeft:4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const thStyle = (field) => ({
    padding:'8px', cursor:'pointer', userSelect:'none', whiteSpace:'nowrap',
    color: sortField === field ? '#93c5fd' : '#9ca3af',
  });

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.75)', zIndex:1200, display:'flex', alignItems:'flex-start', justifyContent:'center', paddingTop:60, overflowY:'auto' }}>
      <div style={{ background:'#1f2937', border:'1px solid #4b5563', borderRadius:10, padding:24, width:'85vw', maxWidth:820, marginBottom:40 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <div>
            <div style={{ color:'#9ca3af', fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.05em' }}>Series Transactions</div>
            <div style={{ color:'#e5e7eb', fontWeight:600, fontSize:'1rem' }}>{seriesName}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#9ca3af', fontSize:'1.4rem', cursor:'pointer', lineHeight:1 }}>✕</button>
        </div>
        <div style={{ color:'#6b7280', fontSize:'0.8rem', marginBottom:14 }}>Read-only view · Click column headers to sort</div>

        {loading && <div style={{ color:'#9ca3af', textAlign:'center', padding:'24px 0' }}>Loading…</div>}

        {!loading && sorted.length === 0 && (
          <div style={{ color:'#6b7280', textAlign:'center', padding:'24px 0' }}>No transactions found for this series.</div>
        )}

        {!loading && sorted.length > 0 && (
          <div style={{ overflowX:'auto', maxHeight:'55vh', overflowY:'auto', border:'1px solid #374151', borderRadius:8 }}>
            <table className="table table-dark table-hover mb-0" style={{ fontSize:'0.82rem' }}>
              <thead style={{ position:'sticky', top:0, background:'#111827', zIndex:1 }}>
                <tr>
                  <th style={thStyle('transaction_date')} onClick={() => toggleSort('transaction_date')}>Date <SortIcon field="transaction_date" /></th>
                  <th style={thStyle('user_description')} onClick={() => toggleSort('user_description')}>Description <SortIcon field="user_description" /></th>
                  <th style={{ ...thStyle('amount'), textAlign:'right' }} onClick={() => toggleSort('amount')}>Amount <SortIcon field="amount" /></th>
                  <th style={thStyle('expense_category')} onClick={() => toggleSort('expense_category')}>Category <SortIcon field="expense_category" /></th>
                  <th style={thStyle('_src')} onClick={() => toggleSort('_src')}>Source <SortIcon field="_src" /></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => {
                  const date = r.transaction_date || r.date || '';
                  const desc = r.user_description || r.description || '';
                  const cat = r.expense_category || r.category || '';
                  const srcName = r.source_account_display || (r.lender_name ? `${r.lender_name}${r.account_last4 ? ' *'+r.account_last4 : ''}` : null) || (r._src === 'cash' ? 'Cash' : 'Credit Card');
                  let srcUrl = '';
                  if (r._src === 'cash' && r.account) srcUrl = `/balance_sheet/assets/cash_assets/${r.account}`;
                  else if (r._src === 'revolver' && r.revolver) srcUrl = r.revolver_type === 'heloc' ? `/balance_sheet/financing/revolvers/helocs/${r.revolver}` : `/balance_sheet/financing/revolvers/credit-cards/${r.revolver}`;
                  return (
                    <tr key={i}>
                      <td style={{ padding:'8px', whiteSpace:'nowrap' }}>{date}</td>
                      <td style={{ padding:'8px' }}>{desc}</td>
                      <td style={{ padding:'8px', textAlign:'right', color:'#e5e7eb' }}>{fmtC(r.amount)}</td>
                      <td style={{ padding:'8px', color:'#9ca3af' }}>{cat}</td>
                      <td style={{ padding:'8px', color:'#9ca3af' }}>{srcUrl ? <a href={srcUrl} target="_blank" rel="noreferrer" style={{ color:'#93c5fd', textDecoration:'none' }} onMouseEnter={e=>e.target.style.textDecoration='underline'} onMouseLeave={e=>e.target.style.textDecoration='none'}>{srcName}</a> : srcName}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}>
          <button className="btn btn-sm btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── TransactionSearchModal ─────────────────────────────────────────────────
// Self-contained modal for searching transactions and adding them to a series.
// Props:
//   seriesId       – id of the target series
//   seriesType     – 'standalone' | 'asset'
//   seriesName     – display name (for title)
//   categoryChoices – { items, noneItem }
//   onClose        – called when modal is dismissed
//   onAdded        – called after transactions are successfully linked
function TransactionSearchModal({ seriesId, seriesType, seriesName, categoryChoices, onClose, onAdded }) {
  const [q, setQ] = React.useState('');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [minAmount, setMinAmount] = React.useState('');
  const [maxAmount, setMaxAmount] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState(new Set());
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [conflictCategories, setConflictCategories] = React.useState([]);
  const [resolvedCategory, setResolvedCategory] = React.useState('');
  const [inspectSeries, setInspectSeries] = React.useState(null); // { id, name }

  const fmtC = v => '$' + Math.abs(Number(v || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    setResults([]);
    setSelected(new Set());
    try {
      const params = {
        q,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        min_amount: minAmount || undefined,
        max_amount: maxAmount || undefined,
        category: category || undefined,
        // exclude_with_series omitted — show all transactions including already-linked ones
      };
      const res = await axios.get('/api/balance_sheet/transactions/search/', { params });
      const processed = (res.data || []).map(t => {
        const amt = parseFloat(t.amount || 0);
        const isCash = t.transaction_type === 'cash';
        const isCC = t.transaction_type === 'revolver';
        let isRefund = false;
        if (isCash && amt > 0) isRefund = true;
        if (isCC && amt < 0) isRefund = true;
        return {
          ...t,
          sourceType: t.transaction_type,
          source: t.source_account_display || (t.lender_name ? `${t.lender_name}${t.account_last4 ? ' *'+t.account_last4 : ''}` : '') || '',
          amount: isRefund ? -Math.abs(amt) : Math.abs(amt),
        };
      });
      setResults(processed);
    } catch (e) {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAll = (e) => {
    if (e.target.checked) setSelected(new Set(results.map(r => `${r.sourceType}-${r.id}`)));
    else setSelected(new Set());
  };

  const executeLinkCalls = async (resolvedCat) => {
    const cashIds = [];
    const revolverIds = [];
    for (const key of selected) {
      const [type, id] = key.split('-');
      if (type === 'cash') cashIds.push(Number(id));
      else revolverIds.push(Number(id));
    }
    const payload = (ids, accountType) => ({
      series_id: seriesId,
      transaction_ids: ids,
      account_type: accountType,
      ...(resolvedCat ? { resolved_category: resolvedCat, force: true } : {}),
    });
    if (cashIds.length > 0) {
      await axios.post('/api/balance_sheet/assets/standalone-recurring-series/bulk-link/', payload(cashIds, 'cash'));
    }
    if (revolverIds.length > 0) {
      await axios.post('/api/balance_sheet/assets/standalone-recurring-series/bulk-link/', payload(revolverIds, 'revolver'));
    }
  };

  const handleAdd = async () => {
    if (selected.size === 0) { setError('Please select at least one transaction.'); return; }
    setSaving(true);
    setError('');
    try {
      await executeLinkCalls(null);
      onAdded && onAdded();
      onClose();
    } catch (e) {
      const conflictData = e?.response?.data || e?.data;
      if (conflictData?.conflict) {
        setConflictCategories(conflictData.categories || []);
        setResolvedCategory(conflictData.categories?.[0] || '');
      } else {
        setError('Failed to link transactions. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConflictConfirm = async () => {
    if (!resolvedCategory) { setError('Please select a category.'); return; }
    setSaving(true);
    setError('');
    try {
      await executeLinkCalls(resolvedCategory);
      setConflictCategories([]);
      onAdded && onAdded();
      onClose();
    } catch (e) {
      setError('Failed to link transactions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.7)', zIndex:1100, display:'flex', alignItems:'flex-start', justifyContent:'center', paddingTop:40, overflowY:'auto' }}>
      <div style={{ background:'#1f2937', border:'1px solid #374151', borderRadius:10, padding:24, width:'90vw', maxWidth:900, marginBottom:40 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h5 style={{ color:'#e5e7eb', margin:0 }}>Add Transactions → <span style={{ color:'#93c5fd' }}>{seriesName}</span></h5>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#9ca3af', fontSize:'1.3rem', cursor:'pointer', lineHeight:1 }}>✕</button>
        </div>

        {/* Search controls */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, alignItems:'flex-end', marginBottom:14, padding:'12px 14px', background:'#111827', borderRadius:8, border:'1px solid #374151' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
            <label style={{ color:'#6b7280', fontSize:'0.75rem' }}>Description</label>
            <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Search…"
              className="form-control form-control-sm" style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', width:180 }} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
            <label style={{ color:'#6b7280', fontSize:'0.75rem' }}>Start Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="form-control form-control-sm" style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151' }} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
            <label style={{ color:'#6b7280', fontSize:'0.75rem' }}>End Date</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="form-control form-control-sm" style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151' }} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
            <label style={{ color:'#6b7280', fontSize:'0.75rem' }}>Min $</label>
            <input type="number" step="0.01" value={minAmount} onChange={e => setMinAmount(e.target.value)}
              className="form-control form-control-sm" style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', width:90 }} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
            <label style={{ color:'#6b7280', fontSize:'0.75rem' }}>Max $</label>
            <input type="number" step="0.01" value={maxAmount} onChange={e => setMaxAmount(e.target.value)}
              className="form-control form-control-sm" style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', width:90 }} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
            <label style={{ color:'#6b7280', fontSize:'0.75rem' }}>Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="form-select form-select-sm" style={{ background:'#0b1220', color:'#e5e7eb', border:'1px solid #374151', width:160 }}>
              <option value="">Any</option>
              {Array.isArray(categoryChoices?.items) && categoryChoices.items.map(p => (
                <React.Fragment key={`p-${p.value}`}>
                  <option value={p.value}>{p.label || p.value}</option>
                  {Array.isArray(p.children) && p.children.map(ch => (
                    <option key={`c-${p.value}-${ch.value}`} value={ch.value}>{(p.label || p.value)} - {ch.label || ch.value}</option>
                  ))}
                </React.Fragment>
              ))}
              {categoryChoices?.noneItem && <option value={categoryChoices.noneItem.value}>{categoryChoices.noneItem.label}</option>}
            </select>
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleSearch} disabled={loading} style={{ alignSelf:'flex-end' }}>
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>

        {/* Conflict resolution */}
        {conflictCategories.length > 0 && (
          <div style={{ background:'#451a03', border:'1px solid #92400e', borderRadius:8, padding:'12px 16px', marginBottom:14 }}>
            <div style={{ color:'#fbbf24', fontWeight:600, marginBottom:8 }}>Category Conflict</div>
            <div style={{ color:'#e5e7eb', fontSize:'0.85rem', marginBottom:10 }}>The selected transactions have different categories. Please choose one to apply:</div>
            <select value={resolvedCategory} onChange={e => setResolvedCategory(e.target.value)}
              className="form-select form-select-sm" style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151', marginBottom:10, width:'auto' }}>
              {conflictCategories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-sm btn-warning" onClick={handleConflictConfirm} disabled={saving}>Confirm &amp; Link</button>
              <button className="btn btn-sm btn-secondary" onClick={() => setConflictCategories([])} disabled={saving}>Cancel</button>
            </div>
          </div>
        )}

        {/* Results table */}
        {results.length > 0 && (
          <div style={{ marginBottom:14 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <span style={{ color:'#9ca3af', fontSize:'0.85rem' }}>{results.length} result{results.length !== 1 ? 's' : ''} — {selected.size} selected</span>
              <span style={{ color:'#9ca3af', fontSize:'0.85rem' }}>
                Total selected: <span style={{ color:'#3b82f6', fontWeight:600 }}>
                  {fmtC(results.filter(r => selected.has(`${r.sourceType}-${r.id}`)).reduce((s, r) => s + Number(r.amount || 0), 0))}
                </span>
              </span>
            </div>
            <div style={{ overflowX:'auto', maxHeight:'40vh', overflowY:'auto', border:'1px solid #374151', borderRadius:8 }}>
              <table className="table table-dark table-hover mb-0" style={{ fontSize:'0.82rem' }}>
                <thead style={{ position:'sticky', top:0, background:'#111827', zIndex:1 }}>
                  <tr>
                    <th style={{ padding:'8px 6px', width:36 }}>
                      <input type="checkbox"
                        style={{ width:16, height:16, cursor:'pointer', accentColor:'#3b82f6' }}
                        checked={results.length > 0 && selected.size === results.length}
                        onChange={toggleAll} />
                    </th>
                    <th style={{ padding:'8px' }}>Date</th>
                    <th style={{ padding:'8px' }}>Description</th>
                    <th style={{ padding:'8px', textAlign:'right' }}>Amount</th>
                    <th style={{ padding:'8px' }}>Category</th>
                    <th style={{ padding:'8px' }}>Source</th>
                    <th style={{ padding:'8px' }}>Series</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, idx) => {
                    const key = `${r.sourceType}-${r.id}`;
                    const dateVal = r.transaction_date || r.date || '';
                    const desc = r.user_description || r.description || '';
                    const cat = r.expense_category || r.category || '';
                    return (
                      <tr key={`${key}-${idx}`} style={{ cursor:'pointer' }} onClick={() => toggleRow(key)}>
                        <td style={{ padding:'8px 6px' }}>
                          <input type="checkbox" checked={selected.has(key)} onChange={() => toggleRow(key)} onClick={e => e.stopPropagation()}
                            style={{ width:16, height:16, cursor:'pointer', accentColor:'#3b82f6' }} />
                        </td>
                        <td style={{ padding:'8px', whiteSpace:'nowrap' }}>{dateVal}</td>
                        <td style={{ padding:'8px' }}>{desc}</td>
                        <td style={{ padding:'8px', textAlign:'right', color:'#e5e7eb' }}>{fmtC(r.amount)}</td>
                        <td style={{ padding:'8px', color:'#9ca3af' }}>{cat}</td>
                        <td style={{ padding:'8px', color:'#9ca3af' }}>{(() => { const nm = r.source || (r.sourceType === 'cash' ? 'Cash' : 'Credit Card'); let url = ''; if (r.sourceType === 'cash' && r.account) url = `/balance_sheet/assets/cash_assets/${r.account}`; else if (r.sourceType === 'revolver' && r.revolver) url = r.revolver_type === 'heloc' ? `/balance_sheet/financing/revolvers/helocs/${r.revolver}` : `/balance_sheet/financing/revolvers/credit-cards/${r.revolver}`; return url ? <a href={url} target="_blank" rel="noreferrer" style={{ color:'#93c5fd', textDecoration:'none' }} onMouseEnter={e=>e.target.style.textDecoration='underline'} onMouseLeave={e=>e.target.style.textDecoration='none'}>{nm}</a> : nm; })()}</td>
                        <td style={{ padding:'8px' }}>
                          {r.standalone_recurring_series_name ? (
                            <a
                              href={`/income_statement/expenses?view=recurring&tab=series&series_id=${r.standalone_recurring_series}&series_type=standalone`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={e => e.stopPropagation()}
                              style={{ color:'#60a5fa', fontSize:'0.82rem', textDecoration:'underline' }}
                            >
                              {r.standalone_recurring_series_name}
                            </a>
                          ) : <span style={{ color:'#4b5563' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && results.length === 0 && (
          <div style={{ color:'#6b7280', textAlign:'center', padding:'24px 0', fontSize:'0.9rem' }}>
            {q || startDate || endDate || minAmount || maxAmount || category ? 'No transactions found.' : 'Use the search fields above to find transactions to add.'}
          </div>
        )}

        {error && <div style={{ color:'#f87171', fontSize:'0.85rem', marginBottom:10 }}>{error}</div>}

        {/* Series inspect popup */}
        {inspectSeries && (
          <SeriesInspectPopup
            seriesId={inspectSeries.id}
            seriesName={inspectSeries.name}
            onClose={() => setInspectSeries(null)}
          />
        )}

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
          <button className="btn btn-sm btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-sm btn-success" onClick={handleAdd} disabled={saving || selected.size === 0}>
            {saving ? 'Adding…' : `Add ${selected.size > 0 ? selected.size : ''} Transaction${selected.size !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AllRecurringSeriesPanel ────────────────────────────────────────────────
// Shown in the "Recurring" view of the main Expenses page.
// Lists all recurring series (asset-linked + standalone) with toggle filters.
function AllRecurringSeriesPanel({ allAssets, navigate, categoryChoices }) {
  // Read series_id and series_type directly from URL on every render so we always have the latest values
  const urlParams = new URLSearchParams(window.location.search);
  const initSeriesId = urlParams.get('series_id') || null;
  const initSeriesType = urlParams.get('series_type') || null;

  const initTab = urlParams.get('tab') === 'series' ? 'series' : 'summary';
  const [recurringTab, setRecurringTab] = React.useState(initTab); // 'summary' | 'series'
  const [loading, setLoading] = React.useState(false);
  const [allSeries, setAllSeries] = React.useState([]);
  const [seriesTypeTab, setSeriesTypeTab] = React.useState('all'); // 'all' | 'asset' | 'standalone'
  const [filterOpenOnly, setFilterOpenOnly] = React.useState(true); // show only open (no close_date)
  const [filterClosedOnly, setFilterClosedOnly] = React.useState(false); // show only closed (has close_date)
  const [searchQuery, setSearchQuery] = React.useState(''); // search filter for series list
  const [selected, setSelected] = React.useState(null); // { id, series_type }
  const [detail, setDetail] = React.useState([]);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailView, setDetailView] = React.useState('graph'); // 'table' | 'graph'
  const [sortField, setSortField] = React.useState('date');
  const [sortDir, setSortDir] = React.useState('desc');
  // Edit series state
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [editForm, setEditForm] = React.useState({});
  const [editSaving, setEditSaving] = React.useState(false);
  const [editError, setEditError] = React.useState('');
  const [editLinkToAsset, setEditLinkToAsset] = React.useState(false);
  const [editLinkAssetId, setEditLinkAssetId] = React.useState('');
  const [editLinkCategory, setEditLinkCategory] = React.useState('');
  const [editLinkConflict, setEditLinkConflict] = React.useState([]);
  const [editLinkResolved, setEditLinkResolved] = React.useState('');
  const [bulkPhysicalAssets, setBulkPhysicalAssets] = React.useState([]);
  // Add Transactions modal state
  const [showAddTxModal, setShowAddTxModal] = React.useState(false);
  // Table checkbox selection state
  const [tableChecked, setTableChecked] = React.useState(new Set()); // Set of row indices into sortedDetail
  // Move to series modal state
  const [showMoveModal, setShowMoveModal] = React.useState(false);
  const [moveMode, setMoveMode] = React.useState('existing'); // 'existing' | 'new'
  const [moveSeriesOptions, setMoveSeriesOptions] = React.useState([]);
  const [moveSeriesLoading, setMoveSeriesLoading] = React.useState(false);
  const [moveSelectedSeriesId, setMoveSelectedSeriesId] = React.useState('');
  const [moveNewName, setMoveNewName] = React.useState('');
  const [moveNewCategory, setMoveNewCategory] = React.useState('');
  const [moveSaving, setMoveSaving] = React.useState(false);
  const [moveError, setMoveError] = React.useState('');
  const [moveConflictCategories, setMoveConflictCategories] = React.useState([]);
  const [moveResolvedCategory, setMoveResolvedCategory] = React.useState('');
  // De-link confirmation state
  const [delinkConfirm, setDelinkConfirm] = React.useState(null); // { row, seriesName } or null
  const [delinkSaving, setDelinkSaving] = React.useState(false);
  const [delinkError, setDelinkError] = React.useState('');
  // Time period filter for detail view
  const [timePeriod, setTimePeriod] = React.useState('1Y'); // '1M'|'3M'|'6M'|'YTD'|'1Y'|'PriorYr'|'All'
  // Load all series on mount
  React.useEffect(() => {
    setLoading(true);
    axios.get('/api/balance_sheet/assets/all-recurring-series/', { params: { include_inactive: 1 } })
      .then(res => {
        const items = Array.isArray(res.data) ? res.data : [];
        setAllSeries(items);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Auto-select from URL params whenever initSeriesId or allSeries changes
  React.useEffect(() => {
    if (!initSeriesId) return;
    if (allSeries.length === 0) return;
    let found = allSeries.find(s =>
      String(s.id) === String(initSeriesId) &&
      (!initSeriesType || s.series_type === initSeriesType)
    );
    // Fallback: match by id only if type didn't match (handles mismatched series_type in URL)
    if (!found) found = allSeries.find(s => String(s.id) === String(initSeriesId));
    if (found) {
      setSelected({ id: found.id, series_type: found.series_type });
    }
  }, [allSeries, initSeriesId, initSeriesType]);

  // Load detail when selection changes
  React.useEffect(() => {
    if (!selected) { setDetail([]); return; }
    setDetailLoading(true);
    const fetchDetail = async () => {
      try {
        let items = [];
        if (selected.series_type === 'standalone') {
          // Fetch all transactions linked to this standalone series
          const [cashRes, rvRes] = await Promise.allSettled([
            axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { standalone_recurring_series_id: selected.id } }),
            axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { standalone_recurring_series_id: selected.id } }),
          ]);
          if (cashRes.status === 'fulfilled') {
            const data = cashRes.value?.data;
            const list = Array.isArray(data) ? data : (data?.transactions || []);
            list.forEach(t => items.push({ ...t, _source: 'cash' }));
          }
          if (rvRes.status === 'fulfilled') {
            const data = rvRes.value?.data;
            const list = Array.isArray(data) ? data : (data?.transactions || []);
            list.forEach(t => items.push({ ...t, _source: 'revolver' }));
          }
        } else {
          // Asset-linked: fetch from both transaction endpoints filtered by asset_recurring_series_id
          const [cashRes, rvRes] = await Promise.allSettled([
            axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { asset_recurring_series_id: selected.id } }),
            axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { asset_recurring_series_id: selected.id } }),
          ]);
          if (cashRes.status === 'fulfilled') {
            const data = cashRes.value?.data;
            const list = Array.isArray(data) ? data : (data?.transactions || []);
            list.forEach(t => items.push({ ...t, _source: 'cash' }));
          }
          if (rvRes.status === 'fulfilled') {
            const data = rvRes.value?.data;
            const list = Array.isArray(data) ? data : (data?.transactions || []);
            list.forEach(t => items.push({ ...t, _source: 'revolver' }));
          }
        }
        setDetail(items);
      } catch (e) {
        setDetail([]);
      } finally {
        setDetailLoading(false);
      }
    };
    fetchDetail();
  }, [selected, allSeries]);

  const filteredSeries = React.useMemo(() => {
    let list = allSeries;
    if (seriesTypeTab === 'asset') list = list.filter(s => s.series_type === 'asset');
    else if (seriesTypeTab === 'standalone') list = list.filter(s => s.series_type === 'standalone');
    if (filterOpenOnly) list = list.filter(s => !s.close_date);
    if (filterClosedOnly) list = list.filter(s => !!s.close_date);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(s => {
        if ((s.display_name || '').toLowerCase().includes(q)) return true;
        if ((s.asset_description || '').toLowerCase().includes(q)) return true;
        if ((s.vendor_payee || '').toLowerCase().includes(q)) return true;
        if ((s.notes || '').toLowerCase().includes(q)) return true;
        if (Array.isArray(s.tx_descriptions) && s.tx_descriptions.some(d => d.includes(q))) return true;
        return false;
      });
    }
    // Secondary sort: asset-linked A-Z first, then standalone A-Z
    list = [...list].sort((a, b) => {
      const aIsAsset = a.series_type === 'asset' ? 0 : 1;
      const bIsAsset = b.series_type === 'asset' ? 0 : 1;
      if (aIsAsset !== bIsAsset) return aIsAsset - bIsAsset;
      return (a.display_name || '').localeCompare(b.display_name || '');
    });
    return list;
  }, [allSeries, seriesTypeTab, filterOpenOnly, filterClosedOnly, searchQuery]);

  const selectedObj = selected ? allSeries.find(s => s.id === selected.id && s.series_type === selected.series_type) : null;

  // Clear checkboxes when selection or view changes
  React.useEffect(() => { setTableChecked(new Set()); }, [selected, detailView]);

  const handleTableCheckAll = (sortedRows) => {
    if (tableChecked.size === sortedRows.length) {
      setTableChecked(new Set());
    } else {
      setTableChecked(new Set(sortedRows.map((_, i) => i)));
    }
  };

  const handleTableCheckRow = (idx) => {
    setTableChecked(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const handleRemoveFromSeries = async () => {
    if (tableChecked.size === 0) return;
    const rows = sortedDetail.filter((_, i) => tableChecked.has(i));
    const cashIds = rows.filter(r => r._source === 'cash').map(r => r.id);
    const revolverIds = rows.filter(r => r._source === 'revolver').map(r => r.id);
    try {
      if (cashIds.length) await axios.post('/api/balance_sheet/assets/standalone-recurring-series/bulk-unlink/', { transaction_ids: cashIds, account_type: 'cash' });
      if (revolverIds.length) await axios.post('/api/balance_sheet/assets/standalone-recurring-series/bulk-unlink/', { transaction_ids: revolverIds, account_type: 'revolver' });
      setTableChecked(new Set());
      setSelected(s => s ? { ...s } : s); // refresh detail
    } catch (e) {
      alert('Failed to remove transactions. Please try again.');
    }
  };

  const openMoveModal = () => {
    setMoveMode('existing');
    setMoveSelectedSeriesId('');
    setMoveNewName('');
    setMoveNewCategory('');
    setMoveError('');
    setMoveConflictCategories([]);
    setMoveResolvedCategory('');
    setMoveSeriesLoading(true);
    axios.get('/api/balance_sheet/assets/all-recurring-series/')
      .then(res => {
        const all = Array.isArray(res.data) ? res.data : [];
        // Exclude the currently selected series from the list
        setMoveSeriesOptions(all.filter(s => !(s.series_type === (selected?.series_type || 'standalone') && s.id === selected?.id)));
      })
      .catch(() => setMoveSeriesOptions([]))
      .finally(() => setMoveSeriesLoading(false));
    setShowMoveModal(true);
  };

  const executeMoveLink = async (targetSeriesKey, resolvedCategory) => {
    // targetSeriesKey may be 'standalone__ID' or 'asset__ID' or a plain number (new standalone)
    let seriesType = 'standalone';
    let seriesId;
    if (typeof targetSeriesKey === 'string' && targetSeriesKey.includes('__')) {
      const parts = targetSeriesKey.split('__');
      seriesType = parts[0];
      seriesId = Number(parts[1]);
    } else {
      seriesId = Number(targetSeriesKey);
    }
    const endpoint = seriesType === 'asset'
      ? '/api/balance_sheet/assets/asset-recurring-series/bulk-link/'
      : '/api/balance_sheet/assets/standalone-recurring-series/bulk-link/';
    const rows = sortedDetail.filter((_, i) => tableChecked.has(i));
    const cashIds = rows.filter(r => r._source === 'cash').map(r => r.id);
    const revolverIds = rows.filter(r => r._source === 'revolver').map(r => r.id);
    const payload = (ids, acctType) => ({
      series_id: seriesId,
      transaction_ids: ids,
      account_type: acctType,
      ...(resolvedCategory ? { resolved_category: resolvedCategory, force: true } : {}),
    });
    if (cashIds.length) await axios.post(endpoint, payload(cashIds, 'cash'));
    if (revolverIds.length) await axios.post(endpoint, payload(revolverIds, 'revolver'));
  };

  const handleMoveSave = async () => {
    setMoveError('');
    setMoveSaving(true);
    try {
      const rows = sortedDetail.filter((_, i) => tableChecked.has(i));
      const cashIds = rows.filter(r => r._source === 'cash').map(r => r.id);
      const revolverIds = rows.filter(r => r._source === 'revolver').map(r => r.id);
      // First unlink from current series
      if (cashIds.length) await axios.post('/api/balance_sheet/assets/standalone-recurring-series/bulk-unlink/', { transaction_ids: cashIds, account_type: 'cash' });
      if (revolverIds.length) await axios.post('/api/balance_sheet/assets/standalone-recurring-series/bulk-unlink/', { transaction_ids: revolverIds, account_type: 'revolver' });
      let targetSeriesId;
      if (moveMode === 'existing') {
        if (!moveSelectedSeriesId) { setMoveError('Please select a series.'); setMoveSaving(false); return; }
        targetSeriesId = moveSelectedSeriesId; // may be 'standalone__ID' or 'asset__ID'
      } else {
        if (!moveNewName.trim()) { setMoveError('Please enter a series name.'); setMoveSaving(false); return; }
        const res = await axios.post('/api/balance_sheet/assets/standalone-recurring-series/', { name: moveNewName.trim(), expense_category: moveNewCategory || '' });
        targetSeriesId = res.data.id;
      }
      await executeMoveLink(targetSeriesId, null);
      setShowMoveModal(false);
      setTableChecked(new Set());
      setSelected(s => s ? { ...s } : s);
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.conflict) {
        setMoveConflictCategories(err.response.data.categories || []);
        setMoveError(err.response.data.message || 'Category conflict.');
      } else {
        setMoveError('Failed to move transactions. Please try again.');
      }
    } finally {
      setMoveSaving(false);
    }
  };

  const handleMoveConflictResolve = async () => {
    if (!moveResolvedCategory) { setMoveError('Please select a category.'); return; }
    setMoveError('');
    setMoveSaving(true);
    try {
      await executeMoveLink(moveSelectedSeriesId, moveResolvedCategory);
      setShowMoveModal(false);
      setTableChecked(new Set());
      setMoveConflictCategories([]);
      setSelected(s => s ? { ...s } : s);
    } catch (e) {
      setMoveError('Failed to resolve conflict. Please try again.');
    } finally {
      setMoveSaving(false);
    }
  };

  const handleDelink = async () => {
    if (!delinkConfirm) return;
    setDelinkSaving(true);
    setDelinkError('');
    const { row } = delinkConfirm;
    const source = row._source || (row.source === 'cash' ? 'cash' : row.source === 'revolver' ? 'revolver' : row._source);
    const seriesType = selectedObj?.series_type || 'asset';
    try {
      await axios.post('/api/balance_sheet/assets/recurring-series/delink-transaction/', {
        transaction_id: row.id,
        source: source,
        series_type: seriesType,
      });
      setDetail(prev => prev.filter(r => !(r.id === row.id && (r._source || r.source) === (row._source || row.source))));
      setDelinkConfirm(null);
    } catch (e) {
      setDelinkError(e?.response?.data?.detail || 'Failed to de-link transaction. Please try again.');
    } finally {
      setDelinkSaving(false);
    }
  };

  // Filter detail rows by selected time period
  const filteredDetail = React.useMemo(() => {
    const now = new Date();
    let start, end;
    if (timePeriod === 'All') {
      return detail;
    } else if (timePeriod === '1M') {
      start = new Date(now); start.setMonth(start.getMonth() - 1);
    } else if (timePeriod === '3M') {
      start = new Date(now); start.setMonth(start.getMonth() - 3);
    } else if (timePeriod === '6M') {
      start = new Date(now); start.setMonth(start.getMonth() - 6);
    } else if (timePeriod === 'YTD') {
      start = new Date(now.getFullYear(), 0, 1);
    } else if (timePeriod === '1Y') {
      start = new Date(now); start.setFullYear(start.getFullYear() - 1);
    } else if (timePeriod === 'PriorYr') {
      const yr = now.getFullYear() - 1;
      start = new Date(yr, 0, 1);
      end = new Date(yr, 11, 31, 23, 59, 59);
    }
    return detail.filter(row => {
      const d = row.payment_date || row.transaction_date || row.date || '';
      if (!d) return true;
      const dt = new Date(d);
      if (start && dt < start) return false;
      if (end && dt > end) return false;
      return true;
    });
  }, [detail, timePeriod]);

  const sortedDetail = React.useMemo(() => {
    const rows = [...filteredDetail];
    rows.sort((a, b) => {
      let av = a[sortField] ?? '';
      let bv = b[sortField] ?? '';
      if (sortField === 'amount' || sortField === 'payment_amount') {
        av = Number(av) || 0; bv = Number(bv) || 0;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [filteredDetail, sortField, sortDir]);

  // Derive period grouping from series frequency
  const freqLower = (selectedObj?.recurring_frequency || '').toLowerCase();
  const isAnnual = freqLower.includes('annual') || freqLower.includes('yearly') || freqLower === 'year';
  const isQuarterly = freqLower.includes('quarter');
  const isSemiAnnual = freqLower.includes('semi') || freqLower.includes('biannual') || freqLower.includes('bi-annual');
  const periodLabel = isAnnual ? 'Years' : isQuarterly ? 'Quarters' : isSemiAnnual ? 'Periods' : 'Months';
  const avgLabel = isAnnual ? 'Avg./Yr' : isQuarterly ? 'Avg./Qtr' : isSemiAnnual ? 'Avg./Period' : 'Avg./Mo';

  const detailMetrics = React.useMemo(() => {
    const now = new Date();
    const getPeriodKey = (dateStr) => {
      if (!dateStr) return '';
      if (isAnnual) return dateStr.slice(0, 4);
      if (isQuarterly) {
        const m = parseInt(dateStr.slice(5, 7), 10);
        const q = Math.ceil(m / 3);
        return `${dateStr.slice(0, 4)}-Q${q}`;
      }
      if (isSemiAnnual) {
        const m = parseInt(dateStr.slice(5, 7), 10);
        const h = m <= 6 ? 'H1' : 'H2';
        return `${dateStr.slice(0, 4)}-${h}`;
      }
      return dateStr.slice(0, 7); // YYYY-MM
    };

    // Determine range start/end from timePeriod (mirrors filteredDetail logic)
    let rangeStart, rangeEnd;
    if (timePeriod === 'All') {
      // no range constraint — allPeriodKeys stays empty, data keys used directly
    } else if (timePeriod === '1M') {
      rangeStart = new Date(now); rangeStart.setMonth(rangeStart.getMonth() - 1);
      rangeEnd = now;
    } else if (timePeriod === '3M') {
      rangeStart = new Date(now); rangeStart.setMonth(rangeStart.getMonth() - 3);
      rangeEnd = now;
    } else if (timePeriod === '6M') {
      rangeStart = new Date(now); rangeStart.setMonth(rangeStart.getMonth() - 6);
      rangeEnd = now;
    } else if (timePeriod === 'YTD') {
      rangeStart = new Date(now.getFullYear(), 0, 1);
      rangeEnd = now;
    } else if (timePeriod === '1Y') {
      rangeStart = new Date(now); rangeStart.setFullYear(rangeStart.getFullYear() - 1);
      rangeEnd = now;
    } else if (timePeriod === 'PriorYr') {
      const yr = now.getFullYear() - 1;
      rangeStart = new Date(yr, 0, 1);
      rangeEnd = new Date(yr, 11, 31);
    }

    // Build all expected period keys in the range
    const allPeriodKeys = [];
    if (rangeStart && rangeEnd) {
      if (isAnnual) {
        for (let y = rangeStart.getFullYear(); y <= rangeEnd.getFullYear(); y++) {
          allPeriodKeys.push(String(y));
        }
      } else if (isQuarterly) {
        let y = rangeStart.getFullYear();
        let q = Math.ceil((rangeStart.getMonth() + 1) / 3);
        const endY = rangeEnd.getFullYear();
        const endQ = Math.ceil((rangeEnd.getMonth() + 1) / 3);
        while (y < endY || (y === endY && q <= endQ)) {
          allPeriodKeys.push(`${y}-Q${q}`);
          q++;
          if (q > 4) { q = 1; y++; }
        }
      } else if (isSemiAnnual) {
        let y = rangeStart.getFullYear();
        let h = rangeStart.getMonth() < 6 ? 1 : 2;
        const endY = rangeEnd.getFullYear();
        const endH = rangeEnd.getMonth() < 6 ? 1 : 2;
        while (y < endY || (y === endY && h <= endH)) {
          allPeriodKeys.push(`${y}-H${h}`);
          h++;
          if (h > 2) { h = 1; y++; }
        }
      } else {
        // Monthly
        let y = rangeStart.getFullYear();
        let mo = rangeStart.getMonth();
        const endY = rangeEnd.getFullYear();
        const endMo = rangeEnd.getMonth();
        while (y < endY || (y === endY && mo <= endMo)) {
          allPeriodKeys.push(`${y}-${String(mo + 1).padStart(2, '0')}`);
          mo++;
          if (mo > 11) { mo = 0; y++; }
        }
      }
    }

    // Accumulate actual data
    const byPeriod = {};
    sortedDetail.forEach(row => {
      const d = row.payment_date || row.transaction_date || row.date || '';
      const key = getPeriodKey(d);
      if (!key) return;
      byPeriod[key] = (byPeriod[key] || 0) + Math.abs(Number(row.amount || row.payment_amount || 0));
    });

    // Merge: use allPeriodKeys if available, else fall back to keys from data
    const orderedKeys = allPeriodKeys.length > 0
      ? allPeriodKeys
      : Object.keys(byPeriod).sort();
    // Ensure any data keys not in range are still included
    Object.keys(byPeriod).forEach(k => { if (!orderedKeys.includes(k)) orderedKeys.push(k); });
    orderedKeys.sort();

    const orderedAmounts = orderedKeys.map(k => byPeriod[k] || 0);
    const nonZeroAmounts = orderedAmounts.filter(v => v > 0);
    const total = orderedAmounts.reduce((s, v) => s + v, 0);
    const avg = nonZeroAmounts.length ? total / nonZeroAmounts.length : 0;
    const stdDev = nonZeroAmounts.length > 1
      ? Math.sqrt(nonZeroAmounts.reduce((s, v) => s + Math.pow(v - avg, 2), 0) / nonZeroAmounts.length)
      : 0;
    return { total, avg, stdDev, months: orderedKeys, amounts: orderedAmounts };
  }, [sortedDetail, isAnnual, isQuarterly, isSemiAnnual, timePeriod]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ color:'#6b7280' }}> ⇅</span>;
    return <span style={{ color:'#3b82f6' }}>{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>;
  };

  const openEditModal = () => {
    if (!selectedObj) return;
    setEditForm({
      display_name: selectedObj.display_name || '',
      recurring_type: selectedObj.recurring_type || 'fixed',
      recurring_frequency: selectedObj.recurring_frequency || 'monthly',
      expense_category: selectedObj.expense_category || '',
      vendor_payee: selectedObj.vendor_payee || '',
      notes: selectedObj.notes || '',
      close_date: selectedObj.close_date || '',
    });
    setEditError('');
    setEditLinkToAsset(false);
    setEditLinkAssetId('');
    setEditLinkCategory('');
    setEditLinkConflict([]);
    setEditLinkResolved('');
    // Ensure physical assets are loaded for the link-to-asset dropdown
    if (bulkPhysicalAssets.length === 0) {
      axios.get('/api/balance_sheet/assets/physical_assets/minimal/')
        .then(res => setBulkPhysicalAssets(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    }
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!selectedObj) return;
    setEditSaving(true);
    setEditError('');
    setEditLinkConflict([]);
    try {
      if (selectedObj.series_type === 'standalone') {
        // If user wants to link to an asset, do that conversion instead
        if (editLinkToAsset) {
          if (!editLinkAssetId) { setEditError('Please select an asset to link to.'); setEditSaving(false); return; }
          if (!editLinkCategory) { setEditError('Please select an expense category for the asset-linked series.'); setEditSaving(false); return; }
          try {
            await axios.post(
              `/api/balance_sheet/assets/standalone-recurring-series/${selectedObj.id}/link-to-asset/`,
              {
                asset_id: editLinkAssetId,
                expense_category: editLinkCategory,
                resolved_category: editLinkResolved || null,
              }
            );
          } catch (err) {
            if (err.response?.status === 409 && err.response.data?.conflict) {
              setEditLinkConflict(err.response.data.categories || []);
              setEditSaving(false);
              return;
            }
            throw err;
          }
          // Refresh series list and close
          const res = await axios.get('/api/balance_sheet/assets/all-recurring-series/');
          setAllSeries(Array.isArray(res.data) ? res.data : []);
          setSelected(null);
          setShowEditModal(false);
          setEditSaving(false);
          return;
        }
        await axios.patch(
          `/api/balance_sheet/assets/standalone-recurring-series/${selectedObj.id}/`,
          {
            display_name: editForm.display_name.trim(),
            recurring_type: editForm.recurring_type,
            recurring_frequency: editForm.recurring_frequency,
            expense_category: editForm.expense_category || null,
            vendor_payee: editForm.vendor_payee || null,
            notes: editForm.notes || null,
            close_date: editForm.close_date || null,
          }
        );
      } else {
        // Asset-linked: rename if name changed, then update type/freq
        const oldName = selectedObj.display_name;
        const newName = editForm.display_name.trim();
        const assetId = selectedObj.asset_id;
        const includeChildren = selectedObj.scope_include_children ? 1 : 0;
        if (newName !== oldName) {
          await axios.post(
            `/api/balance_sheet/assets/physical_assets/${assetId}/recurring-series/manage/`,
            { action: 'rename', old_name: oldName, new_name: newName, include_children: includeChildren }
          );
        }
        await axios.post(
          `/api/balance_sheet/assets/physical_assets/${assetId}/recurring-series/manage/`,
          {
            action: 'update',
            name: newName,
            recurring_type: editForm.recurring_type,
            recurring_frequency: editForm.recurring_frequency,
            include_children: includeChildren,
            close_date: editForm.close_date || null,
            expense_category: editForm.expense_category || null,
          }
        );
      }
      // Refresh series list
      const res = await axios.get('/api/balance_sheet/assets/all-recurring-series/');
      setAllSeries(Array.isArray(res.data) ? res.data : []);
      setShowEditModal(false);
    } catch (e) {
      setEditError('Failed to save. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  const navigateToAsset = (seriesObj) => {
    if (!seriesObj || seriesObj.series_type !== 'asset' || !seriesObj.asset_id) return;
    const asset = (allAssets || []).find(a => String(a.id) === String(seriesObj.asset_id));
    if (!asset) return;
    const t2 = (asset.asset_type_2 || '').toLowerCase();
    let url = null;
    if (t2 === 'resi_property') url = `/balance_sheet/assets/physical_assets/resi_properties/${asset.id}?tab=expenses&subtab=recurring&series_id=${seriesObj.id}`;
    else if (t2 === 'vehicle') url = `/balance_sheet/assets/physical_assets/vehicles/${asset.id}?tab=expenses&subtab=recurring&series_id=${seriesObj.id}`;
    if (url) navigate(url);
  };

  return (
    <>
    <div style={{ marginTop: '24px' }}>
      {/* Tab bar */}
      <div style={{ display:'flex', alignItems:'center', gap:0, marginBottom:16, borderBottom:'1px solid #374151' }}>
        <h4 style={{ margin:'0 24px 0 0', color:'#e5e7eb', paddingBottom:8 }}>Recurring Expenses</h4>
        {[{ key:'summary', label:'Summary' }, { key:'series', label:'Series' }].map(tab => (
          <button key={tab.key} onClick={() => setRecurringTab(tab.key)}
            style={{
              background:'none', border:'none', borderBottom: recurringTab === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
              color: recurringTab === tab.key ? '#3b82f6' : '#9ca3af',
              fontWeight: recurringTab === tab.key ? 700 : 400,
              fontSize:'0.95rem', padding:'4px 18px 10px 18px', cursor:'pointer', marginBottom:'-1px',
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <div style={{ color:'#9ca3af' }}>Loading series…</div>}

      {/* Summary tab */}
      {recurringTab === 'summary' && (
        <RecurringSummaryTab allSeries={allSeries} />
      )}

      {/* Series tab — filters + list + detail */}
      {recurringTab === 'series' && (
      <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:16, flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <label style={{ display:'flex', alignItems:'center', gap:6, color:'#9ca3af', fontSize:'0.9rem', cursor:'pointer' }}>
            <input type="checkbox" checked={filterOpenOnly} onChange={e => { setFilterOpenOnly(e.target.checked); if (e.target.checked) setFilterClosedOnly(false); }} />
            Open Only
          </label>
          <label style={{ display:'flex', alignItems:'center', gap:6, color:'#f87171', fontSize:'0.9rem', cursor:'pointer' }}>
            <input type="checkbox" checked={filterClosedOnly} onChange={e => { setFilterClosedOnly(e.target.checked); if (e.target.checked) setFilterOpenOnly(false); }} />
            Closed Only
          </label>
        </div>
      </div>
      )}

      {recurringTab === 'series' && <div style={{ display:'grid', gridTemplateColumns:'300px 1fr', gap:16, alignItems:'start' }}>
        {/* Series list */}
        <div style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, overflow:'hidden' }}>
          <div style={{ padding:'8px 12px', borderBottom:'1px solid #374151', fontSize:'0.8rem', color:'#6b7280', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>
            Series ({filteredSeries.length})
          </div>
          {/* Type filter tabs */}
          <div style={{ display:'flex', borderBottom:'1px solid #374151' }}>
            {[{ key:'all', label:'All' }, { key:'asset', label:'Asset-Linked' }, { key:'standalone', label:'Standalone' }].map(t => (
              <button key={t.key} onClick={() => setSeriesTypeTab(t.key)}
                style={{
                  flex:1, background:'none', border:'none',
                  borderBottom: seriesTypeTab === t.key ? '2px solid #3b82f6' : '2px solid transparent',
                  color: seriesTypeTab === t.key ? '#3b82f6' : '#9ca3af',
                  fontWeight: seriesTypeTab === t.key ? 700 : 400,
                  fontSize:'0.75rem', padding:'6px 4px', cursor:'pointer', marginBottom:'-1px',
                }}>
                {t.label}
              </button>
            ))}
          </div>
          <div style={{ padding:'8px 10px', borderBottom:'1px solid #1f2937', position:'relative' }}>
            <input
              type="text"
              placeholder="Search series…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ width:'100%', boxSizing:'border-box', background:'#111827', border:'1px solid #374151', borderRadius:6, padding:'5px 28px 5px 8px', color:'#e5e7eb', fontSize:'0.82rem', outline:'none' }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{ position:'absolute', right:16, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'#6b7280', cursor:'pointer', fontSize:'1rem', lineHeight:1, padding:0 }}
                title="Clear search"
              >✕</button>
            )}
          </div>
          <div style={{ maxHeight:'60vh', overflowY:'auto' }}>
            {filteredSeries.length === 0 && !loading && (
              <div style={{ padding:16, color:'#6b7280', fontSize:'0.9rem' }}>No series found.</div>
            )}
            {filteredSeries.map(s => {
              const isSelected = selected && selected.id === s.id && selected.series_type === s.series_type;
              return (
                <div
                  key={`${s.series_type}-${s.id}`}
                  onClick={() => setSelected({ id: s.id, series_type: s.series_type })}
                  style={{
                    padding:'10px 12px',
                    cursor:'pointer',
                    borderBottom:'1px solid #1f2937',
                    background: isSelected ? '#1e3a5f' : 'transparent',
                    borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                  }}
                >
                  <div style={{ fontWeight:600, color:'#e5e7eb', fontSize:'0.9rem' }}>{s.display_name}</div>
                  <div style={{ fontSize:'0.75rem', color:'#6b7280', marginTop:2, display:'flex', gap:8 }}>
                    <span style={{ background: s.series_type === 'asset' ? '#1e3a5f' : '#1a2e1a', color: s.series_type === 'asset' ? '#93c5fd' : '#86efac', padding:'1px 6px', borderRadius:4, fontSize:'0.7rem' }}>
                      {s.series_type === 'asset' ? '🏠 Asset' : '📋 Standalone'}
                    </span>
                    {(() => {
                      const cat = s.expense_category || s.default_expense_category || '';
                      if (!cat) return null;
                      const lbl = getCategoryDisplayLabel(cat, categoryChoices.flatMap || {});
                      return <span style={{ color:'#93c5fd', fontSize:'0.7rem' }}>{lbl}</span>;
                    })()}
                  </div>
                  {s.close_date && (
                    <div style={{ fontSize:'0.72rem', color:'#f87171', marginTop:2, fontWeight:600 }}>
                      🔴 Closed · {s.close_date}
                    </div>
                  )}
                  {s.series_type === 'asset' && s.asset_description && (
                    <div style={{ fontSize:'0.72rem', color:'#4b5563', marginTop:2 }}>{s.asset_description}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail panel */}
        <div style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, overflow:'hidden' }}>
          {!selected ? (
            <div style={{ padding:32, color:'#6b7280', textAlign:'center' }}>Select a series to view its transactions.</div>
          ) : (
            <>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                <div>
                  <div style={{ fontWeight:700, color:'#e5e7eb', fontSize:'1.05rem' }}>
                    {selectedObj?.display_name}
                    {selectedObj?.close_date && <span style={{ marginLeft:8, fontSize:'0.75rem', background:'#450a0a', color:'#f87171', borderRadius:4, padding:'2px 7px', fontWeight:600 }}>🔴 Closed · {selectedObj.close_date}</span>}
                  </div>
                  <div style={{ fontSize:'0.8rem', color:'#6b7280' }}>
                    {selectedObj?.recurring_type} · {selectedObj?.recurring_frequency}
                    {selectedObj?.series_type === 'asset' && selectedObj?.asset_description && ` · ${selectedObj.asset_description}`}
                    {selectedObj?.vendor_payee && ` · ${selectedObj.vendor_payee}`}
                    {(() => {
                      const cat = selectedObj?.expense_category || selectedObj?.default_expense_category || '';
                      if (!cat) return null;
                      const lbl = getCategoryDisplayLabel(cat, categoryChoices.flatMap || {});
                      return (
                        <span style={{ marginLeft:6, background:'#1e3a5f', color:'#93c5fd', borderRadius:4, padding:'1px 7px', fontSize:'0.75rem', fontWeight:500 }}>
                          {lbl}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                {sortedDetail.length > 0 && (
                  <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                    <div style={{ background:'#111827', borderRadius:6, padding:'4px 12px', minWidth:90 }}>
                      <div style={{ fontSize:'0.65rem', color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em' }}>Total</div>
                      <div style={{ fontSize:'0.9rem', fontWeight:700, color:'#e5e7eb', fontVariantNumeric:'tabular-nums' }}>{formatCurrency(detailMetrics.total)}</div>
                    </div>
                    <div style={{ background:'#111827', borderRadius:6, padding:'4px 12px', minWidth:90 }}>
                      <div style={{ fontSize:'0.65rem', color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em' }}>{avgLabel}</div>
                      <div style={{ fontSize:'0.9rem', fontWeight:700, color:'#e5e7eb', fontVariantNumeric:'tabular-nums' }}>{formatCurrency(detailMetrics.avg)}</div>
                    </div>
                    <div style={{ background:'#111827', borderRadius:6, padding:'4px 12px', minWidth:90 }}>
                      <div style={{ fontSize:'0.65rem', color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em' }}>Std. Dev.</div>
                      <div style={{ fontSize:'0.9rem', fontWeight:700, color:'#e5e7eb', fontVariantNumeric:'tabular-nums' }}>{formatCurrency(detailMetrics.stdDev)}</div>
                    </div>
                    <div style={{ background:'#111827', borderRadius:6, padding:'4px 12px', minWidth:60 }}>
                      <div style={{ fontSize:'0.65rem', color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em' }}>{periodLabel}</div>
                      <div style={{ fontSize:'0.9rem', fontWeight:700, color:'#e5e7eb' }}>{detailMetrics.months.length}</div>
                    </div>
                  </div>
                )}
                <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
                  {selectedObj?.series_type === 'asset' && (
                    <button
                      className="btn btn-sm btn-outline-info"
                      onClick={() => navigateToAsset(selectedObj)}
                    >
                      View in Asset →
                    </button>
                  )}
                  {selectedObj?.series_type === 'standalone' && (
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => setShowAddTxModal(true)}
                    >
                      + Add Transactions
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-outline-warning"
                    onClick={openEditModal}
                  >
                    ✏️ Edit Series
                  </button>
                </div>
                <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                  {['1M','3M','6M','YTD','1Y','Prior Yr.','All'].map(lbl => {
                    const key = lbl === 'Prior Yr.' ? 'PriorYr' : lbl;
                    return (
                      <button
                        key={key}
                        onClick={() => setTimePeriod(key)}
                        style={{
                          padding:'3px 9px', fontSize:'0.75rem', borderRadius:4, border:'1px solid',
                          borderColor: timePeriod === key ? '#3b82f6' : '#374151',
                          background: timePeriod === key ? '#1e3a5f' : 'transparent',
                          color: timePeriod === key ? '#93c5fd' : '#9ca3af',
                          fontWeight: timePeriod === key ? 700 : 400,
                          cursor:'pointer',
                        }}
                      >{lbl}</button>
                    );
                  })}
                </div>
              </div>

              {selectedObj?.notes && (
                <div style={{ padding:'8px 16px', borderBottom:'1px solid #1f2937', fontSize:'0.85rem', color:'#9ca3af', fontStyle:'italic' }}>
                  {selectedObj.notes}
                </div>
              )}

              {detailLoading && <div style={{ padding:24, color:'#9ca3af', textAlign:'center' }}>Loading…</div>}

              {!detailLoading && (
                <div>
                  {/* Bulk action bar — only for standalone series */}
                  {selectedObj?.series_type === 'standalone' && tableChecked.size > 0 && (
                    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'#1e3a5f', borderBottom:'1px solid #374151' }}>
                      <span style={{ color:'#93c5fd', fontSize:'0.85rem' }}>{tableChecked.size} selected</span>
                      <button className="btn btn-sm btn-outline-danger" onClick={handleRemoveFromSeries}>Remove from Series</button>
                      <button className="btn btn-sm btn-outline-warning" onClick={openMoveModal}>Move to Another Series</button>
                    </div>
                  )}
                  <div style={{ overflowX:'auto' }}>
                    <table className="table table-dark table-hover mb-0" style={{ fontSize:'0.85rem' }}>
                      <thead>
                        <tr>
                          {selectedObj?.series_type === 'standalone' && (
                            <th style={{ padding:'8px', width:36 }}>
                              <input
                                type="checkbox"
                                style={{ width:16, height:16, cursor:'pointer', accentColor:'#3b82f6' }}
                                checked={sortedDetail.length > 0 && tableChecked.size === sortedDetail.length}
                                onChange={() => handleTableCheckAll(sortedDetail)}
                              />
                            </th>
                          )}
                          <th style={{ cursor:'pointer', padding:'8px' }} onClick={() => handleSort('payment_date')}>Date <SortIcon field="payment_date" /></th>
                          <th style={{ cursor:'pointer', padding:'8px' }} onClick={() => handleSort('description')}>Description <SortIcon field="description" /></th>
                          <th style={{ cursor:'pointer', padding:'8px', textAlign:'right' }} onClick={() => handleSort('amount')}>Amount <SortIcon field="amount" /></th>
                          <th style={{ padding:'8px' }}>Category</th>
                          {selectedObj?.series_type === 'standalone' && <th style={{ padding:'8px' }}>Source</th>}
                          <th style={{ padding:'8px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedDetail.length === 0 && (
                          <tr><td colSpan={selectedObj?.series_type === 'standalone' ? 7 : 5} style={{ textAlign:'center', color:'#6b7280', padding:16 }}>No transactions found.</td></tr>
                        )}
                        {sortedDetail.map((row, idx) => {
                          const dateVal = row.payment_date || row.transaction_date || row.date || '';
                          const amt = Number(row.amount || row.payment_amount || 0);
                          const cat = row.expense_category || row.category || '';
                          const desc = row.description || row.user_description || '';
                          const srcName = row._source === 'cash'
                            ? (row.source_account_display || 'Cash Account')
                            : (row.lender_name ? `${row.lender_name}${row.account_last4 ? ' *'+row.account_last4 : ''}` : 'Credit Card');
                          let srcUrl = '';
                          if (row._source === 'cash' && row.account) srcUrl = `/balance_sheet/assets/cash_assets/${row.account}`;
                          else if (row._source === 'revolver' && row.revolver) srcUrl = row.revolver_type === 'heloc' ? `/balance_sheet/financing/revolvers/helocs/${row.revolver}` : `/balance_sheet/financing/revolvers/credit-cards/${row.revolver}`;
                          return (
                            <tr key={idx} style={{ background: tableChecked.has(idx) ? 'rgba(59,130,246,0.12)' : undefined }}>
                              {selectedObj?.series_type === 'standalone' && (
                                <td style={{ padding:'8px', width:36 }}>
                                  <input
                                    type="checkbox"
                                    style={{ width:16, height:16, cursor:'pointer', accentColor:'#3b82f6' }}
                                    checked={tableChecked.has(idx)}
                                    onChange={() => handleTableCheckRow(idx)}
                                  />
                                </td>
                              )}
                              <td style={{ padding:'8px' }}>{dateVal}</td>
                              <td style={{ padding:'8px' }}>{desc}</td>
                              <td style={{ padding:'8px', textAlign:'right', color: amt < 0 ? '#f87171' : '#e5e7eb' }}>{formatCurrency(Math.abs(amt))}</td>
                              <td style={{ padding:'8px', color:'#9ca3af', fontSize:'0.8rem' }}>{cat}</td>
                              {selectedObj?.series_type === 'standalone' && <td style={{ padding:'8px', color:'#9ca3af', fontSize:'0.8rem' }}>{srcUrl ? <a href={srcUrl} target="_blank" rel="noreferrer" style={{ color:'#93c5fd', textDecoration:'none' }} onMouseEnter={e=>e.target.style.textDecoration='underline'} onMouseLeave={e=>e.target.style.textDecoration='none'}>{srcName}</a> : srcName}</td>}
                              <td style={{ padding:'8px' }}>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  style={{ fontSize:'0.75rem', padding:'2px 8px' }}
                                  onClick={() => setDelinkConfirm({ row, seriesName: selectedObj?.display_name || '' })}
                                >
                                  De-link
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!detailLoading && (
                <div style={{ padding:'24px 24px 16px 24px' }}>
                  {sortedDetail.length === 0 ? (
                    <div style={{ color:'#6b7280', textAlign:'center' }}>No data to graph.</div>
                  ) : (() => {
                    const { months, amounts } = detailMetrics;
                    return (
                      <div>
                        <div style={{ display:'flex', alignItems:'flex-end', gap:4, height:180, marginTop:8 }}>
                          {months.map((m, i) => {
                            const maxAmt = Math.max(...amounts, 1);
                            const isEmpty = amounts[i] === 0;
                            const h = isEmpty ? 0 : Math.max(Math.round((amounts[i] / maxAmt) * 140), 4);
                            return (
                              <div key={m} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                                <div style={{ fontSize:'0.65rem', color: isEmpty ? '#4b5563' : '#9ca3af' }}>{isEmpty ? '—' : formatCurrency(amounts[i])}</div>
                                <div style={{ width:'100%', height: isEmpty ? 2 : h, background: isEmpty ? '#374151' : '#3b82f6', borderRadius:'3px 3px 0 0', minHeight:2, opacity: isEmpty ? 0.5 : 1 }} title={`${m}: ${isEmpty ? '$0' : formatCurrency(amounts[i])}`} />
                                <div style={{ fontSize:'0.65rem', color:'#6b7280', writingMode:'vertical-rl', transform:'rotate(180deg)', maxHeight:60, overflow:'hidden' }}>{m}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </div>
      </div>}
    </div>

    {/* Add Transactions Modal */}
    {showAddTxModal && selectedObj && (
      <TransactionSearchModal
        seriesId={selectedObj.id}
        seriesType={selectedObj.series_type}
        seriesName={selectedObj.display_name}
        categoryChoices={categoryChoices}
        onClose={() => setShowAddTxModal(false)}
        onAdded={() => {
          // Refresh detail transactions after linking
          setSelected(s => s ? { ...s } : s);
        }}
      />
    )}

    {/* Move to Another Series Modal */}
    {showMoveModal && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 }} onClick={() => { if (!moveSaving) { setShowMoveModal(false); setMoveConflictCategories([]); } }}>
        <div style={{ background:'#1f2937', border:'1px solid #374151', borderRadius:10, padding:28, width:480, maxWidth:'95vw' }} onClick={e => e.stopPropagation()}>
          <h5 style={{ color:'#e5e7eb', marginBottom:16 }}>Move {tableChecked.size} Transaction{tableChecked.size !== 1 ? 's' : ''} to Another Series</h5>

          {moveConflictCategories.length > 0 ? (
            <div>
              <div style={{ color:'#fbbf24', marginBottom:12, fontSize:'0.9rem' }}>{moveError}</div>
              <div style={{ marginBottom:12 }}>
                <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Select category to apply to all:</label>
                <select className="form-select form-select-sm" style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }} value={moveResolvedCategory} onChange={e => setMoveResolvedCategory(e.target.value)}>
                  <option value="">-- Select --</option>
                  {moveConflictCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button className="btn btn-sm btn-secondary" onClick={() => { setShowMoveModal(false); setMoveConflictCategories([]); }} disabled={moveSaving}>Cancel</button>
                <button className="btn btn-sm btn-warning" onClick={handleMoveConflictResolve} disabled={moveSaving || !moveResolvedCategory}>{moveSaving ? 'Saving…' : 'Confirm'}</button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display:'flex', gap:8, marginBottom:16 }}>
                <button className={`btn btn-sm ${moveMode === 'existing' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setMoveMode('existing')}>Existing Series</button>
                <button className={`btn btn-sm ${moveMode === 'new' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setMoveMode('new')}>New Series</button>
              </div>

              {moveMode === 'existing' && (
                <div style={{ marginBottom:14 }}>
                  <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Select Series</label>
                  {moveSeriesLoading ? <div style={{ color:'#9ca3af' }}>Loading…</div> : (
                    <select className="form-select form-select-sm" style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }} value={moveSelectedSeriesId} onChange={e => setMoveSelectedSeriesId(e.target.value)}>
                      <option value="">-- Select a series --</option>
                      {moveSeriesOptions.some(s => s.series_type === 'asset') && (
                        <optgroup label="── Asset-Linked ──">
                          {moveSeriesOptions.filter(s => s.series_type === 'asset').map(s => (
                            <option key={`asset__${s.id}`} value={`asset__${s.id}`}>
                              {s.asset_description ? `${s.asset_description} - ${s.display_name}` : s.display_name} ({s.recurring_type} / {s.recurring_frequency})
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {moveSeriesOptions.some(s => s.series_type === 'standalone') && (
                        <optgroup label="── Non-Asset Linked ──">
                          {moveSeriesOptions.filter(s => s.series_type === 'standalone').map(s => (
                            <option key={`standalone__${s.id}`} value={`standalone__${s.id}`}>
                              {s.display_name} ({s.recurring_type} / {s.recurring_frequency})
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  )}
                </div>
              )}

              {moveMode === 'new' && (
                <div>
                  <div style={{ marginBottom:10 }}>
                    <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Series Name</label>
                    <input className="form-control form-control-sm" style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }} value={moveNewName} onChange={e => setMoveNewName(e.target.value)} placeholder="e.g. Netflix" />
                  </div>
                  <div style={{ marginBottom:10 }}>
                    <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Category (optional)</label>
                    <select className="form-select form-select-sm" style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }} value={moveNewCategory} onChange={e => setMoveNewCategory(e.target.value)}>
                      <option value="">-- None --</option>
                      {(categoryChoices || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {moveError && <div style={{ color:'#f87171', fontSize:'0.85rem', marginBottom:8 }}>{moveError}</div>}

              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
                <button className="btn btn-sm btn-secondary" onClick={() => setShowMoveModal(false)} disabled={moveSaving}>Cancel</button>
                <button className="btn btn-sm btn-warning" onClick={handleMoveSave} disabled={moveSaving}>{moveSaving ? 'Moving…' : 'Move'}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    {/* De-link Confirmation Modal */}
    {delinkConfirm && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200 }} onClick={() => { if (!delinkSaving) { setDelinkConfirm(null); setDelinkError(''); } }}>
        <div style={{ background:'#1f2937', border:'1px solid #374151', borderRadius:10, padding:28, width:460, maxWidth:'95vw' }} onClick={e => e.stopPropagation()}>
          <h5 style={{ color:'#e5e7eb', marginBottom:12 }}>De-link Transaction</h5>
          <p style={{ color:'#9ca3af', fontSize:'0.9rem', marginBottom:8 }}>
            Are you sure you want to remove this transaction from the series <strong style={{ color:'#e5e7eb' }}>{delinkConfirm.seriesName}</strong>?
          </p>
          <div style={{ background:'#111827', border:'1px solid #374151', borderRadius:6, padding:'10px 14px', marginBottom:16, fontSize:'0.85rem', color:'#d1d5db' }}>
            <div><span style={{ color:'#6b7280' }}>Date:</span> {delinkConfirm.row.payment_date || delinkConfirm.row.transaction_date || delinkConfirm.row.date || '—'}</div>
            <div><span style={{ color:'#6b7280' }}>Description:</span> {delinkConfirm.row.description || delinkConfirm.row.user_description || '—'}</div>
            <div><span style={{ color:'#6b7280' }}>Amount:</span> {formatCurrency(Math.abs(Number(delinkConfirm.row.amount || delinkConfirm.row.payment_amount || 0)))}</div>
          </div>
          <p style={{ color:'#fbbf24', fontSize:'0.85rem', marginBottom:16 }}>The transaction will <strong>not</strong> be deleted — it will simply be unlinked from this series.</p>
          {delinkError && <div style={{ color:'#f87171', fontSize:'0.85rem', marginBottom:10 }}>{delinkError}</div>}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button className="btn btn-sm btn-secondary" onClick={() => { setDelinkConfirm(null); setDelinkError(''); }} disabled={delinkSaving}>Cancel</button>
            <button className="btn btn-sm btn-danger" onClick={handleDelink} disabled={delinkSaving}>{delinkSaving ? 'Removing…' : 'Yes, De-link'}</button>
          </div>
        </div>
      </div>
    )}

    {/* Edit Series Modal */}
    {showEditModal && (
      <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.6)', zIndex:1050, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ background:'#1f2937', border:'1px solid #374151', borderRadius:10, padding:28, width:460, maxWidth:'95vw' }}>
          <h5 style={{ color:'#e5e7eb', marginBottom:20 }}>Edit Recurring Series</h5>

          <div style={{ marginBottom:14 }}>
            <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Series Name</label>
            <input
              className="form-control form-control-sm"
              style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }}
              value={editForm.display_name || ''}
              onChange={e => setEditForm(f => ({ ...f, display_name: e.target.value }))}
            />
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Type</label>
            <select
              className="form-select form-select-sm"
              style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }}
              value={editForm.recurring_type || 'fixed'}
              onChange={e => setEditForm(f => ({ ...f, recurring_type: e.target.value }))}
            >
              <option value="fixed">Fixed</option>
              <option value="variable">Variable</option>
            </select>
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Frequency</label>
            <select
              className="form-select form-select-sm"
              style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }}
              value={editForm.recurring_frequency || 'monthly'}
              onChange={e => setEditForm(f => ({ ...f, recurring_frequency: e.target.value }))}
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="semi-annual">Semi-Annual</option>
              <option value="annual">Annual</option>
              <option value="weekly">Weekly</option>
              <option value="bi-weekly">Bi-Weekly</option>
            </select>
          </div>

          {selectedObj?.series_type === 'asset' && (() => {
            const t2 = (selectedObj?.asset_type_2 || '').toLowerCase();
            // Define allowed parent category keys per asset type
            const allowedParents = t2 === 'vehicle'
              ? ['automotive']
              : t2 === 'structure'
              ? ['home']
              : t2 === 'land'
              ? ['landscaping_group', 'taxes_insurance']
              : null; // null = show all
            const filteredItems = allowedParents
              ? (Array.isArray(categoryChoices?.items) ? categoryChoices.items : []).filter(p => allowedParents.includes(p.value))
              : (Array.isArray(categoryChoices?.items) ? categoryChoices.items : []);
            return (
              <div style={{ marginBottom:14 }}>
                <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Expense Category</label>
                <select
                  className="form-select form-select-sm"
                  style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }}
                  value={editForm.expense_category || ''}
                  onChange={e => setEditForm(f => ({ ...f, expense_category: e.target.value }))}
                >
                  <option value="">— No Category —</option>
                  {filteredItems.map(parent => (
                    <optgroup key={parent.value} label={parent.label}>
                      {(Array.isArray(parent.children) ? parent.children : []).map(child => (
                        <option key={child.value} value={child.value}>{parent.label} - {child.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            );
          })()}

          {selectedObj?.series_type === 'standalone' && (
            <>
              <div style={{ marginBottom:14 }}>
                <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Expense Category</label>
                <select
                  className="form-select form-select-sm"
                  style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }}
                  value={editForm.expense_category || ''}
                  onChange={e => setEditForm(f => ({ ...f, expense_category: e.target.value }))}
                >
                  <option value="">— No Category —</option>
                  {(Array.isArray(categoryChoices?.items) ? categoryChoices.items : []).map(parent => (
                    <optgroup key={parent.value} label={parent.label}>
                      {(Array.isArray(parent.children) && parent.children.length > 0
                        ? parent.children
                        : [parent]
                      ).map(child => (
                        <option key={child.value} value={child.value}>
                          {parent.children && parent.children.length > 0 ? `${parent.label} - ${child.label}` : child.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Vendor / Payee</label>
                <input
                  className="form-control form-control-sm"
                  style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }}
                  value={editForm.vendor_payee || ''}
                  onChange={e => setEditForm(f => ({ ...f, vendor_payee: e.target.value }))}
                />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Notes</label>
                <textarea
                  className="form-control form-control-sm"
                  style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151', resize:'vertical' }}
                  rows={2}
                  value={editForm.notes || ''}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Close Date <span style={{ fontSize:'0.75rem', color:'#6b7280' }}>(leave blank if still active)</span></label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }}
                  value={editForm.close_date || ''}
                  onChange={e => setEditForm(f => ({ ...f, close_date: e.target.value }))}
                />
              </div>

              {/* Link to Asset section */}
              <div style={{ borderTop:'1px solid #374151', paddingTop:14, marginBottom:14 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom: editLinkToAsset ? 12 : 0 }}>
                  <input
                    type="checkbox"
                    id="editLinkToAssetChk"
                    checked={editLinkToAsset}
                    onChange={e => { setEditLinkToAsset(e.target.checked); setEditLinkAssetId(''); setEditLinkCategory(''); setEditLinkConflict([]); setEditLinkResolved(''); }}
                    style={{ width:16, height:16, cursor:'pointer', accentColor:'#f59e0b' }}
                  />
                  <label htmlFor="editLinkToAssetChk" style={{ color:'#f59e0b', fontSize:'0.85rem', cursor:'pointer', margin:0 }}>
                    Convert to Asset-Linked Series
                  </label>
                </div>
                {editLinkToAsset && (
                  <>
                    <div style={{ marginBottom:10 }}>
                      <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Link to Asset</label>
                      <select
                        className="form-select form-select-sm"
                        style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }}
                        value={editLinkAssetId}
                        onChange={e => { setEditLinkAssetId(e.target.value); setEditLinkCategory(''); }}
                      >
                        <option value="">— Select an asset —</option>
                        {(() => {
                          const allAssets = bulkPhysicalAssets;
                          const properties = allAssets.filter(a => a.asset_type_2 === 'resi_property' && a.description);
                          const children = allAssets.filter(a => a.asset_type_2 === 'structure' || a.asset_type_2 === 'land');
                          const vehicles = allAssets.filter(a => a.asset_type_2 === 'vehicle' && a.description);
                          const orphans = children.filter(c => !c.residential_property || !c.residential_property.id);
                          const rows = [];
                          properties.forEach(prop => {
                            const kids = children.filter(c => c.residential_property && c.residential_property.id === prop.id);
                            if (kids.length > 0) {
                              rows.push(<option key={`prop-hdr-${prop.id}`} value="" disabled style={{ color:'#9ca3af', fontStyle:'italic' }}>── {prop.description} ──</option>);
                              kids.forEach(kid => rows.push(<option key={kid.id} value={kid.id}>&nbsp;&nbsp;&nbsp;{kid.asset_type_1}</option>));
                            } else {
                              rows.push(<option key={prop.id} value={prop.id}>{prop.description}</option>);
                            }
                          });
                          orphans.forEach(o => rows.push(<option key={o.id} value={o.id}>{o.description || o.asset_type_1}</option>));
                          if (vehicles.length > 0) {
                            rows.push(<option key="veh-hdr" value="" disabled style={{ color:'#9ca3af', fontStyle:'italic' }}>── Vehicles ──</option>);
                            vehicles.forEach(v => rows.push(<option key={v.id} value={v.id}>{v.description}</option>));
                          }
                          return rows;
                        })()}
                      </select>
                    </div>
                    {editLinkAssetId && (() => {
                      const selAsset = bulkPhysicalAssets.find(a => String(a.id) === String(editLinkAssetId));
                      const atype = selAsset ? selAsset.asset_type_2 : null;
                      let catOptions = [];
                      if (atype === 'vehicle') catOptions = [
                        { value:'vehicle_maintenance', label:'Maintenance' },
                        { value:'vehicle_repair', label:'Repair' },
                        { value:'vehicle_enhancement', label:'Enhancement' },
                        { value:'licensing_tags', label:'Licensing/Tags' },
                        { value:'insurance', label:'Insurance Payment' },
                        { value:'taxes', label:'Tax Payment' },
                        { value:'vehicle_other', label:'Other' },
                      ];
                      else if (atype === 'structure') catOptions = [
                        { value:'maintenance', label:'Asset Maintenance' },
                        { value:'repair', label:'Asset Repair' },
                        { value:'enhancement', label:'Asset Enhancement' },
                        { value:'cleaning', label:'Cleaning' },
                        { value:'pest_control', label:'Pest Control' },
                        { value:'home_decor', label:'Home Decor' },
                        { value:'home_furniture', label:'Home Furniture' },
                        { value:'home_artwork', label:'Home Artwork' },
                        { value:'home_inspection', label:'Home Inspection' },
                        { value:'plants', label:'Plants' },
                        { value:'landscaping', label:'Landscaping' },
                        { value:'insurance', label:'Insurance Payment' },
                        { value:'taxes', label:'Tax Payment' },
                        { value:'utilities', label:'Utilities' },
                        { value:'home_other', label:'Home Other' },
                      ];
                      else if (atype === 'land') catOptions = [
                        { value:'landscaping', label:'Landscaping' },
                        { value:'taxes', label:'Tax Payment' },
                      ];
                      if (catOptions.length === 0) return null;
                      return (
                        <div style={{ marginBottom:10 }}>
                          <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Expense Category <span style={{ color:'#f87171' }}>*</span></label>
                          <select
                            className="form-select form-select-sm"
                            style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }}
                            value={editLinkCategory}
                            onChange={e => setEditLinkCategory(e.target.value)}
                          >
                            <option value="">— Select a category —</option>
                            {catOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                      );
                    })()}
                    {editLinkConflict.length > 0 && (
                      <div style={{ marginBottom:10 }}>
                        <div style={{ color:'#fbbf24', fontSize:'0.82rem', marginBottom:6 }}>
                          Multiple expense categories found. Select one to apply to all transactions:
                        </div>
                        <select
                          className="form-select form-select-sm"
                          style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }}
                          value={editLinkResolved}
                          onChange={e => setEditLinkResolved(e.target.value)}
                        >
                          <option value="">— Choose category —</option>
                          {editLinkConflict.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    )}
                    <div style={{ color:'#6b7280', fontSize:'0.78rem' }}>
                      ⚠️ This will convert the series to asset-linked and migrate all linked transactions. This cannot be undone.
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {selectedObj?.series_type === 'asset' && (
            <div style={{ marginBottom:14 }}>
              <label style={{ color:'#9ca3af', fontSize:'0.85rem', display:'block', marginBottom:4 }}>Close Date <span style={{ fontSize:'0.75rem', color:'#6b7280' }}>(leave blank if still active)</span></label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ background:'#111827', color:'#e5e7eb', border:'1px solid #374151' }}
                value={editForm.close_date || ''}
                onChange={e => setEditForm(f => ({ ...f, close_date: e.target.value }))}
              />
            </div>
          )}

          {editError && <div style={{ color:'#f87171', fontSize:'0.85rem', marginBottom:12 }}>{editError}</div>}

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button className="btn btn-sm btn-secondary" onClick={() => setShowEditModal(false)} disabled={editSaving}>Cancel</button>
            <button className="btn btn-sm btn-warning" onClick={handleEditSave} disabled={editSaving || !editForm.display_name?.trim()}>
              {editSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default ExpensesPage;
